import { MediaClip } from "../media";
import { Track } from "../../tracks";
import { MediaSourceRef } from "../../sources/media-source";
import {
    ScriptLayerClipProps,
    ScriptLayerVideoSource,
	    ScriptLayerInputConfig,
	    ScriptLayerCut,
	    ScriptLayerSplit,
	    ScriptLayerSegment,
	    ScriptLayerGap,
	    ScriptLayerGapInsertion,
	    ScriptLayerSourceSegment,
        TranscriptHiddenRange,
        TranscriptWordCorrection,
        TranscriptParagraphBreak,
        TranscriptMarker,
	    SpeakerOverride,
        SpeakerMuteRange,
	    TransitionConfig,
	    DEFAULT_CUT_TRANSITION,
	    DEFAULT_SPLIT_TRANSITION,
	    DEFAULT_SCENE_TRANSITION,
    CutAnalysis,
    CutPointAnalysis,
    HarshnessScore,
    MICROFADE_DURATION_MS,
    HARSH_SCORE_THRESHOLD,
    CrossfadeDescriptor,
    SceneProperties,
    SceneVisualProperties,
    ScriptLayerScene,
    MulticamCameraSetup,
} from "./script.interfaces";
import { VisualMixin } from "../mixins";
import { visualize } from "../mixins/visual.decorator";
import {Clip, ClipType} from "../clip";
import { Timestamp } from "../../models";
import { AudioMixer, MixerTrackHandle } from "../../media/audio-mixer";
import { AudioBufferSink, CanvasSink, Input, UrlSource, WrappedAudioBuffer, WrappedCanvas, ALL_FORMATS } from 'mediabunny';
import { IOError } from "../../errors";
import { Sprite, Texture, Container, Rectangle } from "pixi.js";
import {VideoClip} from "../video/video";
import {AudioClip} from "../audio";
import { readMonoWindowFromSink, findNearestZeroCross, rms } from "../utils/cut-analysis";
import { applyShapeMask, type MaskShape } from "../../utils/shapeMask";

const DEBUG_SCRIPT_CLIP_LOGS =
    typeof window !== "undefined" &&
    (((window as any).__LIBRETTO_DEBUG_SCRIPT_CLIP_LOGS === true) ||
        ((window as any).__LIBRETTO_DEBUG_ENGINE_LOGS === true));

/**
 * State for the per-clip peak limiter (Studio Sound)
 */
type LimiterState = {
    envelope: number;
};

/**
 * Internal representation of an audio source with its config
 */
interface AudioSourceEntry {
	    input: Input;
	    assetId?: string;  // For waveform lookup
	    gain: number;
	    name?: string;
	    color?: string;
	    range?: { inSec: number; outSec: number };  // Source time range (legacy single-segment)
	    offsetSec?: number;  // Timeline offset relative to clip start (legacy single-segment)
	    playbackRate?: number;  // Playback speed (legacy single-segment)
	    segments?: ScriptLayerSourceSegment[]; // Multi-segment placement on the ScriptClip spine
}

/**
 * Internal representation of a video source with its config
 */
interface VideoSourceEntry {
	    input: Input;
	    visual: {
	        x: number;
	        y: number;
        width: number;
        height: number;
        alpha: number;
        maskShape?: MaskShape;
        /** Rotation in degrees (Pixi Container uses degrees via `.angle`). */
        rotation?: number;
        crop?: { x: number; y: number; width: number; height: number };
        originalWidth?: number;  // Original width before any crop
        originalHeight?: number; // Original height before any crop
	    };
	    range?: { inSec: number; outSec: number };  // Source time range (legacy single-segment)
	    offsetSec?: number;  // Timeline offset relative to clip start (legacy single-segment)
	    playbackRate?: number;  // Playback speed (legacy single-segment)
	    segments?: ScriptLayerSourceSegment[]; // Multi-segment placement on the ScriptClip spine
	    layerIndex: number;  // Z-order within the script clip (higher = renders on top)
}

/**
 * Information about an active visual transition between scenes.
 */
interface TransitionInfo {
    /** Scene index we're transitioning FROM */
    fromSceneIndex: number;
    /** Scene index we're transitioning TO */
    toSceneIndex: number;
    /** Transition progress (0 = just started, 1 = complete) */
    progress: number;
    /** Video transition type */
    transitionType: 'cut' | 'fade' | 'smart' | 'dissolve' | 'wipe';
    /** Duration of the transition in seconds */
    durationSec: number;
    /** Optional easing curve name (used by 'smart'). */
    easing?: string;
    /** Optional crossfade toggle (used by 'smart'). */
    addFades?: boolean;
}

/** Linear interpolation helper */
function lerp(a: number, b: number, t: number): number {
    return a + (b - a) * t;
}

const HALF_PI = Math.PI / 2;

/**
 * ScriptLayerClip: A clip that contains multiple audio and video sources.
 * - Audio sources are mixed together and played simultaneously
 * - Video sources are composited with independent transforms (x, y, width, height, alpha)
 * - Created from an array of InputConfigs, each containing a MediaBunny Input
 */
export class ScriptLayerClip extends VisualMixin(MediaClip<ScriptLayerClipProps>) {
    public readonly type : ClipType = 'script';
    public track?: Track<ScriptLayerClip>;

    // Input configs (source of truth)
    private inputConfigs: ScriptLayerInputConfig[] = [];

    // Derived audio sources (populated during init)
    private audioEntries: AudioSourceEntry[] = [];

    // Derived video sources (populated during init)
    private videoEntries: VideoSourceEntry[] = [];

    // Names of source clips (e.g., participant names in a podcast)
    public names: string[] = [];

    // Colors for each audio source (for multi-colored waveform display)
    public colors: string[] = [];

    // ============ Cuts & Splits (Canonical Edit State) ============
    
    // Cuts: remove content between startSec and endSec
    // Note: Named scriptCuts to avoid conflict with MediaClip.cuts
    public scriptCuts: ScriptLayerCut[] = [];
    
    // Gaps: replace content with silence (maintains timeline position)
    public gaps: ScriptLayerGap[] = [];

    // Gap insertions: insert silent time into the timeline (adds duration, shifts downstream content)
    public gapInsertions: ScriptLayerGapInsertion[] = [];
    
    // Splits: boundary markers (no content removed, just segment boundaries for transitions)
    public splits: ScriptLayerSplit[] = [];
    
    // Speaker overrides: manual speaker assignments for time ranges
    // Overrides automatic diarization-based speaker detection
    public speakerOverrides: SpeakerOverride[] = [];

    // Speaker mute ranges: audio-only muting of detected/assigned speakers.
    // Kept separate from gaps so unmute can be lossless.
    public speakerMuteRanges: SpeakerMuteRange[] = [];

    // Transcript-only edits (do not affect playback)
    public transcriptHiddenRanges: TranscriptHiddenRange[] = [];
    public wordCorrections: TranscriptWordCorrection[] = [];
    public transcriptParagraphBreaks: TranscriptParagraphBreak[] = [];
    public transcriptMarkers: TranscriptMarker[] = [];

    // Automatic Multicam camera-to-speaker mapping (editor-only helper state).
    // Persisted with the clip so the user doesn't need to reconfigure.
    public multicamCameraSetup: MulticamCameraSetup | null = null;

    // Prevent concurrent init() calls from racing and duplicating derived entries.
    private _initPromise: Promise<void> | null = null;

    // Prevent concurrent update() calls (Composition does not await async updates during realtime playback).
    private _updatePromise: Promise<void> | null = null;
    private _pendingUpdateTimeSec: number | null = null;

    // ============ Scene Properties ============

    // Per-scene property overrides (gain, playbackRate)
    // Key: scene index (0 = first scene), Value: scene properties
    public sceneProperties: Map<number, SceneProperties> = new Map();

    // Cache for derived scenes (invalidated when splits change)
    private _scenesCache: ScriptLayerScene[] | null = null;

    // Track which scene we're currently in for dynamic gain updates
    private _currentSceneIndex: number = -1;

    // ============ Derived Segments ============
    
    // Derived playable segments (computed from cuts + splits)
    private _scriptSegments: ScriptLayerSegment[] = [];
    
    // Crossfade descriptors for true crossfades at segment boundaries
    private _crossfadeDescriptors: CrossfadeDescriptor[] = [];
    
    // Flag to track if segments need recalculation
    private _segmentsDirty: boolean = true;

    // === AUDIO DECODING ===
    private _audioSinks: Map<Input, AudioBufferSink> = new Map();
    private _audioIters: Map<Input, AsyncGenerator<{ buffer: AudioBuffer; timestamp: number }, void>> = new Map();
    private _audioPrimeSeq = 0;
    private _audioGains: Map<Input, number> = new Map();
    private _mixer?: AudioMixer;

    // === STUDIO SOUND ===
    private _studioSoundEnabled = false;
    // Store original and denoised URLs per audio source (keyed by assetId)
    private _originalSourceUrls: Map<string, string> = new Map();
    private _denoisedSourceUrls: Map<string, string> = new Map();
    // Per-audio-source limiter state for Studio Sound
    private _limiterStates: Map<Input, LimiterState> = new Map();

    // Override gain to update mixer tracks in real-time (like AudioClip.volume)
    public override get gain(): number {
        return this._gain;
    }

    public override set gain(v: number) {
        this._gain = Math.max(0, v);
        // Update all audio mixer tracks with new gain
        if (this._mixer) {
            for (const [input, mixerTrack] of this._audioMixerTracks) {
                const entry = this.audioEntries.find(e => e.input === input);
                const entryGain = entry?.gain ?? 1.0;
                this._mixer.setClipGain(mixerTrack, entryGain * this._gain);
            }
        }
    }

    /**
     * Timeline duration for the ScriptLayerClip spine.
     * Uses the derived scriptSegments mapping (cuts collapse time; gap insertions add holes).
     */
    public override get timelineDuration(): Timestamp {
        const rate = Number((this as any).playbackRate ?? 1) || 1;
        const clipStart = this.start?.seconds ?? 0;
        const segments = this.scriptSegments;

        let maxEnd = clipStart;
        for (const seg of segments) {
            const segDur = Math.max(0, (seg.srcEndSec ?? 0) - (seg.srcStartSec ?? 0));
            const segEnd = (seg.compStartSec ?? 0) + segDur;
            if (segEnd > maxEnd) maxEnd = segEnd;
        }

        // Gap insertions at the clip end don't shift any segment, but they do extend the timeline.
        const outSec = this.range?.[1]?.seconds ?? this.duration?.seconds ?? 0;
        const EPS = 1e-3;
        let trailingInsertSec = 0;
        for (const ins of this.gapInsertions ?? []) {
            const at = Number(ins?.atSec ?? 0);
            const dur = Number(ins?.durationSec ?? 0);
            if (!Number.isFinite(at) || !Number.isFinite(dur) || dur <= EPS) continue;
            if (Math.abs(at - outSec) <= EPS) trailingInsertSec += dur;
        }
        maxEnd += trailingInsertSec;

        const durationSecs = Math.max(0, (maxEnd - clipStart) / rate);
        return Timestamp.fromSeconds(durationSecs);
    }

    // One mixer track per audio source (like having multiple independent AudioClips)
    private _audioMixerTracks: Map<Input, MixerTrackHandle> = new Map();
    // Dedicated mixer tracks for crossfade overlays (one per audio source).
    // These tracks are in "composition-time" (we write PCM with `timestamp = compTimeSec`)
    // and schedule events with `srcOffset = start`.
    private _crossfadeOverlayTracks: Map<Input, MixerTrackHandle> = new Map();
    private _audioSampleRates: Map<Input, number> = new Map();
    private _audioChannelCounts: Map<Input, number> = new Map();
    private _sample_rate?: number;
    private _num_channels?: number;

    // === VIDEO DECODING ===
    private _videoSinks: Map<Input, CanvasSink> = new Map();
    private _videoVisuals: Map<Input, VideoSourceEntry['visual']> = new Map();
    private _videoIters: Map<Input, AsyncGenerator<WrappedCanvas | null, void, unknown>> = new Map();
    private _nextFrames: Map<Input, WrappedCanvas | null> = new Map();
    private _pendingFrameRequests: Set<Input> = new Set();  // Track pending async frame fetches
    
    // Track which segment we were in last - used to detect cut boundaries
    // When segment changes, we reset the iterator instead of iterating through cut frames
    private _lastSegmentIndex: number = -1;

    // Debug: track which fades we've logged (to avoid spamming console)
    private _loggedFades?: Set<string>;

    // Each video source gets its own container, canvas, texture and sprite
    // The container acts like an independent VideoClip view
    private _videoContainers: Map<Input, Container> = new Map();
    /** During Smart transitions with Add fades, we render an outgoing copy of each container. */
    private _transitionFromContainers: Map<Input, Container> = new Map();
    private _transitionFromSprites: Map<Input, Sprite> = new Map();
    private _videoCanvases: Map<Input, HTMLCanvasElement> = new Map();
    private _videoContexts: Map<Input, CanvasRenderingContext2D> = new Map();
    private _videoTextures: Map<Input, Texture> = new Map();
    private _videoSprites: Map<Input, Sprite> = new Map();

    /**
     * Create a ScriptLayerClip from an array of InputConfigs.
     * Each InputConfig contains a MediaBunny Input that may have audio and/or video.
     */
    public constructor(
        inputConfigs: ScriptLayerInputConfig[],
        props: ScriptLayerClipProps = {},
        id?: string
    ) {
        super();
        this.inputConfigs = inputConfigs || [];

        if (id) {
            this.id = id;
        }

        if (props.gain !== undefined) this.gain = props.gain;
        this.props = props;
        Object.assign(this, props);

        // IMPORTANT: Clear _width and _height so @visualize doesn't scale the view.
        // ScriptLayerClip has multiple video sprites that each manage their own size.
        // Letting @visualize set view.width/height would scale ALL children together,
        // breaking independent positioning. Each sprite's size is controlled via visual.width/height.
        this._width = undefined;
        this._height = undefined;

        // Extract names and colors from configs (preserve indices - don't filter!)
        this.names = props.names || inputConfigs.map(c => c.name || '');
        this.colors = props.colors || inputConfigs.map(c => c.color || '');

        // Video sprites are added dynamically in init() when we know which inputs have video
    }

    /**
     * Create a ScriptLayerClip from an array of InputConfigs.
     * This is the preferred way to create a ScriptLayerClip.
     */
    public static fromInputConfigs(
        configs: ScriptLayerInputConfig[],
        props: ScriptLayerClipProps = {},
        id?: string
    ): ScriptLayerClip {
        return new ScriptLayerClip(configs, props, id);
    }

    // ============ Segment Derivation Methods ============

    /**
     * Get the derived segments for this clip.
     * Segments are computed from scriptCuts and splits.
     */
    public get scriptSegments(): ScriptLayerSegment[] {
        if (this._segmentsDirty) {
            this.deriveScriptSegments();
        }
        return this._scriptSegments;
    }
    
    /**
     * Mark segments as needing recalculation.
     */
    private markSegmentsDirty(): void {
        this._segmentsDirty = true;
        // Also reset segment tracking since segment structure is changing
        this._lastSegmentIndex = -1;
    }
    
    /**
     * Get the effective/playable duration after cuts.
     * This is the sum of all segment durations.
     * When there are no cuts, returns the full source duration.
     */
    public get effectiveDuration(): number {
        // Use getter to ensure segments are fresh
        const segments = this.scriptSegments;
        if (!segments.length) {
            return this.duration?.seconds ?? 0;
        }
        let total = 0;
        for (const seg of segments) {
            total += (seg.srcEndSec - seg.srcStartSec);
        }
        return total;
    }

    /**
     * Normalize cuts to be within clip range, sorted, and merged.
     */
    public normalizeScriptCuts(): void {
        const inSec = this.range?.[0]?.seconds ?? 0;
        const outSec = this.range?.[1]?.seconds ?? this.duration?.seconds ?? inSec;
        const EPS = 1e-3;

        if (!Array.isArray(this.scriptCuts)) {
            this.scriptCuts = [];
            return;
        }

        // Clamp cuts to clip range and filter out tiny cuts
        let cs = this.scriptCuts
            .map(c => ({
                ...c,
                startSec: Math.max(inSec, Math.min(outSec, c.startSec)),
                endSec: Math.max(inSec, Math.min(outSec, c.endSec)),
            }))
            .filter(c => c.endSec - c.startSec > EPS);

        // Sort by start time
        cs.sort((a, b) => a.startSec - b.startSec);

        // Merge overlapping cuts
        const merged: ScriptLayerCut[] = [];
        for (const c of cs) {
            if (!merged.length) {
                merged.push({ ...c });
                continue;
            }
            const last = merged[merged.length - 1];
            if (c.startSec <= last.endSec + EPS) {
                // Overlapping - extend the last cut
                last.endSec = Math.max(last.endSec, c.endSec);
            } else {
                merged.push({ ...c });
            }
        }

        this.scriptCuts = merged;
    }

    /**
     * Normalize splits to be within clip range and sorted.
     */
    public normalizeSplits(): void {
        const inSec = this.range?.[0]?.seconds ?? 0;
        const outSec = this.range?.[1]?.seconds ?? this.duration?.seconds ?? inSec;

        if (!Array.isArray(this.splits)) {
            this.splits = [];
            return;
        }

        // Clamp splits to clip range and sort
        this.splits = this.splits
            .filter(s => s.timeSec > inSec && s.timeSec < outSec) // Exclude boundaries
            .sort((a, b) => a.timeSec - b.timeSec);

        // Remove duplicates (within EPS)
        const EPS = 1e-3;
        const unique: ScriptLayerSplit[] = [];
        for (const s of this.splits) {
            if (!unique.length || s.timeSec - unique[unique.length - 1].timeSec > EPS) {
                unique.push(s);
            }
        }
        this.splits = unique;
    }

    /**
     * Apply persisted edits (cuts/gaps/splits) as a single state update.
     * Used by Firestore restore / undo-redo to avoid generating new IDs.
     */
    public applyPersistedEdits(edits: {
        scriptCuts?: ScriptLayerCut[];
        gaps?: ScriptLayerGap[];
        gapInsertions?: ScriptLayerGapInsertion[];
        splits?: ScriptLayerSplit[];
        speakerOverrides?: SpeakerOverride[];
        speakerMuteRanges?: SpeakerMuteRange[];
        transcriptHiddenRanges?: TranscriptHiddenRange[];
        wordCorrections?: TranscriptWordCorrection[];
        transcriptParagraphBreaks?: TranscriptParagraphBreak[];
        transcriptMarkers?: TranscriptMarker[];
    }): void {
        if (Array.isArray(edits.scriptCuts)) {
            this.scriptCuts = [...edits.scriptCuts];
            this.normalizeScriptCuts();
        }
        if (Array.isArray(edits.gaps)) {
            this.gaps = [...edits.gaps];
            // Private helper handles clamping/merging; safe to call from within class.
            this.normalizeGaps();
        }
        if (Array.isArray(edits.gapInsertions)) {
            this.gapInsertions = [...edits.gapInsertions];
            this.normalizeGapInsertions();
        }
        if (Array.isArray(edits.splits)) {
            this.splits = [...edits.splits];
            this.normalizeSplits();
            this.invalidateScenesCache();
        }
        if (Array.isArray(edits.speakerOverrides)) {
            this.speakerOverrides = [...edits.speakerOverrides]
                .map((o: any) => ({
                    id: String(o?.id ?? ""),
                    startSec: Number(o?.startSec ?? 0),
                    endSec: Number(o?.endSec ?? 0),
                    sourceIndex: Number.isFinite(o?.sourceIndex) ? Number(o.sourceIndex) : undefined,
                    speakerName: String(o?.speakerName ?? ""),
                    color: typeof o?.color === "string" ? o.color : undefined,
                    speakerId: typeof o?.speakerId === "string" ? o.speakerId : undefined,
                    diarizationSpeakerId: typeof o?.diarizationSpeakerId === "string" ? o.diarizationSpeakerId : undefined,
                }))
                .filter(o => o.id && Number.isFinite(o.startSec) && Number.isFinite(o.endSec) && o.endSec > o.startSec);
            this.speakerOverrides.sort((a, b) => a.startSec - b.startSec);
        }
        if (Array.isArray(edits.speakerMuteRanges)) {
            this.speakerMuteRanges = [...edits.speakerMuteRanges]
                .map((r: any) => ({
                    id: String(r?.id ?? ""),
                    startSec: Number(r?.startSec ?? 0),
                    endSec: Number(r?.endSec ?? 0),
                    sourceIndex: Number.isFinite(r?.sourceIndex) ? Number(r.sourceIndex) : undefined,
                    diarizationSpeakerId: typeof r?.diarizationSpeakerId === "string" ? r.diarizationSpeakerId : undefined,
                    speakerId: typeof r?.speakerId === "string" ? r.speakerId : undefined,
                    speakerName: typeof r?.speakerName === "string" ? r.speakerName : undefined,
                }))
                .filter(r => r.id && Number.isFinite(r.startSec) && Number.isFinite(r.endSec) && r.endSec > r.startSec);
            this.normalizeSpeakerMuteRanges();
        }
        if (Array.isArray(edits.transcriptHiddenRanges)) {
            this.transcriptHiddenRanges = [...edits.transcriptHiddenRanges]
                .map((r: any) => ({
                    id: String(r?.id ?? ""),
                    startSec: Number(r?.startSec ?? 0),
                    endSec: Number(r?.endSec ?? 0),
                }))
                .filter(r => r.id && Number.isFinite(r.startSec) && Number.isFinite(r.endSec) && r.endSec > r.startSec)
                .sort((a, b) => a.startSec - b.startSec);
        }
        if (Array.isArray(edits.wordCorrections)) {
            this.wordCorrections = [...edits.wordCorrections]
                .map((c: any) => ({
                    id: String(c?.id ?? ""),
                    sourceIndex: Number.isFinite(c?.sourceIndex) ? Number(c.sourceIndex) : undefined,
                    srcStartSec: Number.isFinite(c?.srcStartSec) ? Number(c.srcStartSec) : undefined,
                    srcEndSec: Number.isFinite(c?.srcEndSec) ? Number(c.srcEndSec) : undefined,
                    correctedText: String(c?.correctedText ?? ""),
                }))
                .filter(c => c.id && c.correctedText)
                .sort((a, b) => (a.sourceIndex ?? 0) - (b.sourceIndex ?? 0));
        }
        if (Array.isArray(edits.transcriptParagraphBreaks)) {
            this.transcriptParagraphBreaks = [...edits.transcriptParagraphBreaks]
                .map((b: any) => ({
                    id: String(b?.id ?? ""),
                    sourceIndex: Number.isFinite(b?.sourceIndex) ? Number(b.sourceIndex) : undefined,
                    srcStartSec: Number.isFinite(b?.srcStartSec) ? Number(b.srcStartSec) : undefined,
                    srcEndSec: Number.isFinite(b?.srcEndSec) ? Number(b.srcEndSec) : undefined,
                    timeSec: Number.isFinite(b?.timeSec) ? Number(b.timeSec) : undefined,
                }))
                .filter(b => b.id)
                .sort((a, b) => (a.timeSec ?? 0) - (b.timeSec ?? 0));
        }
        if (Array.isArray(edits.transcriptMarkers)) {
            const cleanAnchor = (a: any): any | undefined => {
                if (!a || typeof a !== "object") return undefined;
                const out: any = {};
                if (Number.isFinite(a?.sourceIndex)) out.sourceIndex = Number(a.sourceIndex);
                if (Number.isFinite(a?.srcStartSec)) out.srcStartSec = Number(a.srcStartSec);
                if (Number.isFinite(a?.srcEndSec)) out.srcEndSec = Number(a.srcEndSec);
                if (Number.isFinite(a?.timeSec)) out.timeSec = Number(a.timeSec);
                return Object.keys(out).length ? out : undefined;
            };

            this.transcriptMarkers = [...edits.transcriptMarkers]
                .map((m: any) => {
                    const id = String(m?.id ?? "");
                    const title = String(m?.title ?? "").trim();
                    if (!id || !title) return null;

                    const rec: any = { id, title };
                    const origin = String(m?.origin ?? "").trim();
                    if (origin) rec.origin = origin;
                    const after = cleanAnchor(m?.after);
                    const before = cleanAnchor(m?.before);
                    if (after) rec.after = after;
                    if (before) rec.before = before;
                    if (Number.isFinite(m?.timeSec)) rec.timeSec = Number(m.timeSec);
                    if (Number.isFinite(m?.createdAt)) rec.createdAt = Number(m.createdAt);
                    if (Number.isFinite(m?.updatedAt)) rec.updatedAt = Number(m.updatedAt);
                    return rec;
                })
                .filter(Boolean)
                .sort((a: any, b: any) => Number(a?.timeSec ?? 0) - Number(b?.timeSec ?? 0));
        }
        this.markSegmentsDirty();
        this.trigger("frame", undefined);
    }

    /**
     * Derive segments from scriptCuts and splits.
     * Segments are playable spans packed contiguously on the timeline.
     */
    public deriveScriptSegments(): void {
        const inSec = this.range?.[0]?.seconds ?? 0;
        const outSec = this.range?.[1]?.seconds ?? this.duration?.seconds ?? inSec;
        const baseCompStart = this.start?.seconds ?? (this.offset?.seconds ?? 0) + inSec;
        this.normalizeSpeakerMuteRanges();

        // Collect all boundaries with their types
        interface Boundary {
            time: number;
            type: 'cut-start' | 'cut-end' | 'split';
            cut?: ScriptLayerCut;
            split?: ScriptLayerSplit;
        }

        const boundaries: Boundary[] = [];

        // Add cut boundaries
        if (this.scriptCuts) {
            for (const cut of this.scriptCuts) {
                const s = Math.max(inSec, Math.min(outSec, cut.startSec));
                const e = Math.max(inSec, Math.min(outSec, cut.endSec));
                if (e > s) {
                    boundaries.push({time: s, type: 'cut-start', cut});
                    boundaries.push({time: e, type: 'cut-end', cut});
                }
            }
        }

        // Add split boundaries
        if (this.splits) {
            for (const split of this.splits) {
                if (split.timeSec > inSec && split.timeSec < outSec) {
                    boundaries.push({time: split.timeSec, type: 'split', split});
                }
            }
        }

        // Sort by time
        boundaries.sort((a, b) => a.time - b.time);

        // Build segments by walking through the timeline
        const segments: ScriptLayerSegment[] = [];
        let cursor = inSec;
        let compCursor = baseCompStart;
        let inCut = false;
        let lastCut: ScriptLayerCut | undefined;
        let pendingStartTransition: TransitionConfig | undefined;
        let sceneIndex = 0;

        // Process each boundary
        for (const b of boundaries) {
            if (b.type === 'cut-start') {
                // End current segment before cut
                if (b.time > cursor && !inCut) {
                    const segDur = b.time - cursor;
                    segments.push({
                        srcStartSec: cursor,
                        srcEndSec: b.time,
                        compStartSec: compCursor,
                        startTransition: pendingStartTransition,
                        endTransition: b.cut?.transition ?? DEFAULT_CUT_TRANSITION,
                    });
                    compCursor += segDur;
                    pendingStartTransition = undefined;
                }
                inCut = true;
                lastCut = b.cut;
            } else if (b.type === 'cut-end') {
                // Coming out of a cut - next segment will start after cut
                cursor = b.time;
                inCut = false;
                pendingStartTransition = lastCut?.transition ?? DEFAULT_CUT_TRANSITION;
            } else if (b.type === 'split' && !inCut) {
                // Split creates a segment boundary (no content removed)
                if (b.time > cursor) {
                    const segDur = b.time - cursor;
                    const defaultSplitTransition = b.split?.transition ?? DEFAULT_SPLIT_TRANSITION;
                    let endTransition: TransitionConfig | undefined = defaultSplitTransition;
                    let nextStartTransition: TransitionConfig | undefined = defaultSplitTransition;

                    // Scene boundary splits use scene in/out transition configs (not split.transition).
                    if (b.split?.isSceneBoundary) {
                        const boundarySceneIndex = sceneIndex + 1;
                        const { outTransition, inTransition } = this.getBoundaryTransitions(boundarySceneIndex);
                        endTransition = outTransition;
                        nextStartTransition = inTransition;
                        sceneIndex = boundarySceneIndex;
                    }

                    segments.push({
                        srcStartSec: cursor,
                        srcEndSec: b.time,
                        compStartSec: compCursor,
                        startTransition: pendingStartTransition,
                        endTransition,
                    });
                    compCursor += segDur;
                    cursor = b.time;
                    pendingStartTransition = nextStartTransition;
                }
            }
        }

        // Final segment (from last boundary to outSec)
        if (cursor < outSec && !inCut) {
            segments.push({
                srcStartSec: cursor,
                srcEndSec: outSec,
                compStartSec: compCursor,
                startTransition: pendingStartTransition,
                endTransition: undefined,
            });
        }

        // Post-process: split segments at gap boundaries and mark gap portions
        // Gaps don't collapse timeline - they just mark regions as silent
        if (this.gaps && this.gaps.length > 0) {
            const finalSegments: ScriptLayerSegment[] = [];
            
            for (const seg of segments) {
                // Find all gaps that overlap with this segment
                const overlappingGaps = this.gaps.filter(gap => 
                    gap.startSec < seg.srcEndSec && gap.endSec > seg.srcStartSec
                );
                
                if (overlappingGaps.length === 0) {
                    // No gaps overlap - keep segment as-is
                    finalSegments.push(seg);
                    continue;
                }
                
                // Split segment at gap boundaries
                let segCursor = seg.srcStartSec;
                let compOffset = 0;
                
                // Collect all boundary points within this segment
                const points: Array<{ time: number; isGapStart: boolean }> = [];
                for (const gap of overlappingGaps) {
                    if (gap.startSec > seg.srcStartSec && gap.startSec < seg.srcEndSec) {
                        points.push({ time: gap.startSec, isGapStart: true });
                    }
                    if (gap.endSec > seg.srcStartSec && gap.endSec < seg.srcEndSec) {
                        points.push({ time: gap.endSec, isGapStart: false });
                    }
                }
                points.sort((a, b) => a.time - b.time);
                
                // Check if segment starts inside a gap
                const startsInGap = overlappingGaps.some(gap => 
                    gap.startSec <= seg.srcStartSec && gap.endSec > seg.srcStartSec
                );
                let inGap = startsInGap;
                
                // Process each point
                for (const point of points) {
                    if (point.time > segCursor) {
                        // Create segment from cursor to this point
                        finalSegments.push({
                            srcStartSec: segCursor,
                            srcEndSec: point.time,
                            compStartSec: seg.compStartSec + compOffset,
                            startTransition: segCursor === seg.srcStartSec ? seg.startTransition : undefined,
                            endTransition: undefined,
                            isGap: inGap,
                        });
                        compOffset += (point.time - segCursor);
                        segCursor = point.time;
                    }
                    inGap = point.isGapStart;
                }
                
                // Final piece of segment
                if (segCursor < seg.srcEndSec) {
                    finalSegments.push({
                        srcStartSec: segCursor,
                        srcEndSec: seg.srcEndSec,
                        compStartSec: seg.compStartSec + compOffset,
                        startTransition: segCursor === seg.srcStartSec ? seg.startTransition : undefined,
                        endTransition: seg.endTransition,
                        isGap: inGap,
                    });
                }
            }
            
            this._scriptSegments = finalSegments;
        } else {
            this._scriptSegments = segments;
        }

        // Apply "gap insertions" (insert new silent time that shifts downstream content later).
        // Insertions are defined in clip-local time (script/transcript domain) and manifest as
        // holes in composition time between segments.
        if (this.gapInsertions && this.gapInsertions.length > 0) {
            this.normalizeGapInsertions();
            const EPS = 1e-3;

            let segs = this._scriptSegments;
            for (const ins of this.gapInsertions) {
                const at = Number(ins?.atSec ?? 0);
                const dur = Number(ins?.durationSec ?? 0);
                if (!Number.isFinite(at) || !Number.isFinite(dur) || dur <= EPS) continue;

                const next: ScriptLayerSegment[] = [];
                for (const seg of segs) {
                    // Segment entirely before the insertion point.
                    if (at >= seg.srcEndSec - EPS) {
                        next.push(seg);
                        continue;
                    }

                    // Segment entirely after (or at) the insertion point: shift later in comp-time.
                    if (at <= seg.srcStartSec + EPS) {
                        next.push({ ...seg, compStartSec: seg.compStartSec + dur });
                        continue;
                    }

                    // Insertion point is inside this segment: split into left/right pieces.
                    const leftDur = at - seg.srcStartSec;
                    const left: ScriptLayerSegment = {
                        ...seg,
                        srcEndSec: at,
                        // Insertion is not an edit boundary with transitions.
                        endTransition: undefined,
                    };
                    const right: ScriptLayerSegment = {
                        ...seg,
                        srcStartSec: at,
                        compStartSec: seg.compStartSec + leftDur + dur,
                        startTransition: undefined,
                    };

                    next.push(left);
                    next.push(right);
                }
                segs = next;
            }

            this._scriptSegments = segs;
        }
        
        // Build crossfade descriptors for true crossfades at segment boundaries
        this._crossfadeDescriptors = [];
        const finalSegs = this._scriptSegments;
        
        for (let i = 1; i < finalSegs.length; i++) {
            const prevSeg = finalSegs[i - 1];
            const currSeg = finalSegs[i];
            
            // Skip if either segment is a gap
            if (prevSeg.isGap || currSeg.isGap) continue;
            
            // Check if there's a jump in source time (indicating a cut, not a split)
            // A split has srcEndSec === next.srcStartSec (contiguous)
            // A cut has srcEndSec !== next.srcStartSec (jump)
            const isJumpCut = Math.abs(prevSeg.srcEndSec - currSeg.srcStartSec) > 0.001;
            if (!isJumpCut) continue;
            
            // Get fade duration - prefer audio analysis, fall back to transition config
            // Find the cut that corresponds to this segment boundary
            const matchingCut = this.scriptCuts?.find(cut => 
                Math.abs(cut.endSec - currSeg.srcStartSec) < 0.001
            );
            
            let fadeDurationMs: number;
	            if (matchingCut?.analysis) {
	                // Use audio analysis results - take the max of fadeIn and fadeOut
	                fadeDurationMs = Math.max(
	                    matchingCut.analysis.fadeInMs,
	                    matchingCut.analysis.fadeOutMs
	                );
	                if (DEBUG_SCRIPT_CLIP_LOGS) {
	                    console.log(`[ScriptClip] Using analyzed crossfade duration: ${fadeDurationMs}ms`);
	                }
	            } else {
	                // Fall back to transition config or default
	                const transitionConfig = currSeg.startTransition ?? DEFAULT_CUT_TRANSITION;
	                fadeDurationMs = transitionConfig.audio?.durationMs ?? MICROFADE_DURATION_MS;
	            }
            const fadeDurationSec = fadeDurationMs / 1000;
            
            // Calculate source time ranges for the crossfade
            // Tail of segment A (before the cut)
            const srcBeforeEndSec = prevSeg.srcEndSec;
            const srcBeforeStartSec = Math.max(prevSeg.srcStartSec, srcBeforeEndSec - fadeDurationSec);
            
            // Head of segment B (after the cut)
            const srcAfterStartSec = currSeg.srcStartSec;
            const srcAfterEndSec = Math.min(currSeg.srcEndSec, srcAfterStartSec + fadeDurationSec);
            
            // Calculate composition time for the crossfade
            // The crossfade covers the last fadeDuration of segment A (before the join)
            // Previous segment's comp end = compStartSec + duration
            const prevSegDur = prevSeg.srcEndSec - prevSeg.srcStartSec;
            const compJoinSec = prevSeg.compStartSec + prevSegDur;
            const compCrossfadeStartSec = compJoinSec - fadeDurationSec;
            const compCrossfadeEndSec = compJoinSec;  // Crossfade ends at the join point
            
            // Create the descriptor
            // Note: sourceIndex is 0 for now - we'll need to determine which source
            // when we have multiple sources with different timelines
            this._crossfadeDescriptors.push({
                sourceIndex: 0, // TODO: determine from segment metadata if needed
                srcBeforeStartSec,
                srcBeforeEndSec,
                srcAfterStartSec,
                srcAfterEndSec,
                compCrossfadeStartSec,
                compCrossfadeEndSec,
                fadeDurationSec,
                segmentAfterIndex: i,
            });
        }
        
        // Mark as no longer dirty
        this._segmentsDirty = false;
        
        // Reset logged fades so they'll be logged again on next playback
        this._loggedFades = undefined;
    }

    /**
     * Add a cut to the script layer (with transitions/microfades).
     * Note: Named addScriptCut to avoid conflict with MediaClip.addCut
     * @param transcriptMode - controls transcript display for removed content
     */
    public addScriptCut(
        startSec: number, 
        endSec: number, 
        transition?: TransitionConfig,
        transcriptMode?: "delete" | "strikethrough" | "retake-strikethrough",
        analysis?: CutAnalysis
    ): ScriptLayerCut {
        // If analysis provided, use snapped times and custom fade durations
        let finalStartSec = startSec;
        let finalEndSec = endSec;
        let finalTransition = transition;
        
	        if (analysis) {
	            finalStartSec = analysis.startAnalysis.snappedSec;
	            finalEndSec = analysis.endAnalysis.snappedSec;
            
            // Use analysis-derived fade durations if no transition provided
            if (!finalTransition) {
                finalTransition = {
                    audio: { 
                        type: 'microfade', 
                        durationMs: Math.max(analysis.fadeOutMs, analysis.fadeInMs) 
                    },
                    video: { type: 'cut', durationMs: 0 },
                };
	            }
	            
	            if (DEBUG_SCRIPT_CLIP_LOGS) {
	                console.log("[ScriptLayerClip] addScriptCut with analysis:", {
	                    originalStart: startSec,
	                    snappedStart: finalStartSec,
	                    originalEnd: endSec,
	                    snappedEnd: finalEndSec,
	                    fadeMs: finalTransition.audio?.durationMs,
	                    isHarsh: analysis.isHarsh,
	                    rmsRatio: analysis.rmsRatio.toFixed(2),
	                });
	            }
	        }
        
        const cut: ScriptLayerCut = {
            id: crypto?.randomUUID?.() ?? `cut_${Date.now()}_${Math.random()}`,
            startSec: finalStartSec,
            endSec: finalEndSec,
            transition: finalTransition,
            transcriptMode: transcriptMode ?? 'delete',
            analysis,
	        };
	        this.scriptCuts.push(cut);
	        
	        if (DEBUG_SCRIPT_CLIP_LOGS) {
	            console.log("[ScriptLayerClip] addScriptCut:", {
	                startSec,
	                endSec,
	                clipRangeIn: this.range?.[0]?.seconds,
	                clipRangeOut: this.range?.[1]?.seconds,
	                clipDuration: this.duration?.seconds,
	                cutsBeforeNorm: this.scriptCuts.length,
	            });
	        }
	        
	        this.normalizeScriptCuts();
	        
	        if (DEBUG_SCRIPT_CLIP_LOGS) {
	            const first = this.scriptCuts[0];
	            const last = this.scriptCuts[this.scriptCuts.length - 1];
	            console.log("[ScriptLayerClip] after normalizeScriptCuts:", {
	                cutsAfterNorm: this.scriptCuts.length,
	                first: first ? { id: first.id, start: first.startSec, end: first.endSec } : null,
	                last: last ? { id: last.id, start: last.startSec, end: last.endSec } : null,
	            });
	        }
	        
	        this.markSegmentsDirty();
        
        return cut;
    }

    /**
     * Add a split (boundary marker) to the script layer.
     * Splits are boundary points for transitions without removing content.
     */
    public addScriptSplit(timeSec: number, options?: { isSceneBoundary?: boolean; label?: string; transition?: TransitionConfig }): ScriptLayerSplit {
        const split: ScriptLayerSplit = {
            id: crypto?.randomUUID?.() ?? `split_${Date.now()}_${Math.random()}`,
            timeSec,
            ...options,
        };
        this.splits.push(split);
        this.normalizeSplits();
        this.markSegmentsDirty();
        // Invalidate scene cache if this affects scene boundaries
        if (options?.isSceneBoundary) {
            this.invalidateScenesCache();
        }
        return split;
    }

    /**
     * Remove a script cut by ID.
     */
    public removeScriptCut(cutId: string): boolean {
        const idx = this.scriptCuts.findIndex(c => c.id === cutId);
        if (idx >= 0) {
            this.scriptCuts.splice(idx, 1);
            this.markSegmentsDirty();
            return true;
        }
        return false;
    }

    /**
     * Add a gap to the script layer (replaces audio with silence, maintains timeline position).
     * Unlike cuts, gaps don't collapse the timeline.
     */
    public addScriptGap(startSec: number, endSec: number): ScriptLayerGap {
        const gap: ScriptLayerGap = {
            id: crypto?.randomUUID?.() ?? `gap_${Date.now()}_${Math.random()}`,
            startSec,
            endSec,
        };
        this.gaps.push(gap);
        this.normalizeGaps();
        this.markSegmentsDirty();
        return gap;
    }

    /**
     * Insert silent time into the script layer timeline.
     * This ADDS new time (unlike ScriptLayerGap which replaces existing content with silence).
     *
     * @param atSec - Clip-local time (transcript domain) where the insertion occurs
     * @param durationSec - Duration of inserted silence in seconds
     */
	    public addScriptGapInsertion(atSec: number, durationSec: number): ScriptLayerGapInsertion {
	        const insertion: ScriptLayerGapInsertion = {
	            id: crypto?.randomUUID?.() ?? `gapins_${Date.now()}_${Math.random()}`,
	            atSec,
	            durationSec,
	        };
	        this.gapInsertions.push(insertion);
	        this.normalizeGapInsertions();
	        this.markSegmentsDirty();
	        return insertion;
	    }

	    /**
	     * Insert a new empty scene on the ScriptClip spine.
	     * This creates "fillable spine time" by pushing all downstream content later and
	     * leaving a silent region with no active sources.
	     *
	     * Implementation details:
	     * - Uses per-source `segments` (when present) to split active segments at the insertion point.
	     * - Shifts all segments that start at/after the insertion point by `durationSec`.
	     * - Ripples all downstream edit state (cuts/gaps/splits/speaker overrides) by `durationSec`.
	     * - Creates scene boundary splits at the start/end of the empty scene (as needed).
	     */
	    public insertEmptyScene(atSec: number, durationSec: number): boolean {
	        const EPS = 1e-3;
	        const dur = Number(durationSec ?? 0);
	        if (!Number.isFinite(dur) || dur <= EPS) return false;

	        const inSec = this.range?.[0]?.seconds ?? 0;
	        const outSec = this.range?.[1]?.seconds ?? this.duration?.seconds ?? inSec;
	        const at = Number(atSec ?? 0);
	        if (!Number.isFinite(at)) return false;

	        // Clamp insertion to current clip bounds (in clip-local time).
	        const clampedAt = Math.max(inSec, Math.min(outSec, at));

	        const isAtStart = clampedAt <= inSec + EPS;
	        const isAtEnd = clampedAt >= outSec - EPS;

	        // === 0) Keep sceneProperties stable when inserting new scenes ===
	        // sceneProperties is keyed by scene index, so inserting an empty scene changes indices.
	        // Rules:
	        // - Insert at start: shift all existing scenes +1.
	        // - Insert at an existing scene boundary: shift all scenes at/after that boundary +1.
	        // - Insert inside a scene: split that scene into {left, empty, right}
	        //   - left keeps original props but drops outTransition (new boundary)
	        //   - right keeps original props but drops inTransition (new boundary)
	        //   - scenes after shift +2
	        if (this.sceneProperties?.size) {
	            const scenesBefore = this.getScenes();
	            const BOUNDARY_EPS = 0.01;
	            const existingProps = new Map<number, SceneProperties>(this.sceneProperties);

	            const cloneSceneProps = (props: SceneProperties): SceneProperties => {
	                const cloned: SceneProperties = { ...(props as any) };
                    const layoutByAspect = (props as any)?.layoutByAspect;
                    if (layoutByAspect && typeof layoutByAspect === "object") {
                        try { (cloned as any).layoutByAspect = { ...(layoutByAspect as any) }; } catch {}
                    }
	                const vis = (props as any)?.visualOverrides;
	                if (vis && typeof vis?.entries === "function") {
	                    const clonedVis = new Map<number, any>();
	                    for (const [k, v] of vis.entries()) {
	                        clonedVis.set(Number(k), { ...(v ?? {}) });
	                    }
	                    (cloned as any).visualOverrides = clonedVis;
	                }
                    const visByAspect = (props as any)?.visualOverridesByAspect;
                    if (visByAspect && typeof visByAspect === "object") {
                        const clonedByAspect: any = {};
                        for (const [presetKey, mapRaw] of Object.entries(visByAspect as any)) {
                            const map: any = mapRaw as any;
                            if (!map || typeof map?.entries !== "function") continue;
                            const clonedMap = new Map<number, any>();
                            for (const [k, v] of map.entries()) {
                                clonedMap.set(Number(k), { ...(v ?? {}) });
                            }
                            clonedByAspect[presetKey] = clonedMap;
                        }
                        if (Object.keys(clonedByAspect).length > 0) {
                            (cloned as any).visualOverridesByAspect = clonedByAspect;
                        }
                    }
	                if ((props as any)?.inTransition) {
	                    (cloned as any).inTransition = JSON.parse(JSON.stringify((props as any).inTransition));
	                }
	                if ((props as any)?.outTransition) {
	                    (cloned as any).outTransition = JSON.parse(JSON.stringify((props as any).outTransition));
	                }
	                return cloned;
	            };

	            if (isAtStart) {
	                const shifted = new Map<number, SceneProperties>();
	                for (const [idx, props] of existingProps) {
	                    shifted.set(idx + 1, props);
	                }
	                this.sceneProperties = shifted;
	            } else if (!isAtEnd) {
	                // Find if insertion is exactly at a scene boundary (start of a non-first scene).
	                const boundaryScene = scenesBefore.find(
	                    (s) => (s?.index ?? 0) > 0 && Math.abs(Number(s?.startSec ?? NaN) - clampedAt) <= BOUNDARY_EPS
	                );

	                if (boundaryScene) {
	                    const boundaryIndex = boundaryScene.index;
	                    const shifted = new Map<number, SceneProperties>();
	                    for (const [idx, props] of existingProps) {
	                        shifted.set(idx >= boundaryIndex ? idx + 1 : idx, props);
	                    }
	                    this.sceneProperties = shifted;
	                } else {
	                    // Insertion inside a scene: duplicate that scene's properties onto the new right-side scene.
	                    const containing =
	                        scenesBefore.find(
	                            (s) =>
	                                Number(s?.startSec ?? 0) + EPS < clampedAt &&
	                                clampedAt < Number(s?.endSec ?? 0) - EPS
	                        ) ?? scenesBefore.find((s) => clampedAt >= Number(s?.startSec ?? 0) && clampedAt < Number(s?.endSec ?? 0));

	                    const sceneIndex = Number(containing?.index ?? NaN);
	                    if (Number.isFinite(sceneIndex)) {
	                        const base = existingProps.get(sceneIndex);
	                        const leftProps = base ? cloneSceneProps(base) : undefined;
	                        const rightProps = base ? cloneSceneProps(base) : undefined;

	                        // New boundary at clampedAt ends the left scene → drop its outTransition.
	                        if (leftProps && "outTransition" in (leftProps as any)) {
	                            delete (leftProps as any).outTransition;
	                        }
	                        // New boundary at emptyEnd starts the right scene → drop its inTransition.
	                        if (rightProps && "inTransition" in (rightProps as any)) {
	                            delete (rightProps as any).inTransition;
	                        }

	                        const shifted = new Map<number, SceneProperties>();
	                        for (const [idx, props] of existingProps) {
	                            if (idx < sceneIndex) shifted.set(idx, props);
	                            else if (idx === sceneIndex) {
	                                // handled below
	                            } else {
	                                shifted.set(idx + 2, props);
	                            }
	                        }
	                        if (leftProps && Object.keys(leftProps as any).length > 0) shifted.set(sceneIndex, leftProps);
	                        else shifted.delete(sceneIndex);
	                        if (rightProps && Object.keys(rightProps as any).length > 0) shifted.set(sceneIndex + 2, rightProps);
	                        this.sceneProperties = shifted;
	                    }
	                }
	            }
	        }

	        // Determine whether we already have a scene boundary at this exact time.
	        const hasSceneBoundaryAt = (time: number): boolean => {
	            return (this.splits ?? []).some(s => s?.isSceneBoundary && Math.abs(Number(s?.timeSec ?? NaN) - time) <= 0.01);
	        };

	        // === 1) Ripple-shift edit state (time domain: clip-local seconds) ===
	        // Splits: shift strictly after the insertion point (so a boundary at `at` stays as the boundary
	        // between the previous scene and the newly inserted empty scene).
	        if (Array.isArray(this.splits)) {
	            for (const s of this.splits) {
	                if (!s || !Number.isFinite(s.timeSec)) continue;
	                if (s.timeSec > clampedAt + EPS) {
	                    s.timeSec += dur;
	                }
	            }
	        }

	        // Cuts/gaps/speaker overrides: shift starts/ends at-or-after insertion; extend ranges that straddle it.
	        const rippleRanges = <T extends { startSec: number; endSec: number }>(ranges: T[] | undefined): void => {
	            if (!Array.isArray(ranges)) return;
	            for (const r of ranges) {
	                if (!r) continue;
	                const s = Number(r.startSec ?? 0);
	                const e = Number(r.endSec ?? 0);
	                if (!Number.isFinite(s) || !Number.isFinite(e)) continue;
	                if (e <= clampedAt + EPS) continue;
	                if (s >= clampedAt - EPS) {
	                    r.startSec = s + dur;
	                    r.endSec = e + dur;
	                } else {
	                    // Straddles the insertion point: extend the end to preserve the same "content span".
	                    r.endSec = e + dur;
	                }
	            }
	        };

	        rippleRanges(this.scriptCuts as any);
	        rippleRanges(this.gaps as any);
	        rippleRanges(this.speakerOverrides as any);
            rippleRanges(this.speakerMuteRanges as any);

	        // === 2) Update per-source segments to create the empty region ===
	        const generateSegId = () => crypto?.randomUUID?.() ?? `seg_${Date.now()}_${Math.random()}`;

	        for (const cfg of this.inputConfigs) {
	            const cfgRate = Number(cfg.playbackRate ?? 1.0) || 1.0;
	            const baseRange = cfg.range;
	            const baseOffset = Number(cfg.offsetSec ?? 0) || 0;

	            const rawSegments: ScriptLayerSourceSegment[] =
	                Array.isArray((cfg as any).segments) && (cfg as any).segments.length > 0
	                    ? ((cfg as any).segments as ScriptLayerSourceSegment[])
	                    : baseRange
	                      ? [
	                            {
	                                id: generateSegId(),
	                                offsetSec: baseOffset,
	                                range: { inSec: Number(baseRange.inSec ?? 0), outSec: Number(baseRange.outSec ?? 0) },
	                                playbackRate: cfgRate,
	                            },
	                        ]
	                      : [];

	            const nextSegments: ScriptLayerSourceSegment[] = [];
	            for (const seg of rawSegments) {
	                const segRate = Number(seg.playbackRate ?? cfgRate) || 1.0;
	                const segOffset = Number(seg.offsetSec ?? 0);
	                const segIn = Number(seg.range?.inSec ?? 0);
	                const segOut = Number(seg.range?.outSec ?? 0);
	                if (!Number.isFinite(segRate) || segRate <= 0) continue;
	                if (!Number.isFinite(segOffset) || !Number.isFinite(segIn) || !Number.isFinite(segOut) || segOut <= segIn) continue;

	                const segDur = (segOut - segIn) / segRate;
	                const segEnd = segOffset + segDur;

	                if (segEnd <= clampedAt + EPS) {
	                    // Entirely before the insertion.
	                    nextSegments.push({ ...seg, id: seg.id || generateSegId() });
	                    continue;
	                }

	                if (segOffset >= clampedAt - EPS) {
	                    // Entirely after the insertion (or starts exactly at it): shift later.
	                    nextSegments.push({ ...seg, id: seg.id || generateSegId(), offsetSec: segOffset + dur });
	                    continue;
	                }

	                // Insertion point is inside this segment: split into left/right pieces.
	                const splitLocal = clampedAt - segOffset;
	                const splitSrc = segIn + splitLocal * segRate;
	                if (!Number.isFinite(splitSrc) || splitSrc <= segIn + EPS || splitSrc >= segOut - EPS) {
	                    // Degenerate split; fall back to shifting the whole segment.
	                    nextSegments.push({ ...seg, id: seg.id || generateSegId(), offsetSec: segOffset + dur });
	                    continue;
	                }

	                // Left piece: [segIn, splitSrc] at original offset.
	                nextSegments.push({
	                    id: seg.id || generateSegId(),
	                    offsetSec: segOffset,
	                    range: { inSec: segIn, outSec: splitSrc },
	                    playbackRate: segRate,
	                });
	                // Right piece: [splitSrc, segOut] starts after the inserted empty time.
	                nextSegments.push({
	                    id: generateSegId(),
	                    offsetSec: clampedAt + dur,
	                    range: { inSec: splitSrc, outSec: segOut },
	                    playbackRate: segRate,
	                });
	            }

	            nextSegments.sort((a, b) => (a.offsetSec ?? 0) - (b.offsetSec ?? 0));
	            (cfg as any).segments = nextSegments;
	        }

	        // Mirror segment changes into derived runtime entries.
	        const byInput = new Map<Input, ScriptLayerSourceSegment[]>();
	        for (const cfg of this.inputConfigs) {
	            if (!cfg?.input) continue;
	            const segs: ScriptLayerSourceSegment[] = Array.isArray((cfg as any).segments) ? (cfg as any).segments : [];
	            byInput.set(cfg.input, segs);
	        }
	        for (const e of this.audioEntries) {
	            if (!e?.input) continue;
	            e.segments = byInput.get(e.input);
	        }
	        for (const e of this.videoEntries) {
	            if (!e?.input) continue;
	            e.segments = byInput.get(e.input);
	        }

	        // === 3) Update clip duration/range ===
	        let maxEnd = outSec + dur;
	        for (const cfg of this.inputConfigs) {
	            const cfgRate = Number(cfg.playbackRate ?? 1.0) || 1.0;
	            const segs: ScriptLayerSourceSegment[] = Array.isArray((cfg as any).segments) ? (cfg as any).segments : [];
	            for (const seg of segs) {
	                const segRate = Number(seg.playbackRate ?? cfgRate) || 1.0;
	                const segOffset = Number(seg.offsetSec ?? 0);
	                const segIn = Number(seg.range?.inSec ?? 0);
	                const segOut = Number(seg.range?.outSec ?? 0);
	                if (!Number.isFinite(segRate) || segRate <= 0) continue;
	                if (!Number.isFinite(segOffset) || !Number.isFinite(segIn) || !Number.isFinite(segOut) || segOut <= segIn) continue;
	                const segDur = (segOut - segIn) / segRate;
	                maxEnd = Math.max(maxEnd, segOffset + segDur);
	            }
	        }

	        if (Number.isFinite(maxEnd) && maxEnd > 0) {
	            this.duration.seconds = maxEnd;
	            try {
	                if (this.range && this.range.length === 2) {
	                    this.range = [Timestamp.fromSeconds(inSec), Timestamp.fromSeconds(maxEnd)];
	                }
	            } catch {}
	        }

	        // === 4) Create scene boundary splits for the new empty scene ===
	        // Note: We MUST update range first so that a boundary at the old clip end becomes
	        // an interior split (otherwise normalizeSplits() would drop it as "at the boundary").
	        //
	        // Ensure a boundary at the start (unless we're at the clip start).
	        if (!isAtStart && !hasSceneBoundaryAt(clampedAt)) {
	            this.addSceneBoundary(clampedAt);
	        } else if (!isAtStart) {
	            // Ensure split is marked as a scene boundary if it already exists there.
	            const existing = (this.splits ?? []).find(s => Math.abs(Number(s?.timeSec ?? NaN) - clampedAt) <= 0.01);
	            if (existing) existing.isSceneBoundary = true;
	        }

	        // Ensure a boundary at the end of the empty scene (unless we're inserting at the old clip end).
	        const emptyEnd = clampedAt + dur;
	        if (!isAtEnd && !hasSceneBoundaryAt(emptyEnd)) {
	            this.addSceneBoundary(emptyEnd);
	        }

	        // Normalize splits after modifications.
	        try { this.normalizeSplits(); } catch {}
	        this.invalidateScenesCache();

	        this.markSegmentsDirty();
	        this.trigger("frame", undefined);
	        return true;
	    }

    /**
     * Remove a gap insertion by ID.
     */
    public removeScriptGapInsertion(insertionId: string): boolean {
        const idx = this.gapInsertions.findIndex(g => g.id === insertionId);
        if (idx >= 0) {
            this.gapInsertions.splice(idx, 1);
            this.markSegmentsDirty();
            return true;
        }
        return false;
    }

    /**
     * Remove a gap by ID.
     */
    public removeScriptGap(gapId: string): boolean {
        const idx = this.gaps.findIndex(g => g.id === gapId);
        if (idx >= 0) {
            this.gaps.splice(idx, 1);
            this.markSegmentsDirty();
            return true;
        }
        return false;
    }

    /**
     * Returns true when this source is muted by a speaker mute range at the given clip-local time.
     * A mute range with undefined sourceIndex applies to all sources.
     */
    public isTimeInSpeakerMuteRange(timeSec: number, sourceIndex: number): boolean {
        if (!Array.isArray(this.speakerMuteRanges) || this.speakerMuteRanges.length === 0) return false;
        const t = Number(timeSec);
        if (!Number.isFinite(t)) return false;
        for (const r of this.speakerMuteRanges) {
            const s = Number((r as any)?.startSec ?? NaN);
            const e = Number((r as any)?.endSec ?? NaN);
            if (!Number.isFinite(s) || !Number.isFinite(e) || e <= s) continue;
            const src = Number((r as any)?.sourceIndex);
            const appliesToSource = !Number.isFinite(src) || src === sourceIndex;
            if (appliesToSource && t >= s && t < e) return true;
        }
        return false;
    }

    /**
     * Normalize gaps to be within clip range, sorted, and merged.
     */
    private normalizeGaps(): void {
        const inSec = this.range?.[0]?.seconds ?? 0;
        const outSec = this.range?.[1]?.seconds ?? this.duration?.seconds ?? inSec;
        const EPS = 1e-3;

        if (!Array.isArray(this.gaps)) {
            this.gaps = [];
            return;
        }

        // Clamp gaps to clip range and filter out tiny gaps
        let gs = this.gaps
            .map(g => ({
                ...g,
                startSec: Math.max(inSec, Math.min(outSec, g.startSec)),
                endSec: Math.max(inSec, Math.min(outSec, g.endSec)),
            }))
            .filter(g => g.endSec - g.startSec > EPS);

        // Sort by start time
        gs.sort((a, b) => a.startSec - b.startSec);

        // Merge overlapping gaps
        const merged: ScriptLayerGap[] = [];
        for (const g of gs) {
            if (!merged.length) {
                merged.push({ ...g });
                continue;
            }
            const last = merged[merged.length - 1];
            if (g.startSec <= last.endSec + EPS) {
                // Overlapping - extend the last gap
                last.endSec = Math.max(last.endSec, g.endSec);
            } else {
                merged.push({ ...g });
            }
        }

        this.gaps = merged;
    }

    /**
     * Normalize speaker mute ranges to be within clip range and sorted.
     * Intentionally does not merge ranges so per-speaker provenance remains explicit.
     */
    private normalizeSpeakerMuteRanges(): void {
        const inSec = this.range?.[0]?.seconds ?? 0;
        const outSec = this.range?.[1]?.seconds ?? this.duration?.seconds ?? inSec;
        const EPS = 1e-3;

        if (!Array.isArray(this.speakerMuteRanges)) {
            this.speakerMuteRanges = [];
            return;
        }

        this.speakerMuteRanges = this.speakerMuteRanges
            .map((r) => ({
                ...r,
                startSec: Math.max(inSec, Math.min(outSec, Number((r as any)?.startSec ?? 0))),
                endSec: Math.max(inSec, Math.min(outSec, Number((r as any)?.endSec ?? 0))),
                sourceIndex: Number.isFinite((r as any)?.sourceIndex) ? Number((r as any).sourceIndex) : undefined,
                diarizationSpeakerId:
                    typeof (r as any)?.diarizationSpeakerId === "string"
                        ? String((r as any).diarizationSpeakerId)
                        : undefined,
                speakerId: typeof (r as any)?.speakerId === "string" ? String((r as any).speakerId) : undefined,
                speakerName: typeof (r as any)?.speakerName === "string" ? String((r as any).speakerName) : undefined,
                id: String((r as any)?.id ?? ""),
            }))
            .filter((r) => r.id && Number.isFinite(r.startSec) && Number.isFinite(r.endSec) && r.endSec - r.startSec > EPS)
            .sort((a, b) => a.startSec - b.startSec || a.endSec - b.endSec || String(a.id).localeCompare(String(b.id)));
    }

    /**
     * Normalize gap insertions:
     * - Filter insertions outside the current clip range
     * - Filter tiny insertions
     * - Sort by atSec and merge insertions at the same point
     */
    private normalizeGapInsertions(): void {
        const inSec = this.range?.[0]?.seconds ?? 0;
        const outSec = this.range?.[1]?.seconds ?? this.duration?.seconds ?? inSec;
        const EPS = 1e-3;

        if (!Array.isArray(this.gapInsertions)) {
            this.gapInsertions = [];
            return;
        }

        let ins = this.gapInsertions
            .map(g => ({
                ...g,
                atSec: Number(g.atSec ?? 0),
                durationSec: Number(g.durationSec ?? 0),
            }))
            .filter(g => Number.isFinite(g.atSec) && Number.isFinite(g.durationSec) && g.durationSec > EPS)
            // Keep only insertions that fall within the current (trimmed) clip range.
            .filter(g => g.atSec >= inSec - EPS && g.atSec <= outSec + EPS)
            .map(g => ({
                ...g,
                atSec: Math.max(inSec, Math.min(outSec, g.atSec)),
            }));

        ins.sort((a, b) => a.atSec - b.atSec);

        const merged: ScriptLayerGapInsertion[] = [];
        for (const g of ins) {
            if (!merged.length) {
                merged.push({ ...g });
                continue;
            }
            const last = merged[merged.length - 1];
            if (Math.abs(g.atSec - last.atSec) <= EPS) {
                last.durationSec += g.durationSec;
            } else {
                merged.push({ ...g });
            }
        }

        this.gapInsertions = merged;
    }

    /**
     * Remove a script split by ID.
     */
    public removeScriptSplit(splitId: string): boolean {
        const idx = this.splits.findIndex(s => s.id === splitId);
        if (idx >= 0) {
            this.splits.splice(idx, 1);
            this.markSegmentsDirty();
            this.invalidateScenesCache();
            return true;
        }
        return false;
    }

    /**
     * Set the transition config for a split (scene boundary).
     * Used to configure audio/video transitions at scene boundaries.
     */
    public setSplitTransition(splitId: string, transition: TransitionConfig): boolean {
        const split = this.splits.find(s => s.id === splitId);
        if (!split) return false;

        split.transition = transition;
        this.markSegmentsDirty();
        return true;
    }

    /**
     * Get the transition config for a split.
     */
    public getSplitTransition(splitId: string): TransitionConfig | undefined {
        return this.splits.find(s => s.id === splitId)?.transition;
    }

    // ============ Scene Methods ============

    /**
     * Invalidate the scenes cache (call when splits change).
     */
    private invalidateScenesCache(): void {
        this._scenesCache = null;
    }

    /**
     * Get all scenes derived from scene boundary splits.
     * Scenes are spans of content between scene boundaries (or clip start/end).
     * Scene 0 starts at the clip's in-point, subsequent scenes start at isSceneBoundary splits.
     */
    public getScenes(): ScriptLayerScene[] {
        // Return cached scenes if available
        if (this._scenesCache !== null) {
            return this._scenesCache;
        }

        const inSec = this.range?.[0]?.seconds ?? 0;
        const outSec = this.range?.[1]?.seconds ?? this.duration?.seconds ?? inSec;

        // Get all scene boundary splits, sorted by time
        const sceneBoundarySplits = this.splits
            .filter(s => s.isSceneBoundary === true)
            .sort((a, b) => a.timeSec - b.timeSec);

        const scenes: ScriptLayerScene[] = [];
        let sceneStart = inSec;
        let sceneIndex = 0;
        let timelinePosition = 0; // Cumulative timeline position

        // Build scenes from scene boundary splits
        for (const split of sceneBoundarySplits) {
            // Skip splits outside clip range
            if (split.timeSec <= inSec || split.timeSec >= outSec) continue;

            const properties = this.sceneProperties.get(sceneIndex) ?? {};
            const playbackRate = properties.playbackRate ?? 1.0;
            const sourceDuration = split.timeSec - sceneStart;
            const timelineDuration = sourceDuration / playbackRate;

            // Create scene from previous boundary to this split
            scenes.push({
                index: sceneIndex,
                startSec: sceneStart,
                endSec: split.timeSec,
                timelineStartSec: timelinePosition,
                timelineEndSec: timelinePosition + timelineDuration,
                label: sceneIndex === 0 ? undefined : this.getSceneLabelFromSplit(sceneIndex - 1),
                startSplitId: sceneIndex === 0 ? undefined : sceneBoundarySplits[sceneIndex - 1]?.id,
                properties,
            });

            sceneStart = split.timeSec;
            timelinePosition += timelineDuration;
            sceneIndex++;
        }

        // Create final scene (from last boundary to clip end)
        const finalProperties = this.sceneProperties.get(sceneIndex) ?? {};
        const finalPlaybackRate = finalProperties.playbackRate ?? 1.0;
        const finalSourceDuration = outSec - sceneStart;
        const finalTimelineDuration = finalSourceDuration / finalPlaybackRate;

        scenes.push({
            index: sceneIndex,
            startSec: sceneStart,
            endSec: outSec,
            timelineStartSec: timelinePosition,
            timelineEndSec: timelinePosition + finalTimelineDuration,
            label: sceneBoundarySplits.length > 0
                ? sceneBoundarySplits[sceneBoundarySplits.length - 1].label
                : undefined,
            startSplitId: sceneBoundarySplits.length > 0
                ? sceneBoundarySplits[sceneBoundarySplits.length - 1].id
                : undefined,
            properties: finalProperties,
        });

        this._scenesCache = scenes;
        return scenes;
    }

    /**
     * Helper to get label for a scene from the split that starts it.
     */
    private getSceneLabelFromSplit(splitIndex: number): string | undefined {
        const sceneBoundarySplits = this.splits
            .filter(s => s.isSceneBoundary === true)
            .sort((a, b) => a.timeSec - b.timeSec);
        return sceneBoundarySplits[splitIndex]?.label;
    }

    /**
     * Find which scene contains a given source time.
     * Returns the scene index, or -1 if not found.
     */
    public findSceneIndexForTime(srcTimeSec: number): number {
        const scenes = this.getScenes();
        for (const scene of scenes) {
            if (srcTimeSec >= scene.startSec && srcTimeSec < scene.endSec) {
                return scene.index;
            }
        }
        // Check if exactly at the end of last scene
        if (scenes.length > 0) {
            const lastScene = scenes[scenes.length - 1];
            if (Math.abs(srcTimeSec - lastScene.endSec) < 0.001) {
                return lastScene.index;
            }
        }
        return -1;
    }

    /**
     * Check if we're in a visual transition zone at the given source time.
     * Returns transition info if in a transition, null otherwise.
     *
     * Transitions happen BEFORE scene boundaries - the video gradually changes
     * from scene A properties to scene B properties as we approach the boundary.
     */
    public getTransitionAtTime(srcTimeSec: number): TransitionInfo | null {
        const scenes = this.getScenes();
        if (scenes.length < 2) return null;

        // Check each scene boundary for transition zone
        for (let sceneIndex = 1; sceneIndex < scenes.length; sceneIndex++) {
            const scene = scenes[sceneIndex];
            const boundaryTimeSec = scene.startSec; // Scene boundary in source time

            // Get effective transitions at this boundary
            const { outTransition, inTransition } = this.getBoundaryTransitions(sceneIndex);

            // Use the maximum video duration from either transition
            const outDurationMs = outTransition.video?.durationMs ?? 0;
            const inDurationMs = inTransition.video?.durationMs ?? 0;
            const videoDurationMs = Math.max(outDurationMs, inDurationMs);
            const videoDurationSec = videoDurationMs / 1000;

            if (videoDurationSec <= 0) continue; // No transition, skip

            // Transition zone is centered on the boundary (half before, half after)
            // This allows both out-transition and in-transition to apply
            const halfDuration = videoDurationSec / 2;
            const transitionStartSec = boundaryTimeSec - halfDuration;
            const transitionEndSec = boundaryTimeSec + halfDuration;

            // Check if current time is within the transition zone
            if (srcTimeSec >= transitionStartSec && srcTimeSec < transitionEndSec) {
                // Calculate progress (0 at start of transition, 1 at end)
                const progress = (srcTimeSec - transitionStartSec) / videoDurationSec;

                const fromSceneIndex = sceneIndex - 1;
                const toSceneIndex = sceneIndex;

                // Determine effective transition type (prefer out-transition type)
                const transitionType = outTransition.video?.type ?? inTransition.video?.type ?? 'cut';
                const easing = outTransition.video?.easing ?? inTransition.video?.easing;
                const addFades = Boolean(outTransition.video?.addFades ?? inTransition.video?.addFades);

                return {
                    fromSceneIndex,
                    toSceneIndex,
                    progress: Math.max(0, Math.min(1, progress)),
                    transitionType: transitionType as any,
                    durationSec: videoDurationSec,
                    easing,
                    addFades,
                };
            }
        }

        return null;
    }

    /**
     * Interpolate visual properties between two scenes.
     */
    private interpolateVisualProperties(
        fromProps: SceneVisualProperties | undefined,
        toProps: SceneVisualProperties | undefined,
        easedProgress: number,
        baseVisual: { x: number; y: number; width: number; height: number; alpha: number }
    ): { x: number; y: number; width: number; height: number; alpha: number } {
        // Get effective values (scene override or base visual)
        const fromX = fromProps?.x ?? baseVisual.x;
        const fromY = fromProps?.y ?? baseVisual.y;
        const fromWidth = fromProps?.width ?? baseVisual.width;
        const fromHeight = fromProps?.height ?? baseVisual.height;
        const fromAlpha = fromProps?.alpha ?? baseVisual.alpha;

        const toX = toProps?.x ?? baseVisual.x;
        const toY = toProps?.y ?? baseVisual.y;
        const toWidth = toProps?.width ?? baseVisual.width;
        const toHeight = toProps?.height ?? baseVisual.height;
        const toAlpha = toProps?.alpha ?? baseVisual.alpha;

        return {
            x: lerp(fromX, toX, easedProgress),
            y: lerp(fromY, toY, easedProgress),
            width: lerp(fromWidth, toWidth, easedProgress),
            height: lerp(fromHeight, toHeight, easedProgress),
            alpha: lerp(fromAlpha, toAlpha, easedProgress),
        };
    }

    /**
     * Cubic ease-in-out for smooth transitions.
     */
    private easeInOutCubic(t: number): number {
        return t < 0.5
            ? 4 * t * t * t
            : 1 - Math.pow(-2 * t + 2, 3) / 2;
    }

    private easeOutBack(t: number): number {
        const c1 = 1.70158;
        const c3 = c1 + 1;
        return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
    }

    private easeOutElastic(t: number): number {
        if (t === 0 || t === 1) return t;
        const c4 = (2 * Math.PI) / 3;
        return Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * c4) + 1;
    }

    private easeOutBounce(t: number): number {
        const n1 = 7.5625;
        const d1 = 2.75;
        if (t < 1 / d1) return n1 * t * t;
        if (t < 2 / d1) {
            const tt = t - 1.5 / d1;
            return n1 * tt * tt + 0.75;
        }
        if (t < 2.5 / d1) {
            const tt = t - 2.25 / d1;
            return n1 * tt * tt + 0.9375;
        }
        const tt = t - 2.625 / d1;
        return n1 * tt * tt + 0.984375;
    }

    private easeOutExpo(t: number): number {
        return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
    }

    private applyEasing(t: number, easing?: string): number {
        const key = String(easing ?? "natural").trim().toLowerCase();
        const clamped = Math.max(0, Math.min(1, t));
        switch (key) {
            case "none":
                return clamped;
            case "speed_up":
                return clamped * clamped;
            case "slow_down":
                return 1 - Math.pow(1 - clamped, 2);
            case "swing":
            case "natural":
                return this.easeInOutCubic(clamped);
            case "overshoot":
                return this.easeOutBack(clamped);
            case "elastic_out":
                return this.easeOutElastic(clamped);
            case "bounce_out":
                return this.easeOutBounce(clamped);
            case "impulse":
                return this.easeOutExpo(clamped);
            default:
                return this.easeInOutCubic(clamped);
        }
    }

    private resolveSceneVisual(
        overrides: SceneVisualProperties | undefined,
        baseVisual: { x: number; y: number; width: number; height: number; alpha: number }
    ): { x: number; y: number; width: number; height: number; alpha: number } {
        return {
            x: overrides?.x ?? baseVisual.x,
            y: overrides?.y ?? baseVisual.y,
            width: overrides?.width ?? baseVisual.width,
            height: overrides?.height ?? baseVisual.height,
            alpha: overrides?.alpha ?? baseVisual.alpha,
        };
    }

	    private getOrCreateTransitionFromContainer(input: Input): Container | null {
	        const existing = this._transitionFromContainers.get(input);
	        if (existing) return existing;
	
	        const baseTexture = this._videoTextures.get(input);
	        if (!baseTexture) return null;
	
	        const container = new Container();
	        const w = baseTexture.source.width;
	        const h = baseTexture.source.height;
	        const texture = new Texture({
	            source: baseTexture as any,
	            frame: new Rectangle(0, 0, w, h),
	            orig: new Rectangle(0, 0, w, h),
	            dynamic: false,
	        });
	        const sprite = new Sprite(texture);
	        sprite.anchor.set(0.5, 0.5);
	        sprite.position.set(0, 0);
	        container.addChild(sprite);
	        container.visible = false;
	        container.alpha = 0;

        // Add to stage if already connected, otherwise keep in view as fallback.
        const stage = this.track?.composition?.stage;
        if (stage) stage.addChild(container);
	        else this.view.addChild(container);
	
	        this._transitionFromContainers.set(input, container);
	        this._transitionFromSprites.set(input, sprite);
	        return container;
	    }

    /**
     * Get the scene at a specific index.
     */
    public getScene(index: number): ScriptLayerScene | undefined {
        const scenes = this.getScenes();
        return scenes.find(s => s.index === index);
    }

    /**
     * Set properties for a specific scene.
     * Properties override the clip-level defaults for that scene.
     */
    public setSceneProperties(sceneIndex: number, properties: SceneProperties): void {
        const existing = this.sceneProperties.get(sceneIndex) ?? {};
        this.sceneProperties.set(sceneIndex, { ...existing, ...properties });
        // Invalidate scene cache to pick up new properties
        this._scenesCache = null;
    }

    /**
     * Get properties for a specific scene.
     * Returns the merged properties (scene-specific overrides + clip defaults).
     */
    public getSceneProperties(sceneIndex: number): SceneProperties {
        return this.sceneProperties.get(sceneIndex) ?? {};
    }

    /**
     * Get the effective gain for a specific scene.
     * Returns scene-specific gain if set, otherwise clip-level gain.
     */
    public getEffectiveGainForScene(sceneIndex: number): number {
        const sceneProps = this.sceneProperties.get(sceneIndex);
        return sceneProps?.gain ?? this.gain;
    }

    /**
     * Get the effective playback rate for a specific scene.
     * Returns scene-specific rate if set, otherwise 1.0 (normal speed).
     */
    public getEffectivePlaybackRateForScene(sceneIndex: number): number {
        const sceneProps = this.sceneProperties.get(sceneIndex);
        return sceneProps?.playbackRate ?? 1.0;
    }

    /**
     * Get the scene-specific gain multiplier for a specific audio source.
     * Returns undefined if no override is set (meaning "use default", multiplier=1).
     */
    public getSceneAudioSourceGainMultiplier(sceneIndex: number, sourceIndex: number): number | undefined {
        const sceneProps = this.sceneProperties.get(sceneIndex);
        const m = (sceneProps as any)?.audioSourceGains;
        if (m && typeof m.get === "function") {
            const v = m.get(sourceIndex);
            const n = Number(v);
            return Number.isFinite(n) ? n : undefined;
        }
        return undefined;
    }

    /**
     * Get the effective gain multiplier for a source in a scene (defaults to 1 when unset).
     */
    public getEffectiveAudioSourceGainMultiplier(sceneIndex: number, sourceIndex: number): number {
        return this.getSceneAudioSourceGainMultiplier(sceneIndex, sourceIndex) ?? 1.0;
    }

    /**
     * Set (or clear) a scene-specific gain multiplier for an audio source.
     * - multiplier undefined/null: clears the override (back to default).
     * - multiplier number: sets the override (0 = mute).
     */
    public setSceneAudioSourceGainMultiplier(
        sceneIndex: number,
        sourceIndex: number,
        multiplier?: number | null,
    ): void {
        const sceneProps = this.sceneProperties.get(sceneIndex) ?? {};
        const existingMap: Map<number, number> | undefined = (sceneProps as any)?.audioSourceGains;
        const map = existingMap && typeof (existingMap as any).entries === "function" ? new Map(existingMap) : new Map<number, number>();

        if (multiplier == null) {
            map.delete(sourceIndex);
        } else {
            const n = Number(multiplier);
            if (Number.isFinite(n)) map.set(sourceIndex, n);
        }

        if (map.size === 0) {
            const next: any = { ...sceneProps };
            delete next.audioSourceGains;
            this.sceneProperties.set(sceneIndex, next);
        } else {
            this.sceneProperties.set(sceneIndex, { ...sceneProps, audioSourceGains: map } as any);
        }

        this.trigger('frame', undefined);
    }

    /**
     * Get scene-specific visual properties for a video source.
     * Returns scene overrides merged with source defaults.
     */
    public getSceneVisualProperties(sceneIndex: number, sourceIndex: number): {
        alpha: number; x: number; y: number; width: number; height: number; crop?: { x: number; y: number; width: number; height: number };
    } | null {
        const videoEntry = this.videoEntries[sourceIndex];
        if (!videoEntry) return null;

        // Get source-level defaults
        const defaults = {
            alpha: videoEntry.visual?.alpha ?? 1,
            x: videoEntry.visual?.x ?? 0,
            y: videoEntry.visual?.y ?? 0,
            width: videoEntry.visual?.width ?? 0,
            height: videoEntry.visual?.height ?? 0,
            crop: videoEntry.visual?.crop,
        };

        // Get scene overrides if any (aspect-aware)
        const sceneProps = this.sceneProperties.get(sceneIndex);
        const preset = this.getCanvasAspectPreset();
        const byAspect = (sceneProps as any)?.visualOverridesByAspect as Record<string, Map<number, any>> | undefined;
        const map =
            (byAspect && (byAspect as any)[preset] && typeof (byAspect as any)[preset].get === "function"
                ? (byAspect as any)[preset]
                : undefined) ??
            // Legacy fallback (treat as horizontal only)
            (preset === "horizontal" ? (sceneProps as any)?.visualOverrides : undefined);
        const visualOverrides = map?.get?.(sourceIndex);

        if (!visualOverrides) return defaults;

        // Merge overrides with defaults
        return {
            alpha: visualOverrides.alpha ?? defaults.alpha,
            x: visualOverrides.x ?? defaults.x,
            y: visualOverrides.y ?? defaults.y,
            width: visualOverrides.width ?? defaults.width,
            height: visualOverrides.height ?? defaults.height,
            crop: (visualOverrides as any).crop ?? defaults.crop,
        };
    }

    /**
     * Set scene-specific visual properties for a video source.
     */
    public setSceneVisualProperties(
        sceneIndex: number,
        sourceIndex: number,
        props: {
            alpha?: number;
            hiddenByUser?: boolean;
            x?: number;
            y?: number;
            width?: number;
            height?: number;
            crop?: { x: number; y: number; width: number; height: number };
            maskShape?: MaskShape;
        }
    ): void {
        const existing = this.sceneProperties.get(sceneIndex) ?? {};
        const preset = this.getCanvasAspectPreset();
        const byAspect: Record<string, Map<number, any>> = {
            ...(((existing as any).visualOverridesByAspect as Record<string, Map<number, any>> | undefined) ?? {}),
        };
        const legacy = (existing as any).visualOverrides;
        const visualOverrides: Map<number, any> =
            (byAspect as any)[preset] ??
            (preset === "horizontal" && legacy && typeof legacy.get === "function" ? legacy : new Map());

        const currentOverrides = visualOverrides.get(sourceIndex) ?? {};
        const updatedOverrides = { ...currentOverrides, ...props };

        // Remove undefined values
        Object.keys(updatedOverrides).forEach(key => {
            if ((updatedOverrides as any)[key] === undefined) {
                delete (updatedOverrides as any)[key];
            }
        });

        visualOverrides.set(sourceIndex, updatedOverrides);

        this.sceneProperties.set(sceneIndex, {
            ...existing,
            visualOverrides: preset === "horizontal" ? visualOverrides : (existing as any).visualOverrides,
            visualOverridesByAspect: { ...byAspect, [preset]: visualOverrides },
        });

        // Invalidate cache to pick up changes
        this._scenesCache = null;

        // Trigger frame update so canvas re-renders immediately
        this.trigger('frame', undefined);
    }

    /**
     * Clear ALL visual overrides for a scene in the specified canvas aspect preset.
     * Used when switching a scene back to "None"/default layout for an aspect.
     */
    public clearSceneVisualOverrides(
        sceneIndex: number,
        aspectPreset?: "horizontal" | "vertical" | "square"
    ): void {
        const existing = this.sceneProperties.get(sceneIndex) ?? {};
        const preset = aspectPreset ?? this.getCanvasAspectPreset();

        const next: any = { ...existing };
        const byAspect = (existing as any)?.visualOverridesByAspect as Record<string, Map<number, any>> | undefined;
        if (byAspect && typeof byAspect === "object") {
            const cloned: any = { ...(byAspect as any) };
            delete cloned[preset];
            if (Object.keys(cloned).length > 0) next.visualOverridesByAspect = cloned;
            else delete next.visualOverridesByAspect;
        }

        if (preset === "horizontal") {
            delete next.visualOverrides;
        }

        this.sceneProperties.set(sceneIndex, next);
        this._scenesCache = null;
        this.trigger("frame", undefined);
    }

    /**
     * Clear specific visual override keys for a video source across all scenes.
     * Used when switching to an "All scenes" edit scope so base source visuals take effect everywhere.
     */
    public clearSceneVisualOverrideKeysForSource(
        sourceIndex: number,
        keys: Array<'x' | 'y' | 'width' | 'height' | 'alpha' | 'hiddenByUser' | 'rotation' | 'maskShape' | 'crop'>
    ): void {
        if (!Array.isArray(keys) || keys.length === 0) return;
        const idx = Math.floor(Number(sourceIndex));
        if (!Number.isFinite(idx) || idx < 0) return;

        const preset = this.getCanvasAspectPreset();

        for (const [sceneIndex, existing] of this.sceneProperties.entries()) {
            const byAspect = (existing as any)?.visualOverridesByAspect as Record<string, Map<number, any>> | undefined;
            const legacy = (existing as any)?.visualOverrides as Map<number, any> | undefined;
            const visualOverrides: Map<number, any> | undefined =
                (byAspect && (byAspect as any)[preset] && typeof (byAspect as any)[preset].get === "function"
                    ? (byAspect as any)[preset]
                    : undefined) ?? (preset === "horizontal" ? legacy : undefined);
            if (!visualOverrides || typeof visualOverrides.get !== "function") continue;

            const current = visualOverrides.get(idx);
            if (!current || typeof current !== 'object') continue;

            const updated: any = { ...current };
            for (const k of keys) delete updated[k];

            if (Object.keys(updated).length === 0) {
                visualOverrides.delete(idx);
            } else {
                visualOverrides.set(idx, updated);
            }

            this.sceneProperties.set(sceneIndex, {
                ...existing,
                visualOverrides: preset === "horizontal" ? visualOverrides : (existing as any).visualOverrides,
                visualOverridesByAspect: byAspect ? { ...byAspect, [preset]: visualOverrides } : { [preset]: visualOverrides } as any,
            });
        }

        this._scenesCache = null;
        this.trigger('frame', undefined);
    }

    /**
     * Update scene visual properties during drag (lightweight - no cache invalidation).
     * This method updates config values and PixiJS container directly for smooth drag performance.
     * Call commitSceneVisualProperties() when drag ends to trigger the full update cycle.
     */
    public updateSceneVisualPropertiesDrag(
        sceneIndex: number,
        sourceIndex: number,
        props: { alpha?: number; x?: number; y?: number; width?: number; height?: number; maskShape?: MaskShape }
    ): void {
        // Update config values (sidebar reads these)
        const existing = this.sceneProperties.get(sceneIndex) ?? {};
        const preset = this.getCanvasAspectPreset();
        const byAspect: Record<string, Map<number, any>> = {
            ...(((existing as any).visualOverridesByAspect as Record<string, Map<number, any>> | undefined) ?? {}),
        };
        const legacy = (existing as any).visualOverrides;
        const visualOverrides: Map<number, any> =
            (byAspect as any)[preset] ??
            (preset === "horizontal" && legacy && typeof legacy.get === "function" ? legacy : new Map());

        const currentOverrides = visualOverrides.get(sourceIndex) ?? {};
        const updatedOverrides = { ...currentOverrides, ...props };

        // Remove undefined values
        Object.keys(updatedOverrides).forEach(key => {
            if ((updatedOverrides as any)[key] === undefined) {
                delete (updatedOverrides as any)[key];
            }
        });

        visualOverrides.set(sourceIndex, updatedOverrides);
        this.sceneProperties.set(sceneIndex, {
            ...existing,
            visualOverrides: preset === "horizontal" ? visualOverrides : (existing as any).visualOverrides,
            visualOverridesByAspect: { ...byAspect, [preset]: visualOverrides },
        });

        // Update PixiJS container directly (visual on canvas)
        const videoEntry = this.videoEntries[sourceIndex];
        if (videoEntry) {
            const container = this._videoContainers.get(videoEntry.input);
            if (container) {
                if (props.x !== undefined) container.position.x = props.x;
                if (props.y !== undefined) container.position.y = props.y;
                if (props.width !== undefined) container.width = props.width;
                if (props.height !== undefined) container.height = props.height;
                if (props.alpha !== undefined) container.alpha = props.alpha;
            }
        }

        // NO cache invalidation, NO trigger('frame'), NO bumpCutsVersion
        // This is intentionally lightweight for drag performance
    }

    /**
     * Commit scene visual properties after drag ends.
     * This triggers the full update cycle including cache invalidation.
     */
    public commitSceneVisualProperties(): void {
        this._scenesCache = null;
        this.trigger('frame', undefined);
    }

    private getCanvasAspectPreset(): "horizontal" | "vertical" | "square" {
        const w = Number(this.track?.composition?.width ?? 1920);
        const h = Number(this.track?.composition?.height ?? 1080);
        if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return "horizontal";
        const ratio = w / h;
        if (Math.abs(ratio - 1) < 0.06) return "square";
        return ratio < 1 ? "vertical" : "horizontal";
    }

    /**
     * Get the layout preset for a specific scene.
     * Returns the per-aspect layout if set, otherwise null (meaning "no layout"/default placement).
     */
    public getSceneLayout(sceneIndex: number, aspectPreset?: "horizontal" | "vertical" | "square"): string | null {
        const sceneProps = this.sceneProperties.get(sceneIndex);
        if (!sceneProps) return null;

        const preset = aspectPreset ?? this.getCanvasAspectPreset();
        const byAspect = (sceneProps as any)?.layoutByAspect as Record<string, any> | undefined;
        if (byAspect && Object.prototype.hasOwnProperty.call(byAspect, preset)) {
            const v = (byAspect as any)[preset];
            if (v === null) return null;
            if (typeof v === "string") return v;
            return v != null ? String(v) : null;
        }

        // Legacy: treat `layout` as the horizontal preset.
        if (preset === "horizontal" && typeof (sceneProps as any)?.layout === "string") {
            return String((sceneProps as any).layout);
        }

        return null;
    }

    /**
     * Set the layout preset for a specific scene.
     */
    public setSceneLayout(
        sceneIndex: number,
        layout: string | null,
        aspectPreset?: "horizontal" | "vertical" | "square"
    ): void {
        const existing = this.sceneProperties.get(sceneIndex) ?? {};
        const preset = aspectPreset ?? this.getCanvasAspectPreset();
        const nextByAspect: Record<string, string | null> = {
            ...(((existing as any).layoutByAspect as Record<string, string | null> | undefined) ?? {}),
        };

        // Store explicit null for "no layout" to distinguish from "unset".
        if (layout === null) nextByAspect[preset] = null;
        else nextByAspect[preset] = String(layout);

        this.sceneProperties.set(sceneIndex, {
            ...existing,
            layoutByAspect: nextByAspect,
            // Keep legacy `layout` in sync for the horizontal preset only.
            ...(preset === "horizontal"
                ? layout === null
                    ? { layout: undefined }
                    : { layout: String(layout) }
                : {}),
        });
        this._scenesCache = null;
    }

    /**
     * Get the in-transition config for a specific scene.
     * In-transition defines how this scene starts (fades/wipes in).
     */
    public getSceneInTransition(sceneIndex: number): TransitionConfig | undefined {
        return this.sceneProperties.get(sceneIndex)?.inTransition;
    }

    /**
     * Set the in-transition config for a specific scene.
     * In-transition defines how this scene starts (fades/wipes in).
     */
    public setSceneInTransition(sceneIndex: number, transition: TransitionConfig | undefined): void {
        const existing = this.sceneProperties.get(sceneIndex) ?? {};
        this.sceneProperties.set(sceneIndex, {
            ...existing,
            inTransition: transition,
        });
        this._scenesCache = null;
        this.markSegmentsDirty();
    }

    /**
     * Get the out-transition config for a specific scene.
     * Out-transition defines how this scene ends (fades/wipes out).
     */
    public getSceneOutTransition(sceneIndex: number): TransitionConfig | undefined {
        return this.sceneProperties.get(sceneIndex)?.outTransition;
    }

    /**
     * Set the out-transition config for a specific scene.
     * Out-transition defines how this scene ends (fades/wipes out).
     */
    public setSceneOutTransition(sceneIndex: number, transition: TransitionConfig | undefined): void {
        const existing = this.sceneProperties.get(sceneIndex) ?? {};
        this.sceneProperties.set(sceneIndex, {
            ...existing,
            outTransition: transition,
        });
        this._scenesCache = null;
        this.markSegmentsDirty();
    }

    /**
     * Get the effective transition at a scene boundary.
     * Returns both in-transition (next scene) and out-transition (previous scene).
     * For crossfade, uses the longer duration from either side.
     */
    public getBoundaryTransitions(boundarySceneIndex: number): {
        outTransition: TransitionConfig;
        inTransition: TransitionConfig;
    } {
        // boundarySceneIndex is the scene AFTER the boundary (1, 2, 3...)
        // Scene 0 has no boundary before it
        const prevSceneIndex = boundarySceneIndex - 1;

        const outTransition = this.getSceneOutTransition(prevSceneIndex) ?? DEFAULT_SCENE_TRANSITION;
        const inTransition = this.getSceneInTransition(boundarySceneIndex) ?? DEFAULT_SCENE_TRANSITION;

        // For crossfade, use the longer duration (they should match, but in case they don't)
        const isCrossfade = outTransition.audio?.type === 'crossfade' || inTransition.audio?.type === 'crossfade';
        if (isCrossfade) {
            const maxAudioDuration = Math.max(
                outTransition.audio?.durationMs ?? 0,
                inTransition.audio?.durationMs ?? 0
            );
            const maxVideoDuration = Math.max(
                outTransition.video?.durationMs ?? 0,
                inTransition.video?.durationMs ?? 0
            );
            return {
                outTransition: {
                    audio: { type: 'crossfade', durationMs: maxAudioDuration },
                    video: { ...outTransition.video, durationMs: maxVideoDuration } as any,
                },
                inTransition: {
                    audio: { type: 'crossfade', durationMs: maxAudioDuration },
                    video: { ...inTransition.video, durationMs: maxVideoDuration } as any,
                },
            };
        }

        return { outTransition, inTransition };
    }

    /**
     * Add a scene boundary split at the given time.
     * Convenience method that creates a split with isSceneBoundary: true.
     */
    public addSceneBoundary(timeSec: number, label?: string): ScriptLayerSplit {
        this.invalidateScenesCache();
        return this.addScriptSplit(timeSec, { isSceneBoundary: true, label });
    }

    /**
     * Remove a scene boundary (converts to regular split or removes entirely).
     */
    public removeSceneBoundary(splitId: string, keepAsSplit: boolean = false): boolean {
        const split = this.splits.find(s => s.id === splitId);
        if (!split || !split.isSceneBoundary) return false;

        this.invalidateScenesCache();

        if (keepAsSplit) {
            // Convert to regular split (remove scene boundary flag)
            split.isSceneBoundary = false;
            split.label = undefined;
            return true;
        } else {
            // Remove entirely
            return this.removeScriptSplit(splitId);
        }
    }

    /**
     * Merge a scene with the previous scene (removes the scene boundary).
     */
    public mergeSceneWithPrevious(sceneIndex: number): boolean {
        if (sceneIndex <= 0) return false; // Can't merge first scene

        const scenes = this.getScenes();
        const scene = scenes.find(s => s.index === sceneIndex);
        if (!scene || !scene.startSplitId) return false;

        // Preserve scene-level properties deterministically:
        // - Keep the previous scene's properties as the base (so the earlier part doesn't change).
        // - Drop transitions at the removed boundary:
        //   - previous.outTransition was for this boundary → remove it
        //   - current.inTransition was for this boundary → will be dropped when current is removed
        // - Preserve current.outTransition (end of merged scene) since that boundary still exists.
        const prevIndex = sceneIndex - 1;
        const prevProps = this.sceneProperties.get(prevIndex);
        const currProps = this.sceneProperties.get(sceneIndex);
        let mergedPrevProps: SceneProperties | undefined;
        if (prevProps || currProps) {
            mergedPrevProps = { ...(prevProps ?? {}) };
            // Clear any outTransition that belonged to the removed boundary.
            if ("outTransition" in mergedPrevProps) {
                delete (mergedPrevProps as any).outTransition;
            }
            // Carry over the current scene's outTransition (boundary after the merged scene).
            if (currProps?.outTransition !== undefined) {
                (mergedPrevProps as any).outTransition = currProps.outTransition;
            }
        }

        // Remove the scene boundary split
        const removed = this.removeScriptSplit(scene.startSplitId);
        if (removed) {
            // Renumber properties for scenes after this one and drop the removed scene's props.
            const oldProps = new Map<number, SceneProperties>(this.sceneProperties);
            const newProps = new Map<number, SceneProperties>();
            for (const [idx, props] of oldProps) {
                if (idx === sceneIndex) continue; // removed scene
                if (idx > sceneIndex) {
                    newProps.set(idx - 1, props);
                } else {
                    newProps.set(idx, props);
                }
            }
            // Apply merged properties for the resulting merged scene (prevIndex).
            if (mergedPrevProps) {
                newProps.set(prevIndex, mergedPrevProps);
            } else {
                newProps.delete(prevIndex);
            }
            this.sceneProperties = newProps;
            this.invalidateScenesCache();
        }
        return removed;
    }

    // ============ Initialization ============

    /**
     * Initialize by probing each Input for audio and video tracks.
     * This method is idempotent - safe to call multiple times.
     */
    public async init(): Promise<void> {
        // If initialization is already running, await it.
        if (this._initPromise) {
            await this._initPromise;
            return;
        }

        // If already initialized successfully, no-op.
        if (this.state === 'READY' && (this._audioSinks.size > 0 || this._videoSinks.size > 0)) {
            return;
        }

        this._initPromise = this._initInternal();
        try {
            await this._initPromise;
        } finally {
            this._initPromise = null;
        }
    }

    private async _initInternal(): Promise<void> {
        // Clear existing entries to make init idempotent
        // (track.add() also calls init, so this may be called multiple times)
        this.audioEntries = [];
        this.videoEntries = [];
        this._audioSinks.clear();
        this._audioGains.clear();
        this._audioMixerTracks.clear();
        this._crossfadeOverlayTracks.clear();
        this._audioSampleRates.clear();
        this._audioChannelCounts.clear();
        this._videoSinks.clear();
        this._videoVisuals.clear();
	        this._videoCanvases.clear();
	        this._videoContexts.clear();
	        this._videoTextures.clear();
	        this._videoSprites.clear();
	        this._videoContainers.clear();
	        this._transitionFromContainers.clear();
	        this._transitionFromSprites.clear();
	        this.hasAudioStream = false;

        // Empty ScriptClip (no sources) is a valid state: it's pure "spine time".
        // We keep it READY so it can define composition bounds and render as silence.
        if ((this.inputConfigs?.length ?? 0) === 0) {
            try {
                const inSec = this.range?.[0]?.seconds ?? 0;
                const outSec = this.range?.[1]?.seconds ?? this.duration?.seconds ?? inSec;
                if (Number.isFinite(outSec) && outSec > 0) {
                    this.duration.seconds = Math.max(Number(this.duration?.seconds ?? 0) || 0, outSec);
                }
            } catch {}
            this.state = 'READY';
            return;
        }

        let maxDuration = 0;

        // Default visual config (will be updated based on composition size later)
        const defaultVisual = { x: 960, y: 540, width: 1920, height: 1080, alpha: 1.0, rotation: 0 };

	        if (DEBUG_SCRIPT_CLIP_LOGS) {
	            console.log("[ScriptLayerClip.init] Initializing with", this.inputConfigs.length, "input configs");
	        }

	        // Probe each input for audio and video tracks
	        for (const config of this.inputConfigs) {
	            const { input, assetId, name, color, gain = 1.0, visual, offsetSec, playbackRate } = config;
	            // Some callsites omit per-source `range` (meaning "full source").
	            // We resolve it during init (once we can probe duration) so later operations
	            // like `insertEmptyScene()` can safely create per-source segments.
	            let range = config.range;
	            const segments: ScriptLayerSourceSegment[] | undefined =
	                Array.isArray((config as any).segments) && (config as any).segments.length > 0
	                    ? ((config as any).segments as ScriptLayerSourceSegment[])
	                    : undefined;

            // Try to get audio track
            try {
                const audioTrack = await input.getPrimaryAudioTrack();
                if (audioTrack && (await audioTrack.canDecode())) {
                    // If the source has no explicit range, resolve it once from the probed duration.
                    if (!range && (!segments || segments.length === 0)) {
                        try {
                            const dur = await audioTrack.computeDuration();
                            if (Number.isFinite(dur) && dur > 0) {
                                range = { inSec: 0, outSec: dur };
                                config.range = range;
                            }
                        } catch {}
                    }

                    const sink = new AudioBufferSink(audioTrack);
                    this._audioSinks.set(input, sink);
                    this._audioGains.set(input, gain);
                    this.hasAudioStream = true;

                    // Store sample rate and channel count per source for mixer track registration
                    this._audioSampleRates.set(input, audioTrack.sampleRate);
                    this._audioChannelCounts.set(input, audioTrack.numberOfChannels);

	                    this.audioEntries.push({ input, assetId, gain, name, color, range, offsetSec, playbackRate, segments });

                    // Use the first audio source's sample rate and channels (for fallback)
                    if (!this._sample_rate) {
                        this._sample_rate = audioTrack.sampleRate;
                        this._num_channels = audioTrack.numberOfChannels;
                    }

	                    // Track duration - prefer explicit segments, fall back to single-range config.
	                    try {
	                        if (segments && segments.length > 0) {
	                            for (const seg of segments) {
	                                const segRate = Number(seg.playbackRate ?? playbackRate ?? 1.0) || 1.0;
	                                const segIn = Number(seg.range?.inSec ?? range?.inSec ?? 0);
	                                const segOut = Number(seg.range?.outSec ?? range?.outSec ?? 0);
	                                if (!Number.isFinite(segIn) || !Number.isFinite(segOut) || segOut <= segIn) continue;
	                                const trimmedDur = (segOut - segIn) / segRate;
	                                const segOffset = Number(seg.offsetSec ?? 0);
	                                if (!Number.isFinite(segOffset)) continue;
	                                maxDuration = Math.max(maxDuration, segOffset + trimmedDur);
	                            }
	                        } else if (range) {
	                            // Use the trimmed duration
	                            const trimmedDur = (range.outSec - range.inSec) / (playbackRate || 1.0);
	                            maxDuration = Math.max(maxDuration, (offsetSec || 0) + trimmedDur);
	                        } else {
	                            const dur = await audioTrack.computeDuration();
	                            if (Number.isFinite(dur) && dur > 0) {
	                                const effectiveDur = dur / (playbackRate || 1.0);
	                                maxDuration = Math.max(maxDuration, (offsetSec || 0) + effectiveDur);
	                            }
	                        }
	                    } catch {}
                }
            } catch (e) {
                // No audio track or can't decode - that's fine
            }

	            // Try to get video track
	            try {
	                const videoTrack = await input.getPrimaryVideoTrack();
	                const canDecode = videoTrack ? await videoTrack.canDecode() : false;
	                if (DEBUG_SCRIPT_CLIP_LOGS) {
	                    console.log("[ScriptLayerClip.init] Checking video track:", !!videoTrack, "canDecode:", canDecode);
	                }
	                if (videoTrack && canDecode) {
                    // If the source has no explicit range, resolve it once from the probed duration.
                    if (!range && (!segments || segments.length === 0)) {
                        try {
                            const dur = await videoTrack.computeDuration();
                            if (Number.isFinite(dur) && dur > 0) {
                                range = { inSec: 0, outSec: dur };
                                config.range = range;
                            }
                        } catch {}
                    }

                    const canBeTransparent = await videoTrack.canBeTransparent();
                    const sink = new CanvasSink(videoTrack, {
                        poolSize: 2,
                        fit: 'contain',
                        alpha: canBeTransparent,
                    });
	                    this._videoSinks.set(input, sink);
	                    
	                    const visualConfig = visual || defaultVisual;
	                    if (DEBUG_SCRIPT_CLIP_LOGS) {
	                        console.log("[ScriptLayerClip.init] Created video sink with visual:", visualConfig);
	                    }
	                    this._videoVisuals.set(input, visualConfig);
                    // Use provided layerIndex or default to current array length (later sources on top)
                    const entryLayerIndex = config.layerIndex ?? this.videoEntries.length;
	                    this.videoEntries.push({ input, visual: visualConfig, range, offsetSec, playbackRate, segments, layerIndex: entryLayerIndex });

                    // Create dedicated container, canvas, texture and sprite for this video source
                    // Each video source gets its own Container that acts like an independent VideoClip
	                    const container = new Container();
	                    const canvas = document.createElement('canvas');
	                    canvas.width = visualConfig.width;
	                    canvas.height = visualConfig.height;
	                    const context = canvas.getContext('2d')!;
	                    const baseTexture = Texture.from(canvas);
	                    // Use a per-sprite texture with an explicit frame/orig so we can animate crop
	                    // without resizing the underlying canvas/texture (prevents flicker in transitions).
	                    const spriteTexture = new Texture({
	                        source: baseTexture as any,
	                        frame: new Rectangle(0, 0, canvas.width, canvas.height),
	                        orig: new Rectangle(0, 0, canvas.width, canvas.height),
	                        dynamic: false,
	                    });
	                    const sprite = new Sprite(spriteTexture);
                    
                    // Sprite is centered in its container (like VideoClip)
                    sprite.anchor.set(0.5, 0.5);
                    sprite.position.set(0, 0);
                    container.addChild(sprite);
                    
                    // Position container in composition coordinates (like VideoClip)
                    container.position.set(visualConfig.x, visualConfig.y);
                    // Scale container to achieve desired visual size
                    // displayedSize = canvas.size * container.scale
                    // So: container.scale = visual.size / canvas.size
                    // container.scale.set(visualConfig.width / canvas.width, visualConfig.height / canvas.height);
                    container.alpha = visualConfig.alpha;
                    container.angle = Number(visualConfig.rotation ?? 0);
                    
	                    this._videoContainers.set(input, container);
	                    this._videoCanvases.set(input, canvas);
	                    this._videoContexts.set(input, context);
	                    this._videoTextures.set(input, baseTexture);
	                    this._videoSprites.set(input, sprite);
                    
	                    // Container will be added to composition stage in connect()
	                    // For now, add to this.view as fallback (will be moved in connect)
	                    this.view.addChild(container);
	                    
	                    if (DEBUG_SCRIPT_CLIP_LOGS) {
	                        console.log("[ScriptLayerClip.init] Created container for video source, visual:", visualConfig);
	                    }

	                    // Track duration - prefer explicit segments, fall back to single-range config.
	                    try {
	                        if (segments && segments.length > 0) {
	                            for (const seg of segments) {
	                                const segRate = Number(seg.playbackRate ?? playbackRate ?? 1.0) || 1.0;
	                                const segIn = Number(seg.range?.inSec ?? range?.inSec ?? 0);
	                                const segOut = Number(seg.range?.outSec ?? range?.outSec ?? 0);
	                                if (!Number.isFinite(segIn) || !Number.isFinite(segOut) || segOut <= segIn) continue;
	                                const trimmedDur = (segOut - segIn) / segRate;
	                                const segOffset = Number(seg.offsetSec ?? 0);
	                                if (!Number.isFinite(segOffset)) continue;
	                                maxDuration = Math.max(maxDuration, segOffset + trimmedDur);
	                            }
	                        } else if (range) {
	                            // Use the trimmed duration
	                            const trimmedDur = (range.outSec - range.inSec) / (playbackRate || 1.0);
	                            maxDuration = Math.max(maxDuration, (offsetSec || 0) + trimmedDur);
	                        } else {
	                            const dur = await videoTrack.computeDuration();
	                            if (Number.isFinite(dur) && dur > 0) {
	                                const effectiveDur = dur / (playbackRate || 1.0);
	                                maxDuration = Math.max(maxDuration, (offsetSec || 0) + effectiveDur);
	                            }
	                        }
	                    } catch {}
	                }
	            } catch (e) {
	                if (DEBUG_SCRIPT_CLIP_LOGS) {
	                    console.log("[ScriptLayerClip.init] Error getting video track:", e);
	                }
	                // No video track or can't decode - that's fine
	            }
	        }

        // Set duration to the maximum of all sources
        if (maxDuration > 0) {
            this.duration.seconds = maxDuration;
        }

        if (this._audioSinks.size > 0 || this._videoSinks.size > 0) {
            this.state = 'READY';
        } else {
            this.state = 'ERROR';
        }
    }

    public async prepareForPlayback(
        audioCtx: AudioContext,
        _masterGain: GainNode,
        mixer: AudioMixer
    ): Promise<void> {
        // Ensure derived segments + crossfade descriptors are up to date before the composition
        // compiles the mixer schedule (schedule compilation happens right after prepareForPlayback()).
        void this.scriptSegments;

        // Register one mixer track per audio source (like having multiple independent AudioClips)
        // This allows each source to have its own playback rate handled by the mixer
        if (this.hasAudioStream) {
            for (const entry of this.audioEntries) {
                const input = entry.input;
                
                // Skip if already registered
                if (this._audioMixerTracks.has(input)) continue;
                
                const sampleRate = this._audioSampleRates.get(input) || 48000;
                const channels = this._audioChannelCounts.get(input) || 2;
                
                const mixerTrack = mixer.registerTrack({
                    clipId: `${this.id}_src${this.audioEntries.indexOf(entry)}`,
                    trackId: this.track?.id ?? '',
                    channels,
                    sampleRate,
                });
                
                this._audioMixerTracks.set(input, mixerTrack);
                
                // Apply source gain
                mixer.setClipGain(mixerTrack, entry.gain * this.gain);
            }
            this._mixer = mixer;

            // Crossfade overlays: register one extra mixer track per source so we can pre-mix
            // true crossfades at cut points without affecting the main track scheduling.
            if (this._crossfadeDescriptors.length > 0) {
                for (let sourceIndex = 0; sourceIndex < this.audioEntries.length; sourceIndex++) {
                    const entry = this.audioEntries[sourceIndex];
                    const input = entry.input;

                    let overlayTrack = this._crossfadeOverlayTracks.get(input);
                    if (!overlayTrack) {
                        const sampleRate = this._audioSampleRates.get(input) || 48000;
                        const channels = this._audioChannelCounts.get(input) || 2;
                        overlayTrack = mixer.registerTrack({
                            clipId: `${this.id}_src${sourceIndex}_crossfade`,
                            trackId: this.track?.id ?? '',
                            channels,
                            sampleRate,
                        });
                        this._crossfadeOverlayTracks.set(input, overlayTrack);
                    }

                    // Match the main track's per-source gain so overlay + main are consistent.
                    mixer.setClipGain(overlayTrack, entry.gain * this.gain);
                }
            }
        }

        // Video sinks are already created in init() - nothing more to do here
    }

    /**
     * Pre-roll script video for a given composition time.
     * Uses the normal update path so only sources active at this time are decoded.
     */
    public async preRollVideoAt(compTimeSec: number): Promise<void> {
        if (this._videoSinks.size === 0) return;
        if (!Number.isFinite(compTimeSec)) return;
        await this.update(Timestamp.fromSeconds(compTimeSec));
    }

    /**
     * Calculate source time from clip-local time for a given entry.
     * Returns null if the source is not active at this clip time.
     */
	    private calculateSourceTime(
	        clipLocalTime: number,
	        entry: { range?: { inSec: number; outSec: number }; offsetSec?: number; playbackRate?: number }
	    ): number | null {
	        const segs: Array<{
	            offsetSec: number;
	            playbackRate: number;
	            inSec: number;
	            outSec: number;
	        }> = [];

	        const anyEntry: any = entry as any;
	        const entrySegments: ScriptLayerSourceSegment[] | undefined =
	            Array.isArray(anyEntry?.segments) && anyEntry.segments.length > 0 ? anyEntry.segments : undefined;

	        if (entrySegments) {
	            for (const seg of entrySegments) {
	                const offsetSec = Number(seg?.offsetSec ?? 0);
	                const playbackRate = Number(seg?.playbackRate ?? anyEntry?.playbackRate ?? 1.0) || 1.0;
	                const inSec = Number(seg?.range?.inSec ?? anyEntry?.range?.inSec ?? 0);
	                const outSecRaw = seg?.range?.outSec ?? anyEntry?.range?.outSec;
	                const outSec = outSecRaw == null ? Number.POSITIVE_INFINITY : Number(outSecRaw);
	                if (!Number.isFinite(offsetSec) || !Number.isFinite(playbackRate) || playbackRate <= 0) continue;
	                if (!Number.isFinite(inSec)) continue;
	                if (!(outSec === Number.POSITIVE_INFINITY || (Number.isFinite(outSec) && outSec > inSec))) continue;
	                segs.push({ offsetSec, playbackRate, inSec, outSec });
	            }
	        } else {
	            const offsetSec = Number(anyEntry?.offsetSec ?? 0);
	            const playbackRate = Number(anyEntry?.playbackRate ?? 1.0) || 1.0;
	            const inSec = Number(anyEntry?.range?.inSec ?? 0);
	            const outSecRaw = anyEntry?.range?.outSec;
	            const outSec = outSecRaw == null ? Number.POSITIVE_INFINITY : Number(outSecRaw);
	            if (
	                Number.isFinite(offsetSec) &&
	                Number.isFinite(playbackRate) &&
	                playbackRate > 0 &&
	                Number.isFinite(inSec) &&
	                (outSec === Number.POSITIVE_INFINITY || (Number.isFinite(outSec) && outSec > inSec))
	            ) {
	                segs.push({ offsetSec, playbackRate, inSec, outSec });
	            }
	        }

	        // Fast path: no valid segments.
	        if (segs.length === 0) return null;

	        // Find the first segment that covers this clip time.
	        for (const seg of segs) {
	            if (clipLocalTime < seg.offsetSec) continue;
	            const sourceTime = seg.inSec + (clipLocalTime - seg.offsetSec) * seg.playbackRate;
	            if (sourceTime >= seg.outSec) continue;
	            return sourceTime;
	        }

	        return null;
	    }

    /**
     * Check if a source is finished at the given clip time.
     */
	    private isSourceFinished(
	        clipLocalTime: number,
	        entry: { range?: { inSec: number; outSec: number }; offsetSec?: number; playbackRate?: number }
	    ): boolean {
	        const anyEntry: any = entry as any;
	        const entrySegments: ScriptLayerSourceSegment[] | undefined =
	            Array.isArray(anyEntry?.segments) && anyEntry.segments.length > 0 ? anyEntry.segments : undefined;

	        if (!entrySegments) {
	            const offsetSec = Number(anyEntry?.offsetSec ?? 0);
	            const playbackRate = Number(anyEntry?.playbackRate ?? 1.0) || 1.0;
	            const rangeIn = Number(anyEntry?.range?.inSec ?? 0);
	            const rangeOutRaw = anyEntry?.range?.outSec;
	            const rangeOut = rangeOutRaw == null ? Number.POSITIVE_INFINITY : Number(rangeOutRaw);
	            if (rangeOut === Number.POSITIVE_INFINITY) return false;
	            const sourceTime = rangeIn + (clipLocalTime - offsetSec) * playbackRate;
	            return sourceTime >= rangeOut;
	        }

	        // Finished only if we're past the end of the last segment.
	        let maxEndOnTimeline = -Infinity;
	        for (const seg of entrySegments) {
	            const offsetSec = Number(seg?.offsetSec ?? 0);
	            const playbackRate = Number(seg?.playbackRate ?? anyEntry?.playbackRate ?? 1.0) || 1.0;
	            const inSec = Number(seg?.range?.inSec ?? 0);
	            const outSec = Number(seg?.range?.outSec ?? 0);
	            if (!Number.isFinite(offsetSec) || !Number.isFinite(playbackRate) || playbackRate <= 0) continue;
	            if (!Number.isFinite(inSec) || !Number.isFinite(outSec) || outSec <= inSec) continue;
	            const dur = (outSec - inSec) / playbackRate;
	            maxEndOnTimeline = Math.max(maxEndOnTimeline, offsetSec + dur);
	        }
	        if (!Number.isFinite(maxEndOnTimeline)) return true;
	        return clipLocalTime >= maxEndOnTimeline;
	    }

    /**
     * Map absolute composition time to the effective clip-local time,
     * accounting for cuts. Returns null if the time falls in a cut region.
     * 
     * Segments store compStartSec as absolute timeline time, and srcStartSec/srcEndSec
     * as clip-local time. This method finds which segment contains the given comp time
     * and returns the corresponding clip-local time.
     * 
     * @param absCompTime - Absolute composition/timeline time
     * @returns The effective clip-local time, or null if in cut region
     */
    public mapCompTimeToClipLocal(absCompTime: number): number | null {
        // Use the getter to ensure segments are fresh (checks _segmentsDirty)
        const segments = this.scriptSegments;
        
        if (!segments.length) {
            // No segments = no cuts, use normal clip-local conversion
            return this.CompositionTimeToClipTime(absCompTime);
        }
        
        // Find which segment contains this composition time
        for (const seg of segments) {
            const segDur = seg.srcEndSec - seg.srcStartSec;
            const segCompEnd = seg.compStartSec + segDur;
            
            if (absCompTime >= seg.compStartSec && absCompTime < segCompEnd) {
                // Found the segment - compute offset into segment
                const offsetIntoSeg = absCompTime - seg.compStartSec;
                // Return the clip-local time (srcStartSec is in clip-local coords)
                return seg.srcStartSec + offsetIntoSeg;
            }
        }
        
        // Time is outside all segments (in a cut region or past the end)
        return null;
    }
    
    /**
     * Find which segment index contains the given composition time.
     * Returns -1 if not in any segment.
     */
    private findSegmentIndexForCompTime(absCompTime: number): number {
        const segments = this.scriptSegments;
        
        for (let i = 0; i < segments.length; i++) {
            const seg = segments[i];
            const segDur = seg.srcEndSec - seg.srcStartSec;
            const segCompEnd = seg.compStartSec + segDur;
            
            if (absCompTime >= seg.compStartSec && absCompTime < segCompEnd) {
                return i;
            }
        }
        
        return -1;
    }

    /**
     * Override deriveSegments to use our scriptSegments with transitions.
     * This keeps compatibility with code that accesses the parent's segments property.
     */
    public override deriveSegments(): void {
        // Derive our script segments (with transitions)
        this.deriveScriptSegments();
        
        // Also update parent's _segments for compatibility
        (this as any)._segments = this._scriptSegments.map(seg => ({
            srcStartSec: seg.srcStartSec,
            srcEndSec: seg.srcEndSec,
            compStartSec: seg.compStartSec,
        }));
    }

    /**
     * Check if a segment boundary needs a microfade (audio transition).
     */
    public needsMicrofadeAtStart(segmentIndex: number): boolean {
        const seg = this._scriptSegments[segmentIndex];
        if (!seg?.startTransition?.audio) return false;
        return seg.startTransition.audio.type === 'microfade' || 
               seg.startTransition.audio.type === 'fade' ||
               seg.startTransition.audio.type === 'crossfade';
    }

    public needsMicrofadeAtEnd(segmentIndex: number): boolean {
        const seg = this._scriptSegments[segmentIndex];
        if (!seg?.endTransition?.audio) return false;
        return seg.endTransition.audio.type === 'microfade' || 
               seg.endTransition.audio.type === 'fade' ||
               seg.endTransition.audio.type === 'crossfade';
    }

    /**
     * Get the audio fade duration in milliseconds for a segment boundary.
     */
    public getFadeDurationMsAtStart(segmentIndex: number): number {
        const seg = this._scriptSegments[segmentIndex];
        return seg?.startTransition?.audio?.durationMs ?? 0;
    }

    public getFadeDurationMsAtEnd(segmentIndex: number): number {
        const seg = this._scriptSegments[segmentIndex];
        return seg?.endTransition?.audio?.durationMs ?? 0;
    }

    // ============ Cut Analysis Methods ============
    
    /**
     * Map composition time to source time for a given audio source.
     * @param compTimeSec - Time on the composition/script layer timeline
     * @param sourceIndex - Which audio source to map to
     * @returns Source time, or null if the comp time doesn't fall within this source's range
     */
	    public mapCompTimeToSourceTime(compTimeSec: number, sourceIndex: number): number | null {
	        const entry = this.audioEntries[sourceIndex];
	        if (!entry) return null;

	        const segs: ScriptLayerSourceSegment[] | undefined =
	            Array.isArray((entry as any)?.segments) && (entry as any).segments.length > 0 ? (entry as any).segments : undefined;

	        if (segs && segs.length > 0) {
	            for (const seg of segs) {
	                const offsetSec = Number(seg?.offsetSec ?? 0);
	                const playbackRate = Number(seg?.playbackRate ?? (entry as any)?.playbackRate ?? 1.0) || 1.0;
	                const rangeInSec = Number(seg?.range?.inSec ?? 0);
	                const rangeOutSec = Number(seg?.range?.outSec ?? 0);
	                if (!Number.isFinite(offsetSec) || !Number.isFinite(playbackRate) || playbackRate <= 0) continue;
	                if (!Number.isFinite(rangeInSec) || !Number.isFinite(rangeOutSec) || rangeOutSec <= rangeInSec) continue;
	                const dur = (rangeOutSec - rangeInSec) / playbackRate;
	                const end = offsetSec + dur;
	                if (compTimeSec < offsetSec || compTimeSec > end) continue;
	                return rangeInSec + (compTimeSec - offsetSec) * playbackRate;
	            }
	            return null;
	        }

	        const offsetSec = entry.offsetSec ?? 0;
	        const rangeInSec = entry.range?.inSec ?? 0;
	        const rangeOutSec = entry.range?.outSec ?? Infinity;
	        const playbackRate = (entry as any)?.playbackRate ?? 1.0;
	        const sourceDuration = rangeOutSec === Infinity ? Infinity : (rangeOutSec - rangeInSec) / playbackRate;

	        // Check if comp time falls within this source's range on the timeline
	        const compStart = offsetSec;
	        const compEnd = offsetSec + sourceDuration;

	        if (compTimeSec < compStart || compTimeSec > compEnd) {
	            return null; // This comp time isn't covered by this source
	        }

	        // Map: compTime -> sourceTime
	        // compTime = (sourceTime - rangeInSec) / playbackRate + offsetSec
	        // sourceTime = (compTime - offsetSec) * playbackRate + rangeInSec
	        return (compTimeSec - offsetSec) * playbackRate + rangeInSec;
	    }
    
    /**
     * Get the AudioBufferSink for a specific audio source.
     */
    public getAudioSinkForSource(sourceIndex: number): AudioBufferSink | null {
        const entry = this.audioEntries[sourceIndex];
        if (!entry) return null;
        return this._audioSinks.get(entry.input) ?? null;
    }
    
    /**
     * Analyze a single cut point for optimal audio quality.
     * Finds zero crossing and computes RMS for harshness detection.
     * 
     * @param compTimeSec - Cut point in composition time
     * @param sourceIndex - Which audio source to analyze
     * @param gapToAdjacentWordSec - Gap to the adjacent word (for adaptive snap limit)
     * @returns Analysis result, or null if analysis failed
     */
    public async analyzeCutPoint(
        compTimeSec: number,
        sourceIndex: number,
        gapToAdjacentWordSec: number = Infinity
    ): Promise<CutPointAnalysis | null> {
        const srcTimeSec = this.mapCompTimeToSourceTime(compTimeSec, sourceIndex);
        if (srcTimeSec === null) {
            return null;
        }
        
        const sink = this.getAudioSinkForSource(sourceIndex);
        if (!sink) {
            return null;
        }
        
        // Constants for analysis
        const SEARCH_WINDOW_SEC = 0.02;  // 20ms window to search for zero crossing
        const MAX_SNAP_SEC = 0.005;       // 5ms max snap
        const HALF_WIN = SEARCH_WINDOW_SEC / 2;
        const RMS_WINDOW_SEC = 0.01;      // 10ms RMS window
        const sampleRate = this._sample_rate ?? 48000;
        
        // Adaptive snap: don't snap more than half the gap to adjacent word
        const maxSafeSnap = Math.min(MAX_SNAP_SEC, gapToAdjacentWordSec / 2);
        
        try {
            // Read audio around the cut point (in source time)
            const mono = await readMonoWindowFromSink(sink, srcTimeSec - HALF_WIN, SEARCH_WINDOW_SEC, sampleRate);
            
            // Check what we got - reject only if no buffer was returned
            if (!mono || mono.length === 0) {
                return {
                    originalSec: compTimeSec,
                    snappedSec: compTimeSec,
                    snapDeltaSec: 0,
                    hasZeroCrossing: false,
                    rms: -1,
                    amplitudeAtCut: -1,
                };
            }
            
            // Check if we got real audio data or just zeros (e.g., seeking past end of file)
            // If max amplitude is extremely low, we likely don't have real audio data
            const maxAmp = Math.max(...Array.from(mono).map(Math.abs));
            const hasRealAudio = maxAmp > 0.00001; // 1e-5 threshold for true silence detection
            
	            if (!hasRealAudio) {
	                // Audio buffer returned but contains only zeros - likely seeked past end of file
	                // or the source audio doesn't cover this time range
	                if (DEBUG_SCRIPT_CLIP_LOGS) {
	                    console.log(
	                        `[ScriptClip] analyzeCutPoint: no real audio at srcTime=${srcTimeSec.toFixed(2)}s (maxAmp=${maxAmp.toExponential(2)})`
	                    );
	                }
	                return {
	                    originalSec: compTimeSec,
	                    snappedSec: compTimeSec,
	                    snapDeltaSec: 0,
                    hasZeroCrossing: false,  // Unknown - couldn't verify
                    rms: -1,
                    amplitudeAtCut: -1,
                };
            }
            // Note: quiet but non-zero audio is still usable - quiet cut points help avoid clicks
            
            // Find nearest zero crossing
            const centerIdx = Math.floor(mono.length / 2);
            const zeroCrossIdx = findNearestZeroCross(mono, centerIdx);
            
            let snappedSrcTimeSec = srcTimeSec;
            let hasZeroCrossing = false;
            let snappedIdx = centerIdx;
            
            if (zeroCrossIdx !== null) {
                // Convert sample index to time offset
                const offsetSamples = zeroCrossIdx - centerIdx;
                const offsetSec = offsetSamples / sampleRate;
                
                // Clamp to maxSafeSnap
                const clampedOffset = Math.max(-maxSafeSnap, Math.min(maxSafeSnap, offsetSec));
                snappedSrcTimeSec = srcTimeSec + clampedOffset;
                hasZeroCrossing = Math.abs(offsetSec) <= maxSafeSnap; // True if we found one within snap range
                
                // Track where we snapped to for amplitude measurement
                if (hasZeroCrossing) {
                    snappedIdx = Math.floor(zeroCrossIdx);
                }
            }
            
            // Map snapped source time back to composition time
            const entry = this.audioEntries[sourceIndex];
            const offsetSec = entry?.offsetSec ?? 0;
            const rangeInSec = entry?.range?.inSec ?? 0;
            const snappedCompTimeSec = (snappedSrcTimeSec - rangeInSec) + offsetSec;
            
            // Compute RMS around the cut point
            const rmsStartIdx = Math.max(0, Math.floor((mono.length / 2) - (RMS_WINDOW_SEC * sampleRate / 2)));
            const rmsLen = Math.floor(RMS_WINDOW_SEC * sampleRate);
            const rmsValue = rms(mono, rmsStartIdx, rmsLen);
            
            // Get instantaneous amplitude at the snapped cut point
            const amplitudeAtCut = Math.abs(mono[Math.min(snappedIdx, mono.length - 1)] ?? 0);
            
            return {
                originalSec: compTimeSec,
                snappedSec: snappedCompTimeSec,
                snapDeltaSec: snappedCompTimeSec - compTimeSec,
                hasZeroCrossing,
                rms: rmsValue,
                amplitudeAtCut,
            };
        } catch (err) {
            console.error('[ScriptClip] analyzeCutPoint error:', err);
            return {
                originalSec: compTimeSec,
                snappedSec: compTimeSec,
                snapDeltaSec: 0,
                hasZeroCrossing: false,
                rms: 0,
                amplitudeAtCut: 0,
            };
        }
    }
    
    /**
     * Analyze a complete cut (start and end points).
     * Returns analysis with recommended fade durations and harshness assessment.
     * Uses a scoring system for nuanced harshness detection.
     * 
     * @param cutStartCompSec - Where to start cutting (composition time)
     * @param cutEndCompSec - Where to end cutting (composition time)
     * @param sourceIndex - Which audio source
     * @param prevWordEndSec - End of previous word (for adaptive snapping)
     * @param nextWordStartSec - Start of next word (for adaptive snapping)
     */
    public async analyzeCut(
        cutStartCompSec: number,
        cutEndCompSec: number,
        sourceIndex: number,
        prevWordEndSec: number | null = null,
        nextWordStartSec: number | null = null
    ): Promise<CutAnalysis | null> {
        // Gap to adjacent words (for adaptive snap limit)
        const gapBefore = prevWordEndSec !== null ? (cutStartCompSec - prevWordEndSec) : Infinity;
        const gapAfter = nextWordStartSec !== null ? (nextWordStartSec - cutEndCompSec) : Infinity;
        
        // Analyze both cut points
        const [startAnalysis, endAnalysis] = await Promise.all([
            this.analyzeCutPoint(cutStartCompSec, sourceIndex, gapBefore),
            this.analyzeCutPoint(cutEndCompSec, sourceIndex, gapAfter),
        ]);
        
        if (!startAnalysis || !endAnalysis) {
            return null;
        }
        
        // Check if audio analysis was successful (rms = -1 means we couldn't read audio)
        const startHasAudio = startAnalysis.rms >= 0;
        const endHasAudio = endAnalysis.rms >= 0;
        const hasAudioData = startHasAudio && endHasAudio;
        
        // Compute RMS ratio (amplitude mismatch between before and after)
        // Only compute if we have valid audio data from both points
        const rmsRatio = (hasAudioData && startAnalysis.rms > 0.0001)
            ? endAnalysis.rms / startAnalysis.rms 
            : 1.0; // Fallback when no audio data
        
        // ============ SCORING SYSTEM ============
        // After the filler is removed, the TOTAL gap between prev word and next word
        // is the sum of both margins (not the min!)
        // gapBefore = space between prev word end and cut start
        // gapAfter = space between cut end and next word start
        // Total remaining gap = gapBefore + gapAfter
        const totalGapAfterCut = (gapBefore === Infinity || gapAfter === Infinity)
            ? Infinity
            : gapBefore + gapAfter;
        const totalGapMs = totalGapAfterCut * 1000;
        
        // 1. Gap Score (0-30 points) - based on TOTAL remaining gap after filler removal
        let gapScore = 0;
        if (totalGapMs < 20) gapScore = 30;           // Extremely tight (<20ms total gap)
        else if (totalGapMs < 40) gapScore = 25;      // Very tight
        else if (totalGapMs < 60) gapScore = 15;      // Tight
        else if (totalGapMs < 80) gapScore = 8;       // Marginal
        else if (totalGapMs < 100) gapScore = 3;      // Slightly risky
        // else: 0 (safe, >=100ms total gap)
        
        // 2. Zero-Crossing Score (0-25 points)
        // Only meaningful if we have audio data to analyze
        let zeroCrossingScore = 0;
        if (!hasAudioData) {
            // No audio data - can't determine zero crossings, assume moderate risk
            zeroCrossingScore = 15;
        } else if (!startAnalysis.hasZeroCrossing && !endAnalysis.hasZeroCrossing) {
            zeroCrossingScore = 25;  // Neither found
        } else if (!startAnalysis.hasZeroCrossing || !endAnalysis.hasZeroCrossing) {
            zeroCrossingScore = 12;  // Only one found
        }
        // else: 0 (both found)
        
        // 3. RMS Ratio Score (0-25 points)
        let rmsScore = 0;
        if (!hasAudioData) {
            // No audio data - can't determine RMS ratio, assume moderate risk
            rmsScore = 15;
        } else if (rmsRatio < 0.5 || rmsRatio > 2.0) {
            rmsScore = 25;           // Severe mismatch
        } else if (rmsRatio < 0.67 || rmsRatio > 1.5) {
            rmsScore = 15;           // Noticeable mismatch
        } else if (rmsRatio < 0.85 || rmsRatio > 1.15) {
            rmsScore = 8;            // Slight mismatch
        }
        // else: 0 (well matched)
        
        // 4. Amplitude at Cut Point Score (0-20 points)
        // Use 0 if no audio data (assume quiet = good for cutting)
        const maxAmplitude = hasAudioData 
            ? Math.max(startAnalysis.amplitudeAtCut, endAnalysis.amplitudeAtCut)
            : 0;
        let amplitudeScore = 0;
        if (maxAmplitude > 0.3) amplitudeScore = 20;        // Cutting at peak
        else if (maxAmplitude > 0.1) amplitudeScore = 15;   // Mid energy
        else if (maxAmplitude > 0.02) amplitudeScore = 8;   // Low energy
        // else: 0 (near silence or no data)
        
        // Total score
        const totalScore = gapScore + zeroCrossingScore + rmsScore + amplitudeScore;
        
        const score: HarshnessScore = {
            gapScore,
            zeroCrossingScore,
            rmsScore,
            amplitudeScore,
            total: totalScore,
        };
        
        // Harsh if score exceeds threshold
        const isHarsh = totalScore >= HARSH_SCORE_THRESHOLD;
        
        // ============ FADE DURATION ============
        // Adaptive fade based on score
        let fadeMs = MICROFADE_DURATION_MS; // Base: 15ms
        if (totalScore >= 60) fadeMs = 30;
        else if (totalScore >= 40) fadeMs = 25;
        else if (totalScore >= 20) fadeMs = 20;
        // else: 15ms base
        
	        if (DEBUG_SCRIPT_CLIP_LOGS) {
	            console.log("[ScriptClip] analyzeCut score:", {
	                totalGapMs: totalGapMs === Infinity ? "Infinity" : totalGapMs.toFixed(1),
	                gapScore,
	                zeroCrossingScore: hasAudioData ? zeroCrossingScore : `${zeroCrossingScore} (no audio)`,
	                rmsScore: hasAudioData ? `${rmsScore} (ratio: ${rmsRatio.toFixed(2)})` : `${rmsScore} (no audio)`,
	                amplitudeScore: hasAudioData ? `${amplitudeScore} (max: ${maxAmplitude.toFixed(3)})` : `${amplitudeScore} (no audio)`,
	                total: totalScore,
	                isHarsh,
	                fadeMs,
	            });
	        }
        
        return {
            startAnalysis,
            endAnalysis,
            fadeOutMs: fadeMs,
            fadeInMs: fadeMs,
            isHarsh,
            rmsRatio,
            score,
        };
    }

    /**
     * Prime audio for all audio sources.
     * Each source has its own mixer track (like having multiple independent AudioClips).
     * The mixer handles playback rate via the schedule, so we just need to feed audio data.
     */
    public async primeAudio(compTimeSec: number, mixer: AudioMixer, prebufferSec = 0.2) {
        if (!this.hasAudioStream || this._audioMixerTracks.size === 0) return;

        this._audioPrimeSeq += 1;
        const primeSeq = this._audioPrimeSeq;

	        if (DEBUG_SCRIPT_CLIP_LOGS) {
	            console.log("[ScriptLayerClip] primeAudio: numSources =", this._audioMixerTracks.size);
	        }

        // Map absolute composition time -> script-clip local time (accounts for cuts).
        // Empty scenes live in clip-local time, so this stays valid for insertEmptyScene().
        const absCompTimeSec = Number(compTimeSec ?? 0);
        let clipLocalTime = Number.isFinite(absCompTimeSec) ? this.mapCompTimeToClipLocal(absCompTimeSec) : null;
        if (clipLocalTime == null) {
            // Conservative fallback: prime at the start of the next segment (or 0).
            const segs = this.scriptSegments ?? [];
            const next = segs.find((s) => {
                const compStart = Number(s?.compStartSec ?? NaN);
                if (!Number.isFinite(compStart)) return false;
                return compStart >= absCompTimeSec - 1e-3;
            });
            clipLocalTime = Number(next?.srcStartSec ?? 0) || 0;
        }

        // Dispose previous iterators
        for (const prevIter of this._audioIters.values()) {
            await prevIter.return?.();
        }
        this._audioIters.clear();
        if (this._audioPrimeSeq !== primeSeq) return;

        // Crossfade overlays are generated incrementally by the feeders.
        // Flush overlay tracks on each prime/seek so we don't mix stale overlays.
        if (this._crossfadeDescriptors.length > 0 && this._crossfadeOverlayTracks.size > 0) {
            for (const overlayTrack of this._crossfadeOverlayTracks.values()) {
                try {
                    // Overlay track timestamps live in composition time.
                    mixer.flushTrack(overlayTrack, absCompTimeSec);
                } catch {}
            }
        }

        // Prime each audio source to its own mixer track (like AudioClip.primeAudio)
        for (let sourceIndex = 0; sourceIndex < this.audioEntries.length; sourceIndex++) {
            if (this._audioPrimeSeq !== primeSeq) break;
            const entry = this.audioEntries[sourceIndex];
            const input = entry.input;
            const sink = this._audioSinks.get(input);
            const mixerTrack = this._audioMixerTracks.get(input);
            if (!sink || !mixerTrack) continue;

            const sampleRate = this._audioSampleRates.get(input) || 48000;

            // Determine the best source-time to prime from.
            // Use segment-aware mapping so empty scenes / sequential segments don't break priming.
            let seekTime = this.mapCompTimeToSourceTime(clipLocalTime, sourceIndex);

            // If we're currently in an empty region for this source, prime at the next active segment start.
            if (seekTime == null) {
                const segs: ScriptLayerSourceSegment[] =
                    Array.isArray((entry as any)?.segments) && (entry as any).segments.length > 0
                        ? ((entry as any).segments as ScriptLayerSourceSegment[])
                        : [];
                const EPS = 1e-3;

                // Find the next segment whose placement starts at/after the current clip-local time.
                let nextSeg: ScriptLayerSourceSegment | null = null;
                for (const seg of segs) {
                    const off = Number(seg?.offsetSec ?? NaN);
                    if (!Number.isFinite(off)) continue;
                    if (off + EPS < clipLocalTime) continue;
                    if (!nextSeg || off < Number(nextSeg.offsetSec ?? 0)) nextSeg = seg;
                }

                if (nextSeg) {
                    const inSec = Number(nextSeg?.range?.inSec ?? entry.range?.inSec ?? 0);
                    seekTime = Number.isFinite(inSec) ? inSec : null;
                } else {
                    // No future content; prime from the beginning (best-effort).
                    const fallback = Number(entry.range?.inSec ?? 0);
                    seekTime = Number.isFinite(fallback) ? fallback : null;
                }
            }

            if (seekTime == null || !Number.isFinite(seekTime)) continue;

            // Flush this source's track from the seek position
            mixer.flushTrack(mixerTrack, seekTime);

	            // Start feeder for this source, passing the start position.
	            // Await prebuffer so playback starts with audio ready (like AudioClip.primeAudio()).
	            await this.startAudioFeeder(
	                sourceIndex,
	                entry,
	                sink,
	                mixerTrack,
	                mixer,
	                sampleRate,
	                prebufferSec,
	                clipLocalTime,
	                seekTime,
	                absCompTimeSec,
	                primeSeq,
	            );
	        }
	    }

    /**
     * Start a continuous audio feeder for a single source.
     * This is similar to how AudioClip.primeAudio works - just feeds data to the mixer.
     * The mixer handles timing, playback rate, and mixing with other sources.
     * 
     * Now also applies microfades at segment boundaries (cuts).
     */
	    private async startAudioFeeder(
	        sourceIndex: number,
	        entry: AudioSourceEntry,
	        sink: AudioBufferSink,
	        mixerTrack: MixerTrackHandle,
	        mixer: AudioMixer,
	        sampleRate: number,
	        prebufferSec: number,
	        startClipLocalTimeSec: number = 0, // Clip-local script timeline time (after cuts mapping)
	        startSourceTimeSec: number = 0,  // The source time we're starting from
	        startCompTimeSec: number = 0,    // The composition time we're priming at
	        primeSeq?: number
	    ) {
	        const MAX_BUFFER_AHEAD_SEC = 20;
	        const BACKPRESSURE_SLEEP_MS = 25;
	        const EPS = 1e-3;
	        const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

	        const segments = this.scriptSegments;
	        if (!segments || segments.length === 0) return;

	        // IMPORTANT: scriptSegments are in *clip-local* time, but mixer schedule + sink.buffers
	        // operate in *source* time. When sources have offset segments (e.g. insertEmptyScene()),
	        // clip-local and source time diverge. We must map clip-local -> source-time for decode
	        // and for mixer timestamps, while still applying microfades/scene gains in clip-local time.
	        type FeedSegment = {
	            scriptSegIndex: number;
	            clipLocalStartSec: number;
	            clipLocalEndSec: number;
	            compStartSec: number;
	            sourceStartSec: number;
	            sourceEndSec: number;
	            rate: number;
	        };

	        const entrySegmentsRaw: any[] =
	            Array.isArray((entry as any)?.segments) && (entry as any).segments.length > 0
	                ? ((entry as any).segments as any[])
	                : [
	                      {
	                          offsetSec: (entry as any).offsetSec ?? 0,
	                          range: (entry as any).range ?? { inSec: 0, outSec: Infinity },
	                          playbackRate: (entry as any).playbackRate ?? 1.0,
	                      },
	                  ];

	        const feedSegments: FeedSegment[] = [];
	        for (let scriptSegIndex = 0; scriptSegIndex < segments.length; scriptSegIndex++) {
	            const seg = segments[scriptSegIndex];
	            const segStart = Number(seg?.srcStartSec ?? NaN);
	            const segEnd = Number(seg?.srcEndSec ?? NaN);
	            const segCompStart = Number(seg?.compStartSec ?? NaN);
	            if (!Number.isFinite(segStart) || !Number.isFinite(segEnd) || segEnd <= segStart) continue;
	            if (!Number.isFinite(segCompStart)) continue;

	            for (const sourceSeg of entrySegmentsRaw) {
	                // Use clip-level playbackRate (set by UI), fall back to segment/source-level
	                const playbackRate =
	                    (this as any).playbackRate ?? sourceSeg?.playbackRate ?? (entry as any).playbackRate ?? 1.0;
	                const rate = Number(playbackRate) || 1.0;
	                if (!Number.isFinite(rate) || rate <= 0) continue;

	                const entryOffset = Number(sourceSeg?.offsetSec ?? 0);
	                const entryRangeIn = Number(sourceSeg?.range?.inSec ?? 0);
	                const entryRangeOutRaw = sourceSeg?.range?.outSec ?? Infinity;
	                const entryRangeOut =
	                    entryRangeOutRaw === Infinity ? Infinity : Number(entryRangeOutRaw);
	                if (!Number.isFinite(entryOffset) || !Number.isFinite(entryRangeIn)) continue;
	                if (entryRangeOut !== Infinity && (!Number.isFinite(entryRangeOut) || entryRangeOut <= entryRangeIn))
	                    continue;

	                const entryEndOnClip =
	                    entryRangeOut === Infinity ? Infinity : entryOffset + (entryRangeOut - entryRangeIn) / rate;

	                // No overlap between this script segment and this source placement segment.
	                if (segEnd <= entryOffset || segStart >= entryEndOnClip) continue;

	                const clampedStart = Math.max(segStart, entryOffset);
	                const clampedEnd = Math.min(segEnd, entryEndOnClip);
	                const clampedDur = clampedEnd - clampedStart;
	                if (!Number.isFinite(clampedDur) || clampedDur <= 0) continue;

	                const sourceStartSec = entryRangeIn + (clampedStart - entryOffset) * rate;
	                const sourceEndSec = entryRangeIn + (clampedEnd - entryOffset) * rate;
	                if (!Number.isFinite(sourceStartSec) || !Number.isFinite(sourceEndSec) || sourceEndSec <= sourceStartSec)
	                    continue;

	                const compStartSec = segCompStart + (clampedStart - segStart);

	                feedSegments.push({
	                    scriptSegIndex,
	                    clipLocalStartSec: clampedStart,
	                    clipLocalEndSec: clampedEnd,
	                    compStartSec,
	                    sourceStartSec,
	                    sourceEndSec,
	                    rate,
	                });
	            }
	        }

	        if (feedSegments.length === 0) return;
	        feedSegments.sort((a, b) => a.clipLocalStartSec - b.clipLocalStartSec || a.sourceStartSec - b.sourceStartSec);

	        const startClipLocal = Number.isFinite(startClipLocalTimeSec) ? Number(startClipLocalTimeSec) : 0;

	        // Find initial feed segment index for the requested *clip-local* time.
	        let feedIdx =
	            feedSegments.findIndex(
	                (s) => startClipLocal >= s.clipLocalStartSec && startClipLocal < s.clipLocalEndSec
	            ) ?? -1;
	        if (feedIdx < 0) {
	            feedIdx = feedSegments.findIndex((s) => startClipLocal < s.clipLocalEndSec);
	        }
	        if (feedIdx < 0) return;

	        let feedSeg = feedSegments[feedIdx];
	        let segIdx = feedSeg.scriptSegIndex;
	        let seg = segments[segIdx];

	        // Initialize:
	        // - nextChunkClipLocalSec for processing (scene props/microfades are in clip-local time)
	        // - nextChunkSourceSec for decoding + mixer timestamps (worklet schedule uses source time)
	        let nextChunkClipLocalSec = Math.max(startClipLocal, feedSeg.clipLocalStartSec);
	        // Prefer the provided startSourceTimeSec when it falls inside this feed segment; otherwise derive.
	        let nextChunkSourceSec = Number(startSourceTimeSec);
	        if (!Number.isFinite(nextChunkSourceSec) || nextChunkSourceSec < feedSeg.sourceStartSec - EPS || nextChunkSourceSec > feedSeg.sourceEndSec + EPS) {
	            nextChunkSourceSec = feedSeg.sourceStartSec + (nextChunkClipLocalSec - feedSeg.clipLocalStartSec) * feedSeg.rate;
	        }
	        // Track how far we've buffered in *composition time* (cuts don't count as "ahead").
	        let nextChunkCompTimeSec = feedSeg.compStartSec + Math.max(0, nextChunkClipLocalSec - feedSeg.clipLocalStartSec);
	        let acc = 0;

	        const overlayTrack = this._crossfadeOverlayTracks.get(entry.input);

        // Index crossfade descriptors by boundary (segmentAfterIndex), so we can do O(1) lookup
        // per decoded chunk instead of scanning O(#cuts).
        const xfadeByAfterIndex = new Map<number, CrossfadeDescriptor>();
        for (const xfade of this._crossfadeDescriptors) {
            xfadeByAfterIndex.set(xfade.segmentAfterIndex, xfade);
        }

        const enableTrueCrossfades = Boolean(overlayTrack) && xfadeByAfterIndex.size > 0;

        type PendingCrossfade = {
            desc: CrossfadeDescriptor;
            fadeSamples: number;
            channels: number;
            tailPlanes: Float32Array[];
            headPlanes: Float32Array[];
            tailCollected: number;
            headCollected: number;
        };

        // Pending crossfades keyed by `segmentAfterIndex`.
        // At most ~2 will be active at once (current boundary + next boundary).
	        const pendingCrossfadesByAfterIndex = new Map<number, PendingCrossfade>();

        // Helper to process a buffer with microfades applied and gaps silenced.
        // NOTE: Each decoder iterator is bounded to the current segment, so we only need to
        // consider the current segment's boundaries (not all segments).
        const processBuffer = (buffer: AudioBuffer, chunkStartSec: number): Float32Array[] => {
            const channels = buffer.numberOfChannels;
            const frames = buffer.length;
            const chunkEndSec = chunkStartSec + frames / sampleRate;

            // Copy planes so we can modify them.
            const planes: Float32Array[] = [];
            for (let ch = 0; ch < channels; ch++) {
                planes.push(buffer.getChannelData(ch).slice());
            }

            const currentSeg = seg;
            const currentSegIndex = segIdx;

            // Gap segments output pure silence.
            if (currentSeg?.isGap) {
                for (const plane of planes) plane.fill(0);
                return planes;
            }

            const xfadeAtStart = enableTrueCrossfades ? xfadeByAfterIndex.get(currentSegIndex) : undefined;
            const xfadeAtEnd = enableTrueCrossfades ? xfadeByAfterIndex.get(currentSegIndex + 1) : undefined;

            // Capture the tail of segment A and zero it (overlay will supply crossfade audio).
            if (enableTrueCrossfades && overlayTrack && xfadeAtEnd) {
                const afterIndex = xfadeAtEnd.segmentAfterIndex;
                let pending = pendingCrossfadesByAfterIndex.get(afterIndex);
                if (!pending) {
                    const fadeSamples = Math.max(1, Math.ceil(xfadeAtEnd.fadeDurationSec * sampleRate));
                    pending = {
                        desc: xfadeAtEnd,
                        fadeSamples,
                        channels,
                        tailPlanes: Array.from({ length: channels }, () => new Float32Array(fadeSamples)),
                        headPlanes: Array.from({ length: channels }, () => new Float32Array(fadeSamples)),
                        tailCollected: 0,
                        headCollected: 0,
                    };
                    pendingCrossfadesByAfterIndex.set(afterIndex, pending);
                }

                const tailStartSec = xfadeAtEnd.srcBeforeStartSec;
                const tailEndSec = xfadeAtEnd.srcBeforeEndSec;
                if (chunkStartSec < tailEndSec && chunkEndSec > tailStartSec) {
                    const overlapStartSec = Math.max(chunkStartSec, tailStartSec);
                    const overlapEndSec = Math.min(chunkEndSec, tailEndSec);
                    const startSampleInChunk = Math.max(0, Math.floor((overlapStartSec - chunkStartSec) * sampleRate));
                    const endSampleInChunk = Math.min(frames, Math.ceil((overlapEndSec - chunkStartSec) * sampleRate));
                    const startSampleInTail = Math.max(0, Math.floor((overlapStartSec - tailStartSec) * sampleRate));
                    const copyLen = Math.max(0, Math.min(endSampleInChunk - startSampleInChunk, pending.fadeSamples - startSampleInTail));

                    if (copyLen > 0) {
                        for (let ch = 0; ch < channels; ch++) {
                            pending.tailPlanes[ch].set(
                                planes[ch].subarray(startSampleInChunk, startSampleInChunk + copyLen),
                                startSampleInTail
                            );
                        }
                        pending.tailCollected = Math.max(pending.tailCollected, startSampleInTail + copyLen);

                        // Zero the same region in the main track.
                        for (const plane of planes) {
                            for (let i = startSampleInChunk; i < startSampleInChunk + copyLen && i < plane.length; i++) {
                                plane[i] = 0;
                            }
                        }
                    }
                }
            }

            // Capture the head of segment B and write the crossfade overlay once ready.
            const pendingAtStart = enableTrueCrossfades && overlayTrack ? pendingCrossfadesByAfterIndex.get(currentSegIndex) : undefined;
            if (enableTrueCrossfades && overlayTrack && pendingAtStart) {
                const desc = pendingAtStart.desc;
                const fadeSamples = pendingAtStart.fadeSamples;

                const headStartSec = desc.srcAfterStartSec;
                const headEndSec = desc.srcAfterEndSec;
                if (chunkStartSec < headEndSec && chunkEndSec > headStartSec) {
                    const overlapStartSec = Math.max(chunkStartSec, headStartSec);
                    const overlapEndSec = Math.min(chunkEndSec, headEndSec);
                    const startSampleInChunk = Math.max(0, Math.floor((overlapStartSec - chunkStartSec) * sampleRate));
                    const endSampleInChunk = Math.min(frames, Math.ceil((overlapEndSec - chunkStartSec) * sampleRate));
                    const startSampleInHead = Math.max(0, Math.floor((overlapStartSec - headStartSec) * sampleRate));
                    const copyLen = Math.max(0, Math.min(endSampleInChunk - startSampleInChunk, fadeSamples - startSampleInHead));

                    if (copyLen > 0) {
                        for (let ch = 0; ch < channels; ch++) {
                            pendingAtStart.headPlanes[ch].set(
                                planes[ch].subarray(startSampleInChunk, startSampleInChunk + copyLen),
                                startSampleInHead
                            );
                        }
                        pendingAtStart.headCollected = Math.max(pendingAtStart.headCollected, startSampleInHead + copyLen);
                    }
                }

                // Once we've collected the head window (best-effort), emit the overlay.
                // - Preferred: full window (fadeSamples)
                // - Fallback: we've decoded past the requested headEndSec (short segments)
                const headWindowDone =
                    pendingAtStart.headCollected >= fadeSamples || (pendingAtStart.headCollected > 0 && chunkEndSec >= headEndSec - EPS);

                if (headWindowDone) {
                    const mixedPlanes: Float32Array[] = [];
                    for (let ch = 0; ch < channels; ch++) {
                        const mixed = new Float32Array(fadeSamples);
                        const tail = pendingAtStart.tailPlanes[ch];
                        const head = pendingAtStart.headPlanes[ch];
                        for (let i = 0; i < fadeSamples; i++) {
                            const t = i / fadeSamples;
                            const gainOut = Math.cos(t * Math.PI * 0.5);
                            const gainIn = Math.sin(t * Math.PI * 0.5);
                            mixed[i] = (tail[i] ?? 0) * gainOut + (head[i] ?? 0) * gainIn;
                        }
                        mixedPlanes.push(mixed);
                    }

                    mixer.writePcm(overlayTrack, {
                        timestamp: desc.compCrossfadeStartSec,
                        frames: fadeSamples,
                        channels,
                        planes: mixedPlanes,
                    });

                    if (DEBUG_SCRIPT_CLIP_LOGS) {
                        console.log(
                            `[ScriptClip] ✅ Crossfade overlay written (src${sourceIndex}) at ${desc.compCrossfadeStartSec.toFixed(3)}s (${desc.fadeDurationSec.toFixed(3)}s)`
                        );
                    }

                    pendingCrossfadesByAfterIndex.delete(currentSegIndex);
                }
            }

            // Apply microfades at the current segment boundaries.
            // If true crossfades are enabled at a boundary, the overlay handles smoothing there.
            if (!enableTrueCrossfades || !xfadeAtStart) {
                if (this.needsMicrofadeAtStart(currentSegIndex)) {
                    const fadeDurationMs = this.getFadeDurationMsAtStart(currentSegIndex);
                    const fadeDurationSec = fadeDurationMs / 1000;
                    const fadeStartSec = currentSeg.srcStartSec;
                    const fadeEndSec = fadeStartSec + fadeDurationSec;
                    if (chunkStartSec < fadeEndSec && chunkEndSec > fadeStartSec) {
                        if (!this._loggedFades) this._loggedFades = new Set();
                        const fadeKey = `in_${currentSegIndex}_${fadeStartSec.toFixed(3)}`;
                        if (!this._loggedFades.has(fadeKey)) {
                            this._loggedFades.add(fadeKey);
                            if (DEBUG_SCRIPT_CLIP_LOGS) {
                                console.log(
                                    `[ScriptClip] 🔊 FADE-IN APPLIED: segment ${currentSegIndex}, ${fadeStartSec.toFixed(3)}s - ${fadeEndSec.toFixed(3)}s (${fadeDurationMs}ms)`
                                );
                            }
                        }
                        this.applyFadeToPlanes(planes, sampleRate, chunkStartSec, fadeStartSec, fadeEndSec, "in");
                    }
                }
            }

            if (!enableTrueCrossfades || !xfadeAtEnd) {
                if (this.needsMicrofadeAtEnd(currentSegIndex)) {
                    const fadeDurationMs = this.getFadeDurationMsAtEnd(currentSegIndex);
                    const fadeDurationSec = fadeDurationMs / 1000;
                    const fadeEndSec = currentSeg.srcEndSec;
                    const fadeStartSec = fadeEndSec - fadeDurationSec;
                    if (chunkStartSec < fadeEndSec && chunkEndSec > fadeStartSec) {
                        if (!this._loggedFades) this._loggedFades = new Set();
                        const fadeKey = `out_${currentSegIndex}_${fadeStartSec.toFixed(3)}`;
                        if (!this._loggedFades.has(fadeKey)) {
                            this._loggedFades.add(fadeKey);
                            if (DEBUG_SCRIPT_CLIP_LOGS) {
                                console.log(
                                    `[ScriptClip] 🔊 FADE-OUT APPLIED: segment ${currentSegIndex}, ${fadeStartSec.toFixed(3)}s - ${fadeEndSec.toFixed(3)}s (${fadeDurationMs}ms)`
                                );
                            }
                        }
                        this.applyFadeToPlanes(planes, sampleRate, chunkStartSec, fadeStartSec, fadeEndSec, "out");
                    }
                }
            }

            // Apply scene-specific gain adjustments
            // If a scene has a custom gain, apply (sceneGain / clipGain) multiplier
            // This adjusts the output relative to the base mixer gain
            const scenes = this.getScenes();
            if (scenes.length > 0 && this.sceneProperties.size > 0) {
                const clipGain = this.gain || 1.0;

                for (let i = 0; i < frames; i++) {
                    const sampleTimeSec = chunkStartSec + (i / sampleRate);
                    const sceneIndex = this.findSceneIndexForTime(sampleTimeSec);

                    if (sceneIndex >= 0) {
                        const sceneProps = this.sceneProperties.get(sceneIndex);
                        if (sceneProps?.gain !== undefined && sceneProps.gain !== clipGain) {
                            // Apply gain adjustment: multiply by (sceneGain / clipGain)
                            // This converts from clip-level gain to scene-level gain
                            const gainAdjust = sceneProps.gain / clipGain;
                            for (const plane of planes) {
                                plane[i] *= gainAdjust;
                            }
                        }

                        // Per-source gain multiplier (mute/unmute per scene, or scene-specific mixing).
                        // Value is a multiplier relative to the clip's base per-source gain.
                        try {
                            const audioSourceGains = (sceneProps as any)?.audioSourceGains;
                            const multRaw =
                                audioSourceGains && typeof audioSourceGains.get === "function"
                                    ? audioSourceGains.get(sourceIndex)
                                    : undefined;
                            const mult = multRaw == null ? 1 : Number(multRaw);
                            if (Number.isFinite(mult) && mult !== 1) {
                                for (const plane of planes) {
                                    plane[i] *= mult;
                                }
                            }
                        } catch {}
                    }
                }
            }

            // Speaker-targeted mute ranges (audio-only) for this source.
            // Applied last so muted speech is always fully silent.
            try {
                const muteRanges = Array.isArray(this.speakerMuteRanges) ? this.speakerMuteRanges : [];
                if (muteRanges.length > 0) {
                    for (const r of muteRanges as any[]) {
                        const s = Number(r?.startSec ?? NaN);
                        const e = Number(r?.endSec ?? NaN);
                        if (!Number.isFinite(s) || !Number.isFinite(e) || e <= s) continue;

                        const src = Number(r?.sourceIndex);
                        const appliesToSource = !Number.isFinite(src) || src === sourceIndex;
                        if (!appliesToSource) continue;

                        const overlapStart = Math.max(chunkStartSec, s);
                        const overlapEnd = Math.min(chunkEndSec, e);
                        if (!(overlapEnd > overlapStart)) continue;

                        const startSample = Math.max(0, Math.floor((overlapStart - chunkStartSec) * sampleRate));
                        const endSample = Math.min(frames, Math.ceil((overlapEnd - chunkStartSec) * sampleRate));
                        if (endSample <= startSample) continue;

                        for (const plane of planes) {
                            for (let i = startSample; i < endSample; i++) {
                                plane[i] = 0;
                            }
                        }
                    }
                }
            } catch {}

            return planes;
        };

	        // Create a bounded iterator for the current segment so we can skip cut-out regions.
	        let iter: AsyncGenerator<{ buffer: AudioBuffer; timestamp: number }, void> = sink.buffers(
	            nextChunkSourceSec,
	            feedSeg.sourceEndSec
	        );
	        this._audioIters.set(entry.input, iter);

	        const advanceFeedSegment = async (): Promise<boolean> => {
	            if (primeSeq != null && this._audioPrimeSeq !== primeSeq) return false;
	            feedIdx += 1;
	            if (feedIdx >= feedSegments.length) return false;
	            feedSeg = feedSegments[feedIdx];
	            segIdx = feedSeg.scriptSegIndex;
	            seg = segments[segIdx];
	            nextChunkClipLocalSec = feedSeg.clipLocalStartSec;
	            nextChunkSourceSec = feedSeg.sourceStartSec;
	            nextChunkCompTimeSec = feedSeg.compStartSec;
	            try {
	                await (iter as any)?.return?.();
	            } catch {}
	            iter = sink.buffers(nextChunkSourceSec, feedSeg.sourceEndSec);
	            this._audioIters.set(entry.input, iter);
	            return true;
	        };

	        const readNextBuffer = async (): Promise<AudioBuffer | null> => {
	            while (true) {
	                if (primeSeq != null && this._audioPrimeSeq !== primeSeq) return null;
	                const n = await iter.next();
	                if (primeSeq != null && this._audioPrimeSeq !== primeSeq) return null;
	                if (!n || n.done || !n.value || !n.value.buffer) {
	                    const ok = await advanceFeedSegment();
	                    if (!ok) return null;
	                    continue;
	                }
	                return n.value.buffer;
	            }
	        };

	        const writeProcessedBuffer = (buffer: AudioBuffer): number => {
	            const timestamp = nextChunkSourceSec;
	            const segRemainingSec = Math.max(0, feedSeg.sourceEndSec - nextChunkSourceSec);
	            if (segRemainingSec <= EPS) return 0;

	            let frames = buffer.length;
	            let durationSec = frames / sampleRate;
	            if (durationSec > segRemainingSec + EPS) {
	                frames = Math.max(0, Math.floor(segRemainingSec * sampleRate));
	                durationSec = frames / sampleRate;
	                if (frames <= 0) return 0;
	            }

	            let planes = processBuffer(buffer, nextChunkClipLocalSec);
	            if (frames !== buffer.length) {
	                planes = planes.map((p) => p.slice(0, frames));
	            }

	            mixer.writePcm(mixerTrack, {
	                timestamp,
	                frames,
	                channels: buffer.numberOfChannels,
	                planes,
	            });

	            nextChunkSourceSec += durationSec;
	            nextChunkClipLocalSec += durationSec;
	            nextChunkCompTimeSec += durationSec;
	            return durationSec;
	        };

        // Prebuffer loop (awaited by primeAudio)
        try {
            while (acc < prebufferSec) {
                if (primeSeq != null && this._audioPrimeSeq !== primeSeq) return;
	                const buf = await readNextBuffer();
	                if (!buf) break;
	                const wroteSec = writeProcessedBuffer(buf);
	                acc += wroteSec;
	                if (feedSeg.sourceEndSec - nextChunkSourceSec <= EPS) {
	                    const ok = await advanceFeedSegment();
	                    if (!ok) break;
	                }
	            }
	        } catch (e) {
	            console.warn('[ScriptLayerClip] Audio prebuffer error for source', entry.name, e);
            return;
        }

        // Continuous feeder (async)
        (async () => {
            try {
                while (true) {
                    if (primeSeq != null && this._audioPrimeSeq !== primeSeq) return;

                    // Backpressure: don't decode far ahead of playhead (composition time).
                    const comp = this.track?.composition as any;
                    const playheadSec =
                        comp && typeof comp.getPlayheadTimeSec === "function"
                            ? Number(comp.getPlayheadTimeSec())
                            : Number(startCompTimeSec ?? 0);
                    const bufferAhead = nextChunkCompTimeSec - playheadSec;
                    if (Number.isFinite(bufferAhead) && bufferAhead > MAX_BUFFER_AHEAD_SEC) {
                        await sleep(BACKPRESSURE_SLEEP_MS);
                        continue;
                    }

	                    const buf = await readNextBuffer();
	                    if (!buf) return;
	                    writeProcessedBuffer(buf);

	                    if (feedSeg.sourceEndSec - nextChunkSourceSec <= EPS) {
	                        const ok = await advanceFeedSegment();
	                        if (!ok) return;
	                    }
	                }
	            } catch (e) {
	                console.warn('[ScriptLayerClip] Audio feeder error for source', entry.name, e);
            }
        })().catch(() => {});
    }

    /**
     * Apply a fade envelope to audio planes.
     * @param planes - Audio channel data (modified in place)
     * @param sampleRate - Sample rate in Hz
     * @param chunkStartSec - Start time of this chunk in source time
     * @param fadeStartSec - Start time of the fade region in source time
     * @param fadeEndSec - End time of the fade region in source time
     * @param direction - 'in' for fade-in (0→1), 'out' for fade-out (1→0)
     */
    private applyFadeToPlanes(
        planes: Float32Array[],
        sampleRate: number,
        chunkStartSec: number,
        fadeStartSec: number,
        fadeEndSec: number,
        direction: 'in' | 'out'
    ): void {
        const fadeDuration = fadeEndSec - fadeStartSec;
        if (fadeDuration <= 0) return;
        
        const frames = planes[0]?.length ?? 0;
        if (frames === 0) return;
        
        for (let i = 0; i < frames; i++) {
            const sampleTimeSec = chunkStartSec + (i / sampleRate);
            
            // Check if this sample is within the fade region
            if (sampleTimeSec >= fadeStartSec && sampleTimeSec < fadeEndSec) {
                // Calculate fade amount (0 to 1)
                const fadeProgress = (sampleTimeSec - fadeStartSec) / fadeDuration;
                
                // Apply linear fade
                // fade-in: 0→1, fade-out: 1→0
                const gain = direction === 'in' ? fadeProgress : (1 - fadeProgress);
                
                // Apply to all channels
                for (const plane of planes) {
                    plane[i] *= gain;
                }
            }
        }
    }

    public stopAudio() {
        this._audioPrimeSeq += 1;
        for (const iter of this._audioIters.values()) {
            void iter.return?.();
        }
        this._audioIters.clear();
    }

    private disposeVideoStateForInput(input: Input): void {
        const iter = this._videoIters.get(input);
        if (iter) {
            try { (iter as any)?.return?.(); } catch {}
        }
        this._videoIters.delete(input);

        const frame = this._nextFrames.get(input);
        if (frame) {
            try { (frame as any)?.close?.(); } catch {}
        }
        this._nextFrames.delete(input);

        this._pendingFrameRequests.delete(input);
    }

    private disposeAllVideoState(): void {
        for (const iter of this._videoIters.values()) {
            try { (iter as any)?.return?.(); } catch {}
        }
        this._videoIters.clear();

        for (const frame of this._nextFrames.values()) {
            if (!frame) continue;
            try { (frame as any)?.close?.(); } catch {}
        }
        this._nextFrames.clear();
        this._pendingFrameRequests.clear();
    }

    public onSeek(newCompTimeSec: number): void {
        // Reset video iterators on seek - they will be recreated at correct source time.
        // Close any queued frames to avoid VideoFrame GC warnings.
        this.disposeAllVideoState();
        
        // Reset segment tracking - seek is a discontinuity
        this._lastSegmentIndex = -1;
        
        // Reset container visibility - update() will set correct visibility
        for (const container of this._videoContainers.values()) {
            container.visible = true;
        }
    }

    /**
     * Get all audio source schedule info for the composition's mixer schedule.
     * Returns one entry per audio source with its mixer track, timing, and playback rate.
     */
    public getAudioScheduleInfo(): Array<{
        mixerTrack: MixerTrackHandle;
        entry: AudioSourceEntry;
        gain: number;
    }> {
        const result: Array<{
            mixerTrack: MixerTrackHandle;
            entry: AudioSourceEntry;
            gain: number;
        }> = [];

        for (const entry of this.audioEntries) {
            const mixerTrack = this._audioMixerTracks.get(entry.input);
            if (mixerTrack) {
                result.push({
                    mixerTrack,
                    entry,
                    gain: entry.gain * this.gain,
                });
            }
        }
        
        // Note: Crossfade overlay track is scheduled separately via getCrossfadeScheduleInfo()

        return result;
    }
    
    /**
     * Get schedule events for crossfade overlays.
     * Each crossfade needs its own schedule event to tell the mixer when to play it.
     */
    public getCrossfadeScheduleInfo(): Array<{
        mixerTrack: MixerTrackHandle;
        start: number;      // Composition time
        end: number;        // Composition time
        srcOffset: number;  // Source offset (0 for pre-mixed overlays)
        gain: number;
    }> | null {
        if (this._crossfadeDescriptors.length === 0 || this._crossfadeOverlayTracks.size === 0) {
            return null;
        }

        const out: Array<{
            mixerTrack: MixerTrackHandle;
            start: number;
            end: number;
            srcOffset: number;
            gain: number;
        }> = [];

        // One overlay track per audio source. Each track gets the same set of crossfade events.
        for (const entry of this.audioEntries) {
            const overlayTrack = this._crossfadeOverlayTracks.get(entry.input);
            if (!overlayTrack) continue;

            for (const xfade of this._crossfadeDescriptors) {
                out.push({
                    mixerTrack: overlayTrack,
                    start: xfade.compCrossfadeStartSec,
                    end: xfade.compCrossfadeEndSec,
                    // Overlay audio is written at composition-time timestamps.
                    srcOffset: xfade.compCrossfadeStartSec,
                    // Best-effort gain: the worklet prefers clipGain(handleId) when set.
                    gain: entry.gain * this.gain,
                });
            }
        }

        return out.length > 0 ? out : null;
    }

    /**
     * @deprecated Use getAudioScheduleInfo() for proper multi-track handling
     * Returns the first mixer track for backwards compatibility.
     */
    public mixerTrack(): MixerTrackHandle | undefined {
        // Return first track for backwards compatibility
        const firstInput = this.audioEntries[0]?.input;
        return firstInput ? this._audioMixerTracks.get(firstInput) : undefined;
    }

    /**
     * Find video entry for a given input.
     */
    private findVideoEntry(input: Input): VideoSourceEntry | undefined {
        return this.videoEntries.find(e => e.input === input);
    }

    /**
     * Update video frames for all video sources.
     * Each video source renders to its own canvas/sprite.
     * Handles range, offset, and playback rate for each source.
     */
    @visualize
    public async update(time: Timestamp): Promise<void> {
        // Composition does not await clip updates during realtime playback, so async updates can overlap.
        // Serialize to avoid races that can cause video sprites to flicker.
        if (this._updatePromise) {
            this._pendingUpdateTimeSec = time.seconds;
            // Important: when callers DO await update() (export, screenshot capture),
            // they need to wait for the in-flight update to drain the pending timestamp.
            // Returning the in-flight promise keeps realtime playback semantics (Composition doesn't await),
            // while allowing deterministic capture when awaited.
            return this._updatePromise;
        }

        this._pendingUpdateTimeSec = time.seconds;
        this._updatePromise = (async () => {
            while (this._pendingUpdateTimeSec != null) {
                const tSec = this._pendingUpdateTimeSec;
                this._pendingUpdateTimeSec = null;
                try {
                    await this._updateInternal(Timestamp.fromSeconds(tSec));
                } catch {
                    // Swallow to keep render loop alive.
                }
            }
        })();

        try {
            await this._updatePromise;
        } finally {
            this._updatePromise = null;
        }
    }

    private async _updateInternal(time: Timestamp): Promise<void> {
        if (!this.track?.composition) {
            return;
        }

        // Map composition time through segments to get effective clip-local time
        // This is equivalent to VideoClip's CompositionTimeToClipTime - segments handle jumps
        const effectiveClipLocal = this.mapCompTimeToClipLocal(time.seconds);
        
        // Fallback: if outside all segments (edge case), skip rendering
        if (effectiveClipLocal === null || effectiveClipLocal < 0 || effectiveClipLocal >= this.duration.seconds) {
            for (const container of this._videoContainers.values()) {
                container.visible = false;
            }
            return;
        }
        
        // Detect discontinuities that require seeking video iterators.
        // NOTE: Script segments are split both at cuts (discontinuity) and at splits/scene boundaries (continuous).
        // We only reset decode state when there is an actual jump in source time.
        const prevSegmentIndex = this._lastSegmentIndex;
        const currentSegmentIndex = this.findSegmentIndexForCompTime(time.seconds);
        let crossedDiscontinuity = false;
        if (prevSegmentIndex !== -1 && currentSegmentIndex !== -1 && currentSegmentIndex !== prevSegmentIndex) {
            const segs = this.scriptSegments;
            const prevSeg = segs[prevSegmentIndex];
            const currSeg = segs[currentSegmentIndex];
            const EPS = 1e-3;
            crossedDiscontinuity = !prevSeg || !currSeg || Math.abs(prevSeg.srcEndSec - currSeg.srcStartSec) > EPS;
        }
        this._lastSegmentIndex = currentSegmentIndex;
        
        // If we crossed a discontinuity, reset ALL video iterators - they need to seek to new positions.
        if (crossedDiscontinuity) {
            // Close any queued frames to avoid VideoFrame GC warnings.
            this.disposeAllVideoState();
        }

        // Update each video source independently - process in parallel
        const updatePromises: Promise<void>[] = [];

        const inputToSourceIndex = new Map<Input, number>();
        for (let i = 0; i < this.videoEntries.length; i++) {
            inputToSourceIndex.set(this.videoEntries[i].input, i);
        }

        const transitionInfo = this.getTransitionAtTime(effectiveClipLocal);
        const currentSceneIndex = !transitionInfo ? this.findSceneIndexForTime(effectiveClipLocal) : -1;
        const aspectPreset = this.getCanvasAspectPreset();

        const getSceneVisualOverride = (sceneProps: any, srcIndex: number) => {
            if (!sceneProps) return undefined;
            const byAspect = (sceneProps as any)?.visualOverridesByAspect;
            const map =
                byAspect && (byAspect as any)[aspectPreset] && typeof (byAspect as any)[aspectPreset].get === "function"
                    ? (byAspect as any)[aspectPreset]
                    : aspectPreset === "horizontal"
                      ? (sceneProps as any)?.visualOverrides
                      : undefined;
            return map?.get?.(srcIndex);
        };

	        const pickCrop = (
	            overrides: SceneVisualProperties | undefined,
	            fallback: { x: number; y: number; width: number; height: number } | undefined
	        ): { x: number; y: number; width: number; height: number } | undefined => {
            if (overrides && Object.prototype.hasOwnProperty.call(overrides as any, "crop")) {
                const c = (overrides as any).crop;
                if (c && typeof c === "object") return c as any;
                return undefined;
            }
	            return fallback;
	        };
	
	        const clamp01 = (n: number): number => Math.max(0, Math.min(1, n));
	        const FULL_CROP = { x: 0, y: 0, width: 1, height: 1 };
	        const normalizeCrop = (
	            value: { x: number; y: number; width: number; height: number } | undefined
	        ): { x: number; y: number; width: number; height: number } => {
	            if (!value) return FULL_CROP;
	            return {
	                x: clamp01(Number(value.x ?? 0)),
	                y: clamp01(Number(value.y ?? 0)),
	                width: clamp01(Number(value.width ?? 1)),
	                height: clamp01(Number(value.height ?? 1)),
	            };
	        };
	
		        const isFullCrop = (value: { x: number; y: number; width: number; height: number } | undefined): boolean => {
		            if (!value) return true;
		            const x = Number(value.x ?? 0);
		            const y = Number(value.y ?? 0);
		            const w = Number(value.width ?? 1);
		            const h = Number(value.height ?? 1);
		            return Math.abs(x) < 1e-6 && Math.abs(y) < 1e-6 && Math.abs(w - 1) < 1e-6 && Math.abs(h - 1) < 1e-6;
		        };
		
		        const applyCropToSprite = (
		            sprite: Sprite | undefined,
		            cropValue: { x: number; y: number; width: number; height: number } | undefined
		        ): void => {
		            if (!sprite) return;
		            const tex = (sprite as any)?.texture as Texture | undefined;
		            if (!tex) return;
		            const srcW = Number(tex.source?.width ?? 0);
		            const srcH = Number(tex.source?.height ?? 0);
		            if (!Number.isFinite(srcW) || !Number.isFinite(srcH) || srcW <= 0 || srcH <= 0) return;
		
		            const c = normalizeCrop(cropValue);
		            let x = clamp01(c.x);
		            let y = clamp01(c.y);
		            let w = clamp01(c.width);
		            let h = clamp01(c.height);
		            // Ensure we stay within bounds even if upstream crop math produced slight overflows.
		            if (x + w > 1) x = Math.max(0, 1 - w);
		            if (y + h > 1) y = Math.max(0, 1 - h);
		            const px = x * srcW;
		            const py = y * srcH;
		            const pw = Math.max(1, w * srcW);
		            const ph = Math.max(1, h * srcH);
		
		            const frame = tex.frame;
		            const orig = tex.orig;
		            const EPS = 1e-3;
		            const changed =
		                Math.abs(frame.x - px) > EPS ||
		                Math.abs(frame.y - py) > EPS ||
		                Math.abs(frame.width - pw) > EPS ||
		                Math.abs(frame.height - ph) > EPS ||
		                Math.abs(orig.width - pw) > EPS ||
		                Math.abs(orig.height - ph) > EPS;
		            if (!changed) return;
		
		            frame.x = px;
		            frame.y = py;
		            frame.width = pw;
		            frame.height = ph;
		
		            // orig defines sprite local bounds; keep it anchored at (0,0) with the cropped size.
		            orig.x = 0;
		            orig.y = 0;
		            orig.width = pw;
		            orig.height = ph;
		
		            tex.updateUvs();
		            // Ensure vertices/bounds recompute for width/height scaling (dynamic textures require this).
		            try { (sprite as any)?.onViewUpdate?.(); } catch {}
		        };

		        for (const [input, sink] of this._videoSinks.entries()) {
	            const canvas = this._videoCanvases.get(input);
	            const context = this._videoContexts.get(input);
	            const texture = this._videoTextures.get(input);
	            const sprite = this._videoSprites.get(input);
	            const visual = this._videoVisuals.get(input);
	            const container = this._videoContainers.get(input);
	            const entry = this.findVideoEntry(input);
	            const sourceIndex = inputToSourceIndex.get(input) ?? 0;
            
            if (!canvas || !context || !texture || !visual || !entry) continue;

		            // Resolve the effective crop for the current scene/transition.
		            const baseCrop = (visual as any)?.crop as any;
		            let crop = baseCrop as any;
		            let fromCropForAddFades: { x: number; y: number; width: number; height: number } | undefined = undefined;
		            if (transitionInfo) {
		                const fromSceneProps = this.sceneProperties.get(transitionInfo.fromSceneIndex);
		                const toSceneProps = this.sceneProperties.get(transitionInfo.toSceneIndex);
		                const fromVisualOverrides = getSceneVisualOverride(fromSceneProps, sourceIndex);
		                const toVisualOverrides = getSceneVisualOverride(toSceneProps, sourceIndex);
		                const fromCropRaw = pickCrop(fromVisualOverrides, baseCrop);
		                const toCropRaw = pickCrop(toVisualOverrides, baseCrop);
		                const fromCrop = normalizeCrop(fromCropRaw);
		                const toCrop = normalizeCrop(toCropRaw);
		                const eased = this.applyEasing(transitionInfo.progress, transitionInfo.easing);
		
		                // When smart+addFades, we need the outgoing scene's crop simultaneously (even if full).
		                if (transitionInfo.transitionType === "smart" && transitionInfo.addFades) {
		                    fromCropForAddFades = fromCrop;
		                }

		                switch (transitionInfo.transitionType) {
		                    case "fade": {
		                        const useTo = transitionInfo.progress >= 0.5;
		                        crop = useTo ? toCrop : fromCrop;
	                        break;
	                    }
	                    case "smart":
	                    case "dissolve":
	                    case "wipe": {
		                        crop = {
		                            x: lerp(fromCrop.x, toCrop.x, eased),
		                            y: lerp(fromCrop.y, toCrop.y, eased),
		                            width: lerp(fromCrop.width, toCrop.width, eased),
		                            height: lerp(fromCrop.height, toCrop.height, eased),
		                        };
		
		                        // Treat a "full crop" as no crop for downstream rendering (avoids unnecessary churn).
		                        if (isFullCrop(crop)) crop = undefined;
		                        break;
		                    }
		                    case "cut":
		                    default: {
		                        crop = isFullCrop(fromCrop) ? undefined : fromCrop;
		                        break;
		                    }
		                }
		            } else if (currentSceneIndex >= 0) {
		                const sceneProps = this.sceneProperties.get(currentSceneIndex);
		                const visualOverrides = getSceneVisualOverride(sceneProps, sourceIndex);
	                if (visualOverrides && Object.prototype.hasOwnProperty.call(visualOverrides as any, "crop")) {
	                    const c = (visualOverrides as any).crop;
	                    crop = c && typeof c === "object" ? c : undefined;
	                }
	            }

            // Calculate source time for this video using the effective (segment-mapped) clip-local time
            const sourceTime = this.calculateSourceTime(effectiveClipLocal, entry);
            
            // Hide container if source is not active
            if (sourceTime === null) {
                if (container) container.visible = false;
                // Release any queued decode state while inactive.
                this.disposeVideoStateForInput(input);
                continue;
            }
	            
	            // Show container
	            if (container) container.visible = true;
	
	            // Apply crop by updating sprite texture frames (does not require a new decoded frame).
	            // This keeps Smart transitions smooth even when video frame cadence is lower.
	            applyCropToSprite(sprite, crop);
	            if (fromCropForAddFades) {
	                // Ensure the outgoing transition clone can have its own crop simultaneously.
	                this.getOrCreateTransitionFromContainer(input);
	                const fromSprite = this._transitionFromSprites.get(input);
	                applyCropToSprite(fromSprite, fromCropForAddFades);
	            }

	            // Process each video source
	            const processSource = async () => {
	                try {
	                    let iter = this._videoIters.get(input);
                    
                    // Note: Cut boundary detection happens above (crossedCutBoundary check)
                    // which resets all iterators. Here we just check if iterator exists.
                    
                    if (!iter) {
                        // Seek to the correct source time
                        iter = sink.canvases(sourceTime);
                        this._videoIters.set(input, iter);
                        // Prime by reading and drawing the first frame immediately.
                        // Queueing the next frame happens asynchronously to avoid blocking
                        // first paint on an extra decode.
                        const first = (await iter.next()).value ?? null;
                        if (this._videoIters.get(input) !== iter) {
                            try { (first as any)?.close?.(); } catch {}
                            return;
                        }

                        const prev = this._nextFrames.get(input);
                        if (prev) {
                            try { (prev as any)?.close?.(); } catch {}
                        }
                        this._nextFrames.set(input, null);

	                        if (first) {
	                            const srcCanvas = first.canvas instanceof HTMLCanvasElement
	                                ? first.canvas
	                                : this.toHTMLCanvas(first.canvas);

	                            const sourceW = srcCanvas.width;
	                            const sourceH = srcCanvas.height;
	
	                            if (canvas.width !== sourceW || canvas.height !== sourceH) {
	                                canvas.width = sourceW;
	                                canvas.height = sourceH;
	                                // Pixi v8 caches bounds; ensure width/height scaling uses fresh bounds after canvas resizes.
	                                try { (sprite as any)?.onViewUpdate?.(); } catch {}
	                                applyCropToSprite(sprite, crop);
	                                if (fromCropForAddFades) {
	                                    const fromSprite = this._transitionFromSprites.get(input);
	                                    applyCropToSprite(fromSprite, fromCropForAddFades);
	                                }
	                            }
	
	                            // Always draw the full source frame; crop is applied via sprite texture frames.
	                            context.drawImage(srcCanvas, 0, 0);

                            texture.source.update();
                            try { (first as any)?.close?.(); } catch {}
                        }

                        // Kick off async catch-up to queue a future frame without blocking
                        // first paint on another decode.
                        if (!this._pendingFrameRequests.has(input)) {
                            this._pendingFrameRequests.add(input);
                            void this.updateNextVideoFrame(input, iter, sourceTime);
                        }
                    }

                    const currentFrame = this._nextFrames.get(input);
                    // Compare frame timestamp to source time (not clip local time)
	                    if (currentFrame && currentFrame.timestamp <= sourceTime) {
		                        const srcCanvas = currentFrame.canvas instanceof HTMLCanvasElement
		                            ? currentFrame.canvas
		                            : this.toHTMLCanvas(currentFrame.canvas);
	
	                        // Canvas stays at full SOURCE dimensions; crop is applied via sprite texture frames.
	                        const sourceW = srcCanvas.width;
	                        const sourceH = srcCanvas.height;
	                        
	                        // Only resize if source dimensions changed (first frame or source resolution change)
	                        if (canvas.width !== sourceW || canvas.height !== sourceH) {
	                            canvas.width = sourceW;
	                            canvas.height = sourceH;
	                            // Pixi v8 caches bounds; ensure width/height scaling uses fresh bounds after canvas resizes.
	                            try { (sprite as any)?.onViewUpdate?.(); } catch {}
	                            applyCropToSprite(sprite, crop);
	                            if (fromCropForAddFades) {
	                                const fromSprite = this._transitionFromSprites.get(input);
	                                applyCropToSprite(fromSprite, fromCropForAddFades);
	                            }
	                        }
	                        
	                        // Draw source frame at 1:1 (no scaling in drawImage)
	                        context.drawImage(srcCanvas, 0, 0);
	                        
	                        // Only update texture AFTER drawing to avoid flashing blank frames
	                        texture.source.update();

                        // Close the consumed frame
                        try { (currentFrame as any)?.close?.(); } catch {}

                        // Queue next frame with catch-up loop (like VideoClip._updateNextFrame)
                        this._nextFrames.set(input, null);
                        if (!this._pendingFrameRequests.has(input)) {
                            this._pendingFrameRequests.add(input);
                            // Kick off catch-up in background - loops until frame is ahead of target
                            void this.updateNextVideoFrame(input, iter, sourceTime);
                        }
                    } else if (!currentFrame && !this._pendingFrameRequests.has(input)) {
                        // If no frame queued and no request pending, fetch with catch-up
                        this._pendingFrameRequests.add(input);
                        void this.updateNextVideoFrame(input, iter, sourceTime);
                    }
                } catch (e) {
                    console.warn('[ScriptLayerClip] Failed to render video source', e);
                }
            };

            updatePromises.push(processSource());
        }

        // Wait for all video sources to update (they run in parallel)
        await Promise.all(updatePromises);

        // Apply per-scene visual overrides/transitions AFTER texture updates so container sizing stays stable.
        this.updateSpritePositions(effectiveClipLocal);
    }

    /**
     * Catch-up loop for video frames - reads through frames until we find one
     * that's ahead of the target time. This handles jump cuts by quickly
     * skipping intermediate frames without displaying them.
     * Mirrors VideoClip._updateNextFrame behavior.
     */
    private async updateNextVideoFrame(
        input: Input,
        iter: AsyncGenerator<{ canvas: HTMLCanvasElement | OffscreenCanvas; timestamp: number }, void>,
        targetSourceTime: number
    ): Promise<void> {
        try {
            while (true) {
                // Abort if the iterator is stale or the request was cancelled (e.g. seek/cut).
                if (this._videoIters.get(input) !== iter || !this._pendingFrameRequests.has(input)) {
                    try { await (iter as any)?.return?.(); } catch {}
                    this._pendingFrameRequests.delete(input);
                    return;
                }

                const r = await iter.next();
                const candidate = (r.value ?? null) as WrappedCanvas | null;

                // If we were invalidated while awaiting, close the candidate and bail.
                if (this._videoIters.get(input) !== iter || !this._pendingFrameRequests.has(input)) {
                    if (candidate) {
                        try { (candidate as any)?.close?.(); } catch {}
                    }
                    try { await (iter as any)?.return?.(); } catch {}
                    this._pendingFrameRequests.delete(input);
                    return;
                }
                
                if (!candidate) {
                    // No more frames
                    const prev = this._nextFrames.get(input);
                    if (prev) {
                        try { (prev as any)?.close?.(); } catch {}
                    }
                    this._nextFrames.set(input, null);
                    this._pendingFrameRequests.delete(input);
                    return;
                }
                
                // If frame is in the future relative to target, we're caught up
                if (candidate.timestamp > targetSourceTime) {
                    const prev = this._nextFrames.get(input);
                    if (prev && prev !== candidate) {
                        try { (prev as any)?.close?.(); } catch {}
                    }
                    this._nextFrames.set(input, candidate);
                    this._pendingFrameRequests.delete(input);
                    return;
                }
                
                // Frame is in the past - close it and loop to catch up
                try { (candidate as any)?.close?.(); } catch {}
            }
        } catch (e) {
            this._pendingFrameRequests.delete(input);
            try { await (iter as any)?.return?.(); } catch {}
        }
    }

    private toHTMLCanvas(src: HTMLCanvasElement | OffscreenCanvas): HTMLCanvasElement {
        if (src instanceof HTMLCanvasElement) return src;
        const c = document.createElement('canvas');
        c.width = src.width;
        c.height = src.height;
        const ctx = c.getContext('2d')!;
        ctx.drawImage(src as any, 0, 0);
        return c;
    }

    public async connect(track: Track<ScriptLayerClip>): Promise<void> {
        super.connect(track);
        
        // Move video containers from this.view to composition stage
        // This makes each video source act like an independent clip
        const stage = track.composition?.stage;
        if (stage) {
            for (const container of this._videoContainers.values()) {
                // Remove from this.view if present
                if (container.parent === this.view) {
                    this.view.removeChild(container);
                }
                // Add to composition stage
                stage.addChild(container);
            }
            for (const container of this._transitionFromContainers.values()) {
                if (container.parent === this.view) {
                    this.view.removeChild(container);
                }
                stage.addChild(container);
            }
	            if (DEBUG_SCRIPT_CLIP_LOGS) {
	                console.log("[ScriptLayerClip.connect] Moved", this._videoContainers.size, "containers to stage");
	            }
        }
    }
    
    public disconnect(): void {
        // Stop any queued decode state (iterators/frames).
        this.disposeAllVideoState();

        // Remove video containers from stage
        for (const container of this._videoContainers.values()) {
            if (container.parent) {
                container.parent.removeChild(container);
            }
        }
	        for (const container of this._transitionFromContainers.values()) {
	            if (container.parent) {
	                container.parent.removeChild(container);
	            }
	        }
	        this._transitionFromContainers.clear();
	        this._transitionFromSprites.clear();
		        if (DEBUG_SCRIPT_CLIP_LOGS) {
		            console.log("[ScriptLayerClip.disconnect] Removed containers from stage");
		        }
	    }

    /**
     * Get input configs (for external access)
     */
    public getInputConfigs(): ScriptLayerInputConfig[] {
        return [...this.inputConfigs];
    }

    /**
     * Get audio entries (for waveform generation, etc.)
     */
    public getAudioEntries(): AudioSourceEntry[] {
        return [...this.audioEntries];
    }

    /**
     * Get audio sources for waveform lookup (backwards compatible API).
     * Returns objects with `id` property for fetching waveform data.
     */
    public getAudioSources(): Array<{ id: string | undefined }> {
        return this.audioEntries.map(entry => ({ id: entry.assetId }));
    }

    // ============ Studio Sound ============

    /**
     * Get Studio Sound enabled state.
     */
    public get studioSoundEnabled(): boolean {
        return this._studioSoundEnabled;
    }

    /**
     * Set Studio Sound state and swap audio sources accordingly.
     * For ScriptLayerClip, this applies to ALL audio sources.
     * @param enabled - Whether to use denoised audio
     * @param denoisedUrls - Map of assetId to denoised audio URL
     * @param originalUrls - Map of assetId to original audio URL
     */
    public async setStudioSound(
        enabled: boolean,
        denoisedUrls?: Map<string, string>,
        originalUrls?: Map<string, string>
    ): Promise<void> {
        if (enabled === this._studioSoundEnabled) {
            return;
        }

        // Store URLs for future swapping
        if (originalUrls) {
            for (const [assetId, url] of originalUrls) {
                this._originalSourceUrls.set(assetId, url);
            }
        }
        if (denoisedUrls) {
            for (const [assetId, url] of denoisedUrls) {
                this._denoisedSourceUrls.set(assetId, url);
            }
        }

        // Replace each audio source
        const replacePromises: Promise<void>[] = [];
        for (let i = 0; i < this.audioEntries.length; i++) {
            const entry = this.audioEntries[i];
            const assetId = entry.assetId;
            if (!assetId) {
                console.warn(`[ScriptLayerClip.setStudioSound] Audio entry ${i} has no assetId, skipping`);
                continue;
            }

            const targetUrl = enabled
                ? this._denoisedSourceUrls.get(assetId)
                : this._originalSourceUrls.get(assetId);

            if (!targetUrl) {
                console.warn(`[ScriptLayerClip.setStudioSound] No ${enabled ? 'denoised' : 'original'} URL for assetId ${assetId}`);
                continue;
            }

            replacePromises.push(this.replaceAudioSource(i, targetUrl));
        }

        await Promise.all(replacePromises);
        this._studioSoundEnabled = enabled;

        // Reset limiter states when toggling
        this._limiterStates.clear();

        // Emit event for UI to update waveforms
        this.trigger('studioSoundChanged', { enabled, clipId: this.id });
    }

    /**
     * Replace a specific audio source with a new URL.
     * Used for Studio Sound (denoised audio) switching.
     * @param sourceIndex - Index into audioEntries
     * @param newUrl - The new audio URL to load
     */
    public async replaceAudioSource(sourceIndex: number, newUrl: string): Promise<void> {
        if (sourceIndex < 0 || sourceIndex >= this.audioEntries.length) {
            console.error(`[ScriptLayerClip.replaceAudioSource] Invalid sourceIndex ${sourceIndex}`);
            return;
        }

        const entry = this.audioEntries[sourceIndex];
        const oldInput = entry.input;

        // 1. Create new Input from URL
        const newInput = new Input({
            formats: ALL_FORMATS,
            source: new UrlSource(newUrl),
        });

        // 2. Save old sample rate/channels before cleanup (for comparison)
        const oldSampleRate = this._audioSampleRates.get(oldInput);
        const oldChannels = this._audioChannelCounts.get(oldInput);

        // 3. Clean up old audio resources for this source
        const oldSink = this._audioSinks.get(oldInput);
        if (oldSink) {
            this._audioSinks.delete(oldInput);
        }
        this._audioIters.delete(oldInput);
        this._audioGains.delete(oldInput);
        this._audioSampleRates.delete(oldInput);
        this._audioChannelCounts.delete(oldInput);

        // Transfer limiter state to new input
        const oldLimiterState = this._limiterStates.get(oldInput);
        if (oldLimiterState) {
            this._limiterStates.delete(oldInput);
            this._limiterStates.set(newInput, oldLimiterState);
        }

        // 4. Get mixer track reference (we may need to re-register it later)
        const mixerTrack = this._audioMixerTracks.get(oldInput);
        if (mixerTrack) {
            this._audioMixerTracks.delete(oldInput);
            // Don't set it on newInput yet - we need to check if sample rate changed
        }

        // 5. Update the entry's input reference
        entry.input = newInput;

        // 6. Reinitialize audio sink for this source
        try {
            const audioTrack = await newInput.getPrimaryAudioTrack();
            if (audioTrack) {
                const newSink = new AudioBufferSink(audioTrack);
                this._audioSinks.set(newInput, newSink);
                this._audioGains.set(newInput, entry.gain);

                // Get sample rate and channels from the new audio track
                const newSampleRate = audioTrack.sampleRate;
                const newChannels = audioTrack.numberOfChannels;
                this._audioSampleRates.set(newInput, newSampleRate);
                this._audioChannelCounts.set(newInput, newChannels);

                // Check if sample rate or channels changed - if so, register a new mixer track
                if (mixerTrack && this._mixer) {
                    const sampleRateChanged = oldSampleRate !== newSampleRate;
                    const channelsChanged = oldChannels !== newChannels;
                    
                    if (sampleRateChanged || channelsChanged) {
                        if (DEBUG_SCRIPT_CLIP_LOGS) {
                            console.log(
                                `[ScriptLayerClip] Sample rate/channels changed for source ${sourceIndex}: ${oldSampleRate}Hz/${oldChannels}ch -> ${newSampleRate}Hz/${newChannels}ch, registering new mixer track`
                            );
                        }
                        
                        // Register a new track with the correct sample rate
                        // (old track will be orphaned but that's fine - the worklet will stop using it)
                        const newMixerTrack = this._mixer.registerTrack({
                            clipId: `${this.id}_src${sourceIndex}`,
                            trackId: this.track?.id ?? '',
                            channels: newChannels,
                            sampleRate: newSampleRate,
                        });
                        
                        this._audioMixerTracks.set(newInput, newMixerTrack);
                        this._mixer.setClipGain(newMixerTrack, entry.gain * this.gain);
                    } else {
                        // Sample rate unchanged - just transfer the track
                        this._audioMixerTracks.set(newInput, mixerTrack);
                    }
                } else if (mixerTrack) {
                    // No mixer available yet, just transfer the track reference
                    this._audioMixerTracks.set(newInput, mixerTrack);
                }

                if (DEBUG_SCRIPT_CLIP_LOGS) {
                    console.log(`[ScriptLayerClip.replaceAudioSource] Source ${sourceIndex} replaced successfully`);
                }
            }
        } catch (err) {
            console.error(`[ScriptLayerClip.replaceAudioSource] Failed to init audio for source ${sourceIndex}:`, err);
        }
    }

    /**
     * Dynamically append a new input config (source) to an existing ScriptLayerClip.
     *
     * This is required for "insert into script" operations where we splice new media/sequence
     * content into the script spine without rebuilding/replacing the ScriptLayerClip instance.
     *
     * Notes:
     * - This method is incremental (does NOT call init()) so it preserves existing Pixi containers,
     *   mixer tracks, and scene overrides.
     * - The caller should provide `segments` for placement on the script spine; otherwise the
     *   clip falls back to range/offsetSec/playbackRate semantics (legacy).
     */
    public async appendInputConfig(config: ScriptLayerInputConfig): Promise<void> {
        if (!config?.input) return;

        // Track original length so we can keep names/colors indices aligned with config order.
        this.inputConfigs.push(config);
        try {
            const nextName = typeof config.name === "string" ? config.name : "";
            const nextColor = typeof config.color === "string" ? config.color : "";
            this.names = Array.isArray(this.names) ? [...this.names, nextName] : [nextName];
            this.colors = Array.isArray(this.colors) ? [...this.colors, nextColor] : [nextColor];
        } catch {}

        const { input, assetId, name, color, gain = 1.0, visual, offsetSec, playbackRate } = config;

        // Some callsites omit per-source `range` (meaning "full source").
        // Resolve it here when possible so later operations (insertEmptyScene, etc.) can rely on it.
        let range = config.range;
        const segments: ScriptLayerSourceSegment[] | undefined =
            Array.isArray((config as any).segments) && (config as any).segments.length > 0
                ? ((config as any).segments as ScriptLayerSourceSegment[])
                : undefined;

        let maxDuration = Number(this.duration?.seconds ?? 0) || 0;

        // === Audio ===
        try {
            const audioTrack = await input.getPrimaryAudioTrack();
            const canDecode = audioTrack ? await audioTrack.canDecode() : false;
            if (audioTrack && canDecode) {
                if (!range && (!segments || segments.length === 0)) {
                    try {
                        const dur = await audioTrack.computeDuration();
                        if (Number.isFinite(dur) && dur > 0) {
                            range = { inSec: 0, outSec: dur };
                            config.range = range;
                        }
                    } catch {}
                }

                const sink = new AudioBufferSink(audioTrack);
                this._audioSinks.set(input, sink);
                this._audioGains.set(input, Number(gain) || 1.0);
                this.hasAudioStream = true;

                this._audioSampleRates.set(input, audioTrack.sampleRate);
                this._audioChannelCounts.set(input, audioTrack.numberOfChannels);

                this.audioEntries.push({ input, assetId, gain, name, color, range, offsetSec, playbackRate, segments });

                if (!this._sample_rate) {
                    this._sample_rate = audioTrack.sampleRate;
                    this._num_channels = audioTrack.numberOfChannels;
                }

                // Update duration - prefer explicit segments, fall back to single-range config.
                try {
                    if (segments && segments.length > 0) {
                        for (const seg of segments) {
                            const segRate = Number(seg.playbackRate ?? playbackRate ?? 1.0) || 1.0;
                            const segIn = Number(seg.range?.inSec ?? range?.inSec ?? 0);
                            const segOut = Number(seg.range?.outSec ?? range?.outSec ?? 0);
                            if (!Number.isFinite(segIn) || !Number.isFinite(segOut) || segOut <= segIn) continue;
                            const trimmedDur = (segOut - segIn) / segRate;
                            const segOffset = Number(seg.offsetSec ?? 0);
                            if (!Number.isFinite(segOffset)) continue;
                            maxDuration = Math.max(maxDuration, segOffset + trimmedDur);
                        }
                    } else if (range) {
                        const trimmedDur = (range.outSec - range.inSec) / (playbackRate || 1.0);
                        maxDuration = Math.max(maxDuration, (offsetSec || 0) + trimmedDur);
                    } else {
                        const dur = await audioTrack.computeDuration();
                        if (Number.isFinite(dur) && dur > 0) {
                            const effectiveDur = dur / (playbackRate || 1.0);
                            maxDuration = Math.max(maxDuration, (offsetSec || 0) + effectiveDur);
                        }
                    }
                } catch {}

                // If the mixer is already prepared (composition played/seeked), register a new track now.
                try {
                    if (this._mixer && !this._audioMixerTracks.has(input)) {
                        const sampleRate = this._audioSampleRates.get(input) || 48000;
                        const channels = this._audioChannelCounts.get(input) || 2;
                        const mixerTrack = this._mixer.registerTrack({
                            clipId: `${this.id}_src${this.audioEntries.length - 1}`,
                            trackId: this.track?.id ?? "",
                            channels,
                            sampleRate,
                        });
                        this._audioMixerTracks.set(input, mixerTrack);
                        this._mixer.setClipGain(mixerTrack, (Number(gain) || 1.0) * this.gain);
                    }
                } catch {}
            }
        } catch {}

        // === Video ===
        try {
            const videoTrack = await input.getPrimaryVideoTrack();
            const canDecode = videoTrack ? await videoTrack.canDecode() : false;
            if (videoTrack && canDecode) {
                if (!range && (!segments || segments.length === 0)) {
                    try {
                        const dur = await videoTrack.computeDuration();
                        if (Number.isFinite(dur) && dur > 0) {
                            range = { inSec: 0, outSec: dur };
                            config.range = range;
                        }
                    } catch {}
                }

                const canBeTransparent = await videoTrack.canBeTransparent();
                const sink = new CanvasSink(videoTrack, {
                    poolSize: 2,
                    fit: "contain",
                    alpha: canBeTransparent,
                });
                this._videoSinks.set(input, sink);

                const defaultVisual = { x: 960, y: 540, width: 1920, height: 1080, alpha: 1.0, rotation: 0 } as any;
                const visualConfig = visual || defaultVisual;
                this._videoVisuals.set(input, visualConfig);

                const entryLayerIndex = config.layerIndex ?? this.videoEntries.length;
                this.videoEntries.push({ input, visual: visualConfig, range, offsetSec, playbackRate, segments, layerIndex: entryLayerIndex });

                // Create dedicated container, canvas, texture and sprite for this video source.
                const container = new Container();
                const canvas = document.createElement("canvas");
                canvas.width = visualConfig.width;
                canvas.height = visualConfig.height;
                const context = canvas.getContext("2d")!;
                const baseTexture = Texture.from(canvas);
                const spriteTexture = new Texture({
                    source: baseTexture as any,
                    frame: new Rectangle(0, 0, canvas.width, canvas.height),
                    orig: new Rectangle(0, 0, canvas.width, canvas.height),
                    dynamic: false,
                });
                const sprite = new Sprite(spriteTexture);
                sprite.anchor.set(0.5, 0.5);
                sprite.position.set(0, 0);
                container.addChild(sprite);

                container.position.set(visualConfig.x, visualConfig.y);
                container.alpha = visualConfig.alpha;
                container.angle = Number(visualConfig.rotation ?? 0);

                this._videoContainers.set(input, container);
                this._videoCanvases.set(input, canvas);
                this._videoContexts.set(input, context);
                this._videoTextures.set(input, baseTexture);
                this._videoSprites.set(input, sprite);

                // If we are already connected, add directly to the composition stage; otherwise fall back to this.view.
                const stage = this.track?.composition?.stage;
                if (stage) {
                    stage.addChild(container);
                } else {
                    this.view.addChild(container);
                }

                // Update duration - prefer explicit segments, fall back to single-range config.
                try {
                    if (segments && segments.length > 0) {
                        for (const seg of segments) {
                            const segRate = Number(seg.playbackRate ?? playbackRate ?? 1.0) || 1.0;
                            const segIn = Number(seg.range?.inSec ?? range?.inSec ?? 0);
                            const segOut = Number(seg.range?.outSec ?? range?.outSec ?? 0);
                            if (!Number.isFinite(segIn) || !Number.isFinite(segOut) || segOut <= segIn) continue;
                            const trimmedDur = (segOut - segIn) / segRate;
                            const segOffset = Number(seg.offsetSec ?? 0);
                            if (!Number.isFinite(segOffset)) continue;
                            maxDuration = Math.max(maxDuration, segOffset + trimmedDur);
                        }
                    } else if (range) {
                        const trimmedDur = (range.outSec - range.inSec) / (playbackRate || 1.0);
                        maxDuration = Math.max(maxDuration, (offsetSec || 0) + trimmedDur);
                    } else {
                        const dur = await videoTrack.computeDuration();
                        if (Number.isFinite(dur) && dur > 0) {
                            const effectiveDur = dur / (playbackRate || 1.0);
                            maxDuration = Math.max(maxDuration, (offsetSec || 0) + effectiveDur);
                        }
                    }
                } catch {}
            }
        } catch {}

        // Extend duration (never shrink).
        if (Number.isFinite(maxDuration) && maxDuration > (Number(this.duration?.seconds ?? 0) || 0)) {
            this.duration.seconds = maxDuration;
            try {
                const inSec = Number(this.range?.[0]?.seconds ?? 0);
                if (this.range && this.range.length === 2) {
                    this.range = [Timestamp.fromSeconds(inSec), Timestamp.fromSeconds(maxDuration)];
                }
            } catch {}
        }

        // Keep state READY if we have any sinks (existing + new).
        if (this._audioSinks.size > 0 || this._videoSinks.size > 0) {
            this.state = "READY";
        }

        this.trigger("frame", undefined);
    }

    /**
     * Append new placement segments for an existing source (assetId).
     * This mutates the stored segment array in-place when possible so audio/video entries stay in sync.
     */
    public appendSourceSegments(assetId: string, segmentsToAdd: ScriptLayerSourceSegment[]): boolean {
        const id = String(assetId ?? "").trim();
        if (!id) return false;
        if (!Array.isArray(segmentsToAdd) || segmentsToAdd.length === 0) return false;

        const cfg = this.inputConfigs.find((c) => String(c?.assetId ?? "") === id);
        if (!cfg) return false;

        const nextSegs: ScriptLayerSourceSegment[] = Array.isArray(cfg.segments) ? cfg.segments : [];
        if (!Array.isArray(cfg.segments)) cfg.segments = nextSegs;

        for (const seg of segmentsToAdd) {
            if (!seg) continue;
            const off = Number((seg as any).offsetSec ?? NaN);
            const rin = Number((seg as any).range?.inSec ?? NaN);
            const rout = Number((seg as any).range?.outSec ?? NaN);
            if (!Number.isFinite(off) || !Number.isFinite(rin) || !Number.isFinite(rout) || rout <= rin) continue;
            nextSegs.push({
                id: String((seg as any).id ?? crypto?.randomUUID?.() ?? `seg_${Date.now()}_${Math.random()}`),
                offsetSec: off,
                playbackRate: (seg as any).playbackRate,
                range: { inSec: rin, outSec: rout },
            });
        }

        nextSegs.sort((a, b) => Number(a.offsetSec ?? 0) - Number(b.offsetSec ?? 0));

        // Ensure runtime entries reference the same segment array.
        for (const e of this.audioEntries) {
            if (e?.input === cfg.input) (e as any).segments = nextSegs;
        }
        for (const e of this.videoEntries) {
            if (e?.input === cfg.input) (e as any).segments = nextSegs;
        }

        // Update duration if needed (segments are in clip-local time).
        try {
            let maxEnd = Number(this.duration?.seconds ?? 0) || 0;
            const cfgRate = Number(cfg.playbackRate ?? 1.0) || 1.0;
            for (const seg of nextSegs) {
                const segRate = Number(seg.playbackRate ?? cfgRate) || 1.0;
                const segIn = Number(seg.range?.inSec ?? 0);
                const segOut = Number(seg.range?.outSec ?? 0);
                const segOffset = Number(seg.offsetSec ?? 0);
                if (!Number.isFinite(segRate) || segRate <= 0) continue;
                if (!Number.isFinite(segOffset) || !Number.isFinite(segIn) || !Number.isFinite(segOut) || segOut <= segIn) continue;
                const segDur = (segOut - segIn) / segRate;
                maxEnd = Math.max(maxEnd, segOffset + segDur);
            }
            if (Number.isFinite(maxEnd) && maxEnd > (Number(this.duration?.seconds ?? 0) || 0)) {
                this.duration.seconds = maxEnd;
                const inSec = Number(this.range?.[0]?.seconds ?? 0);
                try {
                    if (this.range && this.range.length === 2) {
                        this.range = [Timestamp.fromSeconds(inSec), Timestamp.fromSeconds(maxEnd)];
                    }
                } catch {}
            }
        } catch {}

        this.trigger("frame", undefined);
        return true;
    }

    /**
     * Get video entries (for UI, etc.)
     */
    public getVideoEntries(): VideoSourceEntry[] {
        return [...this.videoEntries];
    }

    /**
     * Get video sources for UI (backwards compatible API).
     * Returns array with length equal to number of video sources.
     */
    public getVideoSources(): VideoSourceEntry[] {
        return [...this.videoEntries];
    }

    /**
     * Get the container for a specific video source by index.
     * Used for canvas selection and transform handles.
     * Returns the Container (which acts like an independent VideoClip view).
     */
    public getVideoSprite(index: number): Container | undefined {
        if (index < 0 || index >= this.videoEntries.length) return undefined;
        const entry = this.videoEntries[index];
        return this._videoContainers.get(entry.input);
    }

    /**
     * Get all video containers for hit-testing.
     * Returns array of { sprite (actually container), index } for each video source.
     */
    public getVideoSpritesWithIndex(): Array<{ sprite: Container; index: number }> {
        const result: Array<{ sprite: Container; index: number }> = [];
        for (let i = 0; i < this.videoEntries.length; i++) {
            const entry = this.videoEntries[i];
            const container = this._videoContainers.get(entry.input);
            if (container) {
                result.push({ sprite: container, index: i });
            }
        }
        return result;
    }
    
    /**
     * Get video containers sorted by layer index for hit-testing.
     * Returns sprites from highest layer (topmost/front) to lowest layer (back).
     * This is the correct order for hit-testing - we want to check topmost first.
     */
    public getVideoSpritesSortedByLayer(): Array<{ sprite: Container; index: number; layerIndex: number }> {
        const result: Array<{ sprite: Container; index: number; layerIndex: number }> = [];
        for (let i = 0; i < this.videoEntries.length; i++) {
            const entry = this.videoEntries[i];
            const container = this._videoContainers.get(entry.input);
            if (container) {
                result.push({ sprite: container, index: i, layerIndex: entry.layerIndex });
            }
        }
        // Sort by layerIndex descending (highest layer first = topmost for hit-testing)
        result.sort((a, b) => b.layerIndex - a.layerIndex);
        return result;
    }

    /**
     * Get the visual config for a specific video source.
     */
    public getVideoSourceVisual(index: number): VideoSourceEntry['visual'] | undefined {
        if (index < 0 || index >= this.videoEntries.length) return undefined;
        return this.videoEntries[index].visual;
    }

    /**
     * Get the clip's position (used for coordinate conversion)
     */
    private getClipPosition(): { x: number; y: number } {
        // Try to get from clip's position properties first (more reliable)
        let posX = 0;
        let posY = 0;
        
        if (typeof this._position.x === 'number') {
            posX = this._position.x;
        } else if (typeof this._position.x === 'string') {
            const screen = this.track?.composition;
            posX = Number.parseFloat(this._position.x) * (screen?.width ?? 1920) / 100;
        }
        
        if (typeof this._position.y === 'number') {
            posY = this._position.y;
        } else if (typeof this._position.y === 'string') {
            const screen = this.track?.composition;
            posY = Number.parseFloat(this._position.y) * (screen?.height ?? 1080) / 100;
        }
        
        return { x: posX, y: posY };
    }

    /**
     * Update video source transform by index.
     * Visual coordinates are in composition space (absolute).
     * Each container is positioned directly on the stage.
     */
    public updateVideoSourceTransform(index: number, transform: Partial<VideoSourceEntry['visual']>) {
        if (index >= 0 && index < this.videoEntries.length) {
            const entry = this.videoEntries[index];
            Object.assign(entry.visual, transform);
            this._videoVisuals.set(entry.input, entry.visual);
            
            // Update the container's position and canvas size
            const container = this._videoContainers.get(entry.input);
            const canvas = this._videoCanvases.get(entry.input);
            const sprite = this._videoSprites.get(entry.input);
            
            if (container) {
                if (transform.x !== undefined || transform.y !== undefined) {
                    container.position.set(entry.visual.x, entry.visual.y);
                }
                if (transform.width !== undefined || transform.height !== undefined) {
                    // Don't resize canvas - leave it at source dimensions
                    // Set container dimensions - PixiJS will scale automatically
                    container.width = entry.visual.width;
                    container.height = entry.visual.height;
                }
                if (transform.alpha !== undefined) {
                    container.alpha = entry.visual.alpha;
                }
                if (transform.rotation !== undefined) {
                    container.angle = Number(entry.visual.rotation ?? 0);
                }
            }
            
            this.trigger('frame', undefined);
        }
    }

    /**
     * Set crop for a video source by index.
     * Crop values are normalized (0-1) relative to source dimensions.
     * x=0, y=0, width=1, height=1 means no crop (full frame visible).
     * When crop is applied, the visual dimensions are updated proportionally.
     */
    public setVideoSourceCrop(index: number, crop: { x: number; y: number; width: number; height: number } | undefined) {
        if (index >= 0 && index < this.videoEntries.length) {
            const entry = this.videoEntries[index];
            
            // Store original dimensions if not already stored
            if (!entry.visual.originalWidth || !entry.visual.originalHeight) {
                entry.visual.originalWidth = entry.visual.width;
                entry.visual.originalHeight = entry.visual.height;
            }
            
	            entry.visual.crop = crop;
	            
	            // Invalidate video iterator for this entry to force fresh frame decode
	            // This ensures the new crop settings are applied immediately
	            this.disposeVideoStateForInput(entry.input);
            
            // Update visual dimensions based on crop
            if (crop && entry.visual.originalWidth && entry.visual.originalHeight) {
                // New dimensions = original * crop percentage
                entry.visual.width = Math.round(entry.visual.originalWidth * crop.width);
                entry.visual.height = Math.round(entry.visual.originalHeight * crop.height);
                
                // Update container to visual dimensions (canvas will be updated in render loop)
                const container = this._videoContainers.get(entry.input);
                if (container) {
                    container.width = entry.visual.width;
                    container.height = entry.visual.height;
                }
            } else if (!crop && entry.visual.originalWidth && entry.visual.originalHeight) {
                // Reset to original dimensions
                entry.visual.width = entry.visual.originalWidth;
                entry.visual.height = entry.visual.originalHeight;
                
                // Update container to visual dimensions (canvas will be updated in render loop)
                const container = this._videoContainers.get(entry.input);
                if (container) {
                    container.width = entry.visual.width;
                    container.height = entry.visual.height;
                }
            }
            
            this._videoVisuals.set(entry.input, entry.visual);
            
            // Trigger frame update to re-render with new crop
            this.trigger('frame', undefined);
        } else {
            console.warn('[ScriptLayerClip] setVideoSourceCrop: index out of range');
        }
    }

    /**
     * Get crop for a video source by index.
     * Returns the current crop, or the default full-video crop if none is set.
     */
    public getVideoSourceCrop(index: number): { x: number; y: number; width: number; height: number } {
        if (index >= 0 && index < this.videoEntries.length) {
            // Return stored crop or default to full video
            return this.videoEntries[index].visual.crop ?? { x: 0, y: 0, width: 1, height: 1 };
        }
        // Fallback: return full video crop
        return { x: 0, y: 0, width: 1, height: 1 };
    }

    /**
     * Get the original source dimensions for a video source by index.
     * This represents the full source dimensions (before any crop).
     * Used to allow expanding back to full size when re-cropping.
     */
    public getOriginalSourceDimensions(index?: number): { width: number; height: number } | undefined {
        const idx = index ?? 0;
        if (idx >= 0 && idx < this.videoEntries.length) {
            const visual = this.videoEntries[idx].visual;
            // Return stored original dimensions if available, otherwise current dimensions
            return { 
                width: visual.originalWidth ?? visual.width, 
                height: visual.originalHeight ?? visual.height 
            };
        }
        return undefined;
    }

    /**
     * Update all video container positions and dimensions.
     * Each container is positioned directly in composition coordinates (like VideoClip).
     * Canvas stays at source dimensions; container.width/height controls visual size.
     * @param currentTimeSec - Current composition time in seconds (for scene-specific overrides)
     */
    private updateSpritePositions(currentTimeSec?: number): void {
        // Check if we're in a visual transition zone
        const transitionInfo = currentTimeSec !== undefined
            ? this.getTransitionAtTime(currentTimeSec)
            : null;

        // Find current scene index for per-scene visual overrides (when not in transition)
        let currentSceneIndex = -1;
        if (currentTimeSec !== undefined && !transitionInfo) {
            currentSceneIndex = this.findSceneIndexForTime(currentTimeSec);
        }

        const preset = this.getCanvasAspectPreset();

        const getSceneVisualOverride = (sceneProps: any, srcIndex: number) => {
            if (!sceneProps) return undefined;
            const byAspect = (sceneProps as any)?.visualOverridesByAspect;
            const map =
                byAspect && (byAspect as any)[preset] && typeof (byAspect as any)[preset].get === "function"
                    ? (byAspect as any)[preset]
                    : preset === "horizontal"
                      ? (sceneProps as any)?.visualOverrides
                      : undefined;
            return map?.get?.(srcIndex);
        };

        let sourceIndex = 0;
        for (const [input, baseVisual] of this._videoVisuals.entries()) {
            const container = this._videoContainers.get(input);
            const fromContainer = this._transitionFromContainers.get(input);
            if (container) {
                // If the base container is hidden (inactive source), ensure transition clone is hidden too.
                if (!container.visible) {
                    if (fromContainer) fromContainer.visible = false;
                    sourceIndex++;
                    continue;
                }

                // Start with base visual properties
                let effectiveVisual = { ...baseVisual };
                let effectiveMaskShape: MaskShape | undefined = (baseVisual as any)?.maskShape;

                if (transitionInfo) {
                    // In transition zone - interpolate between scenes
                    const fromSceneProps = this.sceneProperties.get(transitionInfo.fromSceneIndex);
                    const toSceneProps = this.sceneProperties.get(transitionInfo.toSceneIndex);
                    const fromVisualOverrides = getSceneVisualOverride(fromSceneProps, sourceIndex);
                    const toVisualOverrides = getSceneVisualOverride(toSceneProps, sourceIndex);
                    const fromMask: MaskShape | undefined = (fromVisualOverrides as any)?.maskShape ?? effectiveMaskShape;
                    const toMask: MaskShape | undefined = (toVisualOverrides as any)?.maskShape ?? effectiveMaskShape;
                    const eased = this.applyEasing(transitionInfo.progress, transitionInfo.easing);

                    // Apply transition type-specific rendering
                    switch (transitionInfo.transitionType) {
                        case 'smart': {
                            const fromResolved = this.resolveSceneVisual(fromVisualOverrides, baseVisual);
                            const toResolved = this.resolveSceneVisual(toVisualOverrides, baseVisual);

                            // When Add fades is on, render both scenes at once:
                            // - base container represents the incoming scene (moves toward toResolved as it fades in)
                            // - transition clone represents outgoing scene (static fromResolved as it fades out)
                            if (transitionInfo.addFades) {
                                const tc = this.getOrCreateTransitionFromContainer(input);
                                if (tc) {
                                    tc.visible = true;
                                    tc.position.set(fromResolved.x, fromResolved.y);
                                    tc.width = fromResolved.width;
                                    tc.height = fromResolved.height;
                                    tc.angle = Number((baseVisual as any).rotation ?? 0);
                                    tc.alpha = fromResolved.alpha * (1 - eased);
                                    try { applyShapeMask(tc, fromMask ?? "none"); } catch {}
                                } else if (fromContainer) {
                                    fromContainer.visible = false;
                                }

                                const interpolated = this.interpolateVisualProperties(
                                    fromVisualOverrides,
                                    toVisualOverrides,
                                    eased,
                                    baseVisual
                                );
                                effectiveVisual = interpolated as any;
                                // Incoming scene fades in as it moves toward the target state.
                                effectiveVisual.alpha = toResolved.alpha * eased;
                                effectiveMaskShape = toMask;
                                break;
                            }

                            // No fades: morph between scene states (single instance).
                            const interpolated = this.interpolateVisualProperties(
                                fromVisualOverrides,
                                toVisualOverrides,
                                eased,
                                baseVisual
                            );
                            effectiveVisual = interpolated as any;
                            effectiveMaskShape = eased >= 0.5 ? toMask : fromMask;
                            // Ensure any previously-created transition clone is hidden.
                            if (fromContainer) fromContainer.visible = false;
                            break;
                        }
                        case 'dissolve': {
                            // Interpolate all visual properties smoothly
                            const interpolated = this.interpolateVisualProperties(
                                fromVisualOverrides,
                                toVisualOverrides,
                                eased,
                                baseVisual
                            );
                            effectiveVisual = interpolated;
                            effectiveMaskShape = transitionInfo.progress >= 0.5 ? toMask : fromMask;
                            if (fromContainer) fromContainer.visible = false;
                            break;
                        }
                        case 'fade': {
                            // Fade-to-black transitions are handled via a composition-level overlay.
                            // Here, we "cut at black": use outgoing scene state in first half,
                            // incoming scene state in second half.
                            const p = Math.max(0, Math.min(1, transitionInfo.progress));
                            const useTo = p >= 0.5;
                            const resolved = useTo
                                ? this.resolveSceneVisual(toVisualOverrides, baseVisual)
                                : this.resolveSceneVisual(fromVisualOverrides, baseVisual);
                            effectiveVisual = resolved as any;
                            effectiveMaskShape = useTo ? toMask : fromMask;
                            if (fromContainer) fromContainer.visible = false;
                            break;
                        }
                        case 'wipe': {
                            // For wipe, interpolate position/size but handle alpha separately
                            // (could add directional clipping in the future)
                            const interpolated = this.interpolateVisualProperties(
                                fromVisualOverrides,
                                toVisualOverrides,
                                eased,
                                baseVisual
                            );
                            effectiveVisual = interpolated;
                            effectiveMaskShape = transitionInfo.progress >= 0.5 ? toMask : fromMask;
                            if (fromContainer) fromContainer.visible = false;
                            break;
                        }
                        case 'cut':
                        default: {
                            // No transition - use "from" scene properties until boundary
                            const visualOverrides = fromVisualOverrides;
                            if (visualOverrides) {
                                if (visualOverrides.alpha !== undefined) effectiveVisual.alpha = visualOverrides.alpha;
                                if (visualOverrides.x !== undefined) effectiveVisual.x = visualOverrides.x;
                                if (visualOverrides.y !== undefined) effectiveVisual.y = visualOverrides.y;
                                if (visualOverrides.width !== undefined) effectiveVisual.width = visualOverrides.width;
                                if (visualOverrides.height !== undefined) effectiveVisual.height = visualOverrides.height;
                                if ((visualOverrides as any).maskShape !== undefined) effectiveMaskShape = (visualOverrides as any).maskShape;
                            }
                            if (fromContainer) fromContainer.visible = false;
                            break;
                        }
                    }
                } else if (currentSceneIndex >= 0) {
                    if (fromContainer) fromContainer.visible = false;
                    // Not in transition - apply scene-specific overrides directly
                    const sceneProps = this.sceneProperties.get(currentSceneIndex);
                    const visualOverrides = getSceneVisualOverride(sceneProps, sourceIndex);
                    if (visualOverrides) {
                        if (visualOverrides.alpha !== undefined) effectiveVisual.alpha = visualOverrides.alpha;
                        if (visualOverrides.x !== undefined) effectiveVisual.x = visualOverrides.x;
                        if (visualOverrides.y !== undefined) effectiveVisual.y = visualOverrides.y;
                        if (visualOverrides.width !== undefined) effectiveVisual.width = visualOverrides.width;
                        if (visualOverrides.height !== undefined) effectiveVisual.height = visualOverrides.height;
                        if ((visualOverrides as any).maskShape !== undefined) effectiveMaskShape = (visualOverrides as any).maskShape;
                    }
                }

                // Position container directly in composition coordinates
                container.position.set(effectiveVisual.x, effectiveVisual.y);
                container.alpha = effectiveVisual.alpha;

                // Set container to visual dimensions (PixiJS scales internally)
                container.width = effectiveVisual.width;
                container.height = effectiveVisual.height;
                const baseRotation = Number((baseVisual as any).rotation ?? 0);
                const nextRotation = Number((effectiveVisual as any).rotation ?? baseRotation);
                container.angle = Number.isFinite(nextRotation) ? nextRotation : baseRotation;

                // Apply optional shape mask on top of the script-layer visual.
                try {
                    applyShapeMask(container, effectiveMaskShape ?? "none");
                } catch {}
            }
            sourceIndex++;
        }
    }
    
    // ============ Source Layer Ordering ============
    
    /**
     * Get the layer index for a video source.
     * Higher values render on top of lower values.
     */
    public getSourceLayerIndex(sourceIndex: number): number {
        if (sourceIndex >= 0 && sourceIndex < this.videoEntries.length) {
            return this.videoEntries[sourceIndex].layerIndex;
        }
        return 0;
    }
    
    /**
     * Set the layer index for a video source.
     * Higher values render on top of lower values.
     */
    public setSourceLayerIndex(sourceIndex: number, layerIndex: number): void {
        if (sourceIndex >= 0 && sourceIndex < this.videoEntries.length) {
            this.videoEntries[sourceIndex].layerIndex = layerIndex;
            this.trigger('frame', undefined);
        }
    }
    
    /**
     * Get video entries sorted by layer index (for rendering order).
     * Lower layer index first, higher layer index last (renders on top).
     */
    public getVideoEntriesSortedByLayer(): Array<{ entry: VideoSourceEntry; index: number }> {
        return this.videoEntries
            .map((entry, index) => ({ entry, index }))
            .sort((a, b) => a.entry.layerIndex - b.entry.layerIndex);
    }
    
    /**
     * Bring a video source one layer forward (increase its layer index).
     * Returns true if the layer was changed.
     */
    public bringSourceForward(sourceIndex: number): boolean {
        if (sourceIndex < 0 || sourceIndex >= this.videoEntries.length) return false;
        
        const entry = this.videoEntries[sourceIndex];
        const currentLayer = entry.layerIndex;
        
        // Find the next higher layer index
        let nextHigherLayer = Infinity;
        for (const e of this.videoEntries) {
            if (e.layerIndex > currentLayer && e.layerIndex < nextHigherLayer) {
                nextHigherLayer = e.layerIndex;
            }
        }
        
        if (nextHigherLayer === Infinity) {
            // Already at front - no higher layer exists
            return false;
        }
        
        // Swap layer indices with the entry that has nextHigherLayer
        for (const e of this.videoEntries) {
            if (e.layerIndex === nextHigherLayer) {
                e.layerIndex = currentLayer;
                break;
            }
        }
        entry.layerIndex = nextHigherLayer;
        
        this.trigger('frame', undefined);
        return true;
    }
    
    /**
     * Send a video source one layer backward (decrease its layer index).
     * Returns true if the layer was changed.
     */
    public sendSourceBackward(sourceIndex: number): boolean {
        if (sourceIndex < 0 || sourceIndex >= this.videoEntries.length) return false;
        
        const entry = this.videoEntries[sourceIndex];
        const currentLayer = entry.layerIndex;
        
        // Find the next lower layer index
        let nextLowerLayer = -Infinity;
        for (const e of this.videoEntries) {
            if (e.layerIndex < currentLayer && e.layerIndex > nextLowerLayer) {
                nextLowerLayer = e.layerIndex;
            }
        }
        
        if (nextLowerLayer === -Infinity) {
            // Already at back - no lower layer exists
            return false;
        }
        
        // Swap layer indices with the entry that has nextLowerLayer
        for (const e of this.videoEntries) {
            if (e.layerIndex === nextLowerLayer) {
                e.layerIndex = currentLayer;
                break;
            }
        }
        entry.layerIndex = nextLowerLayer;
        
        this.trigger('frame', undefined);
        return true;
    }
    
    /**
     * Bring a video source to the front (highest layer index).
     */
    public bringSourceToFront(sourceIndex: number): void {
        if (sourceIndex < 0 || sourceIndex >= this.videoEntries.length) return;
        
        // Find the max layer index
        let maxLayer = 0;
        for (const e of this.videoEntries) {
            if (e.layerIndex > maxLayer) maxLayer = e.layerIndex;
        }
        
        // Set this source's layer to max + 1
        this.videoEntries[sourceIndex].layerIndex = maxLayer + 1;
        this.trigger('frame', undefined);
    }
    
    /**
     * Send a video source to the back (lowest layer index).
     */
    public sendSourceToBack(sourceIndex: number): void {
        if (sourceIndex < 0 || sourceIndex >= this.videoEntries.length) return;
        
        // Find the min layer index
        let minLayer = Infinity;
        for (const e of this.videoEntries) {
            if (e.layerIndex < minLayer) minLayer = e.layerIndex;
        }
        
        // Set this source's layer to min - 1
        this.videoEntries[sourceIndex].layerIndex = minLayer - 1;
        this.trigger('frame', undefined);
    }
    
    // ============ Video Source Removal ============
    
    /**
     * Remove a video source from the clip by index.
     * The audio from this source will continue to play (if it has audio).
     * Returns true if the source was removed successfully.
     */
    public removeVideoSource(sourceIndex: number): boolean {
        if (sourceIndex < 0 || sourceIndex >= this.videoEntries.length) {
            console.warn('[ScriptLayerClip] removeVideoSource: index out of range');
            return false;
        }
        
	        const entry = this.videoEntries[sourceIndex];
	        const input = entry.input;

	        // Stop any queued decode state for this input (close frames/iterators).
	        this.disposeVideoStateForInput(input);
 	        
	        // Get Pixi resources for this source
	        const container = this._videoContainers.get(input);
	        const sprite = this._videoSprites.get(input);
	        const texture = this._videoTextures.get(input);
	        const fromContainer = this._transitionFromContainers.get(input);
	        const fromSprite = this._transitionFromSprites.get(input);
	        
	        // Remove container from stage
	        if (container) {
	            try {
	                if (container.parent) {
	                    container.parent.removeChild(container);
	                }
	                container.destroy({ children: true });
	            } catch (e) {
	                console.warn('[ScriptLayerClip] Error destroying container:', e);
	            }
	        }
	
	        if (fromContainer) {
	            try {
	                if (fromContainer.parent) {
	                    fromContainer.parent.removeChild(fromContainer);
	                }
	                fromContainer.destroy({ children: true });
	            } catch (e) {
	                console.warn('[ScriptLayerClip] Error destroying transition container:', e);
	            }
	        }
	
	        // Clean up sprite textures (sprites do not destroy textures by default).
	        if (sprite) {
	            try { (sprite.texture as any)?.destroy?.(false); } catch {}
	        }
	        if (fromSprite) {
	            try { (fromSprite.texture as any)?.destroy?.(false); } catch {}
	        }
	        
	        // Clean up texture
	        if (texture) {
	            try {
	                texture.destroy(true);
            } catch (e) {
                console.warn('[ScriptLayerClip] Error destroying texture:', e);
            }
        }
        
        // Remove video sink reference (CanvasSink doesn't have stop, just remove reference)
        this._videoSinks.delete(input);
        
	        // Remove from all Maps
	        this._videoContainers.delete(input);
	        this._videoSprites.delete(input);
	        this._videoTextures.delete(input);
	        this._videoCanvases.delete(input);
	        this._videoContexts.delete(input);
	        this._videoSinks.delete(input);
	        this._videoVisuals.delete(input);
	        this._transitionFromContainers.delete(input);
	        this._transitionFromSprites.delete(input);
	        
	        // Remove from videoEntries array
	        this.videoEntries.splice(sourceIndex, 1);
        
        if (DEBUG_SCRIPT_CLIP_LOGS) {
            console.log(
                "[ScriptLayerClip] Removed video source at index",
                sourceIndex,
                "remaining sources:",
                this.videoEntries.length
            );
        }
        
        // Trigger a frame update
        this.trigger('frame', undefined);
        
        return true;
    }
    
    /**
     * Get the number of video sources.
     */
    public getVideoSourceCount(): number {
        return this.videoEntries.length;
    }
}
